import { WEBSITE_NAVIGATOR_SUGGESTIONS } from "./suggestions.generated.js";
import { createWebsiteNavigatorLatencyDebug } from "./latency-debug.js";
import { WEBSITE_NAVIGATOR_UI_COPY } from "./ui_copy.generated.js";
import { WEBSITE_NAVIGATOR_UI_PRESETS } from "./ui_presets.generated.js";
import { hasSiteConsent, onSiteConsentAccepted, requestSiteConsentAttention } from "../consentBanner.js";

const STORAGE_KEY = "website_navigator_session_token";
const HANDOFF_STORAGE_KEY = "website_navigator_service_page_handoff";
const HANDOFF_MAX_AGE_MS = 300000;
const INQUIRY_SUBMITTED_STORAGE_PREFIX = "website_navigator_inquiry_submitted";
const NAVIGATOR_ACTIVITY_STORAGE_PREFIX = "website_navigator_session_activity";
const NAVIGATOR_MESSAGE_STORAGE_PREFIX = "website_navigator_session_message";
const VOICE_SURFACE_NAVIGATOR = "navigator";
const VOICE_SURFACE_BOTTOM = "bottom";
const OVERLAY_DRAG_THRESHOLD = 6;
const TRANSCRIBE_PATH = "/api/transcribe";
const ACTION_PROMPT_KEYS = {
  book_consultation: "booking",
  start_website_check: "index",
  create_inquiry: "contact",
  prepare_inquiry: "contact",
  compare_services: "compare",
};
const NAVIGATOR_OPENING_MIN_MS = 420;
const MODULE_OPENING_MIN_MS = 320;
const PROMPTS = WEBSITE_NAVIGATOR_UI_COPY.prompts || {};
const PHONE_COUNTRIES = WEBSITE_NAVIGATOR_UI_PRESETS.phone_countries || [];
const DEFAULT_PHONE_COUNTRY = WEBSITE_NAVIGATOR_UI_PRESETS.default_phone_country || PHONE_COUNTRIES[0]?.code || "";
const BOOKING_TIME_FILTERS = WEBSITE_NAVIGATOR_UI_PRESETS.booking_time_filters || [];
const BOOKING_TIMEZONE_FALLBACKS = WEBSITE_NAVIGATOR_UI_PRESETS.booking_timezone_fallbacks || [];
const PRESET_NAVIGATOR_SHORTCUT_MODULES = Array.isArray(WEBSITE_NAVIGATOR_UI_PRESETS.navigator_shortcut_modules) ? WEBSITE_NAVIGATOR_UI_PRESETS.navigator_shortcut_modules : [];
const DISABLED_NAVIGATOR_SHORTCUT_MODULE_IDS = new Set(
  (Array.isArray(WEBSITE_NAVIGATOR_UI_PRESETS.navigator_disabled_shortcut_module_ids) ? WEBSITE_NAVIGATOR_UI_PRESETS.navigator_disabled_shortcut_module_ids : [])
    .map((item) => String(item || "").trim().replace(/-/g, "_").toLowerCase())
    .filter(Boolean)
);
const BOOKING_BEHAVIOR = "booking";
const BOOKING_TARGET_ID = "appointment";
const BOOKING_WORKFLOW_ID = "booking.initial_consultation";
const BOOKING_CALENDAR_COMPONENT_KEY = "booking_calendar";
const BOOKING_CONTACT_COMPONENT_KEY = "booking_contact_microform";
const BOOKING_SUMMARY_COMPONENT_KEY = "booking_summary";
const BOOKING_CALENDAR_COMPONENT_TYPE = "calendar";
const BOOKING_CONTACT_COMPONENT_TYPE = "contact_microform";
const BOOKING_SUMMARY_COMPONENT_TYPE = "booking_summary";
const BOOKING_SETUP_COMPONENT_KEYS = new Set([BOOKING_CALENDAR_COMPONENT_KEY, BOOKING_CONTACT_COMPONENT_KEY]);
const BOOKING_COMPONENT_KEYS = new Set([BOOKING_CALENDAR_COMPONENT_KEY, BOOKING_CONTACT_COMPONENT_KEY, BOOKING_SUMMARY_COMPONENT_KEY]);
const BOOKING_COMPONENT_TYPES = new Set([BOOKING_CALENDAR_COMPONENT_TYPE, BOOKING_SUMMARY_COMPONENT_TYPE]);
const WEBSITE_CHECK_SUMMARY_REFRESH = WEBSITE_NAVIGATOR_UI_PRESETS.website_check_summary_refresh || {};
function currentLanguage() {
  const bodyClasses = document.body?.classList;
  const rootClasses = document.documentElement.classList;
  const htmlLang = cleanText(document.documentElement.getAttribute("lang")).toLowerCase();
  if (bodyClasses?.contains("set-lang-en") || rootClasses.contains("lang-en")) return "en";
  if (bodyClasses?.contains("set-lang-de") || rootClasses.contains("lang-de")) return "de";
  const stored = safeStorageGet("language");
  if (stored === "en") return "en";
  if (stored === "de") return "de";
  if (htmlLang.startsWith("en")) return "en";
  return "en";
}

function safeStorageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}

function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
  }
}

function safeSessionRemove(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
  }
}

function textFor(value, lang = currentLanguage()) {
  if (value && typeof value === "object") {
    return String(value[lang] || value.en || value.de || "");
  }
  return String(value || "");
}

function listFor(value, lang = currentLanguage()) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const selected = value[lang] || value.en || value.de || [];
    return Array.isArray(selected) ? selected : [];
  }
  return [];
}

function navigatorText(groupKey, itemKey, fallback = "") {
  const group = WEBSITE_NAVIGATOR_UI_COPY[groupKey] || {};
  return textFor(group[itemKey] || fallback);
}

function modeDescription(itemKey) {
  return navigatorText("mode_descriptions", itemKey);
}

function localizedCopyGroup(groupKey) {
  const group = WEBSITE_NAVIGATOR_UI_COPY[groupKey] || {};
  const output = {};
  Object.entries(group).forEach(([key, value]) => {
    output[key] = textFor(value);
  });
  return output;
}

function cleanText(value) {
  return String(value || "").trim();
}

function slugKey(value, fallback = "item") {
  const raw = cleanText(value).toLowerCase().replaceAll("_", "-");
  let output = "";
  let separator = false;
  Array.from(raw).forEach((char) => {
    const isAsciiLetter = char >= "a" && char <= "z";
    const isDigit = char >= "0" && char <= "9";
    if (isAsciiLetter || isDigit) {
      output += char;
      separator = false;
      return;
    }
    if (!separator) {
      output += "-";
      separator = true;
    }
  });
  let cleaned = output;
  while (cleaned.includes("--")) cleaned = cleaned.replaceAll("--", "-");
  while (cleaned.startsWith("-")) cleaned = cleaned.slice(1);
  while (cleaned.endsWith("-")) cleaned = cleaned.slice(0, -1);
  return cleaned || fallback;
}

function shortcutModuleEnabledForUi(module) {
  const moduleId = cleanText(module?.module_id).replace(/-/g, "_").toLowerCase();
  return Boolean(moduleId && !DISABLED_NAVIGATOR_SHORTCUT_MODULE_IDS.has(moduleId));
}

function pageKeyFromDocument() {
  const pageScope = document.querySelector("[data-wnav-scope='page']");
  const explicit = cleanText(document.querySelector('meta[name="page-context"]')?.content || pageScope?.dataset?.wnavPage || document.body?.dataset.wnavPage);
  if (explicit) return explicit;
  const path = window.location.pathname || "/";
  if (path === "/" || path.endsWith("/index.html")) return "home";
  const parts = path.split("/").filter(Boolean);
  const last = parts[parts.length - 1] || "home";
  return slugKey(last.replace(".html", ""), "home");
}

function websiteCheckStatus(value) {
  return cleanText(value).toLowerCase();
}

function isWebsiteCheckTerminalStatus(value) {
  return ["done", "partial_done", "failed", "cancelled", "expired", "completed"].includes(websiteCheckStatus(value));
}

function isWebsiteCheckSuccessfulStatus(value) {
  return ["done", "partial_done", "completed"].includes(websiteCheckStatus(value));
}

function websiteCheckJobKey(job) {
  const payload = job || {};
  return cleanText(payload.check_id || payload.run_id || payload.job_id || payload.request_id || payload.normalized_domain || payload.url);
}

function withUrlParam(url, key, value, base = window.location.origin) {
  if (!url || !key || !value) return url;
  try {
    const parsed = new URL(url, base);
    parsed.searchParams.set(key, value);
    return parsed.toString();
  } catch {
    return url;
  }
}

function absoluteUrl(url, base = window.location.origin) {
  if (!url) return url;
  try {
    return new URL(url, base).toString();
  } catch {
    return url;
  }
}

function websiteCheckEmbeddedLiveUrl(url, base) {
  return withUrlParam(url, "navigator_overlay", "0", base);
}

function websiteCheckStatusUrl(url, base = window.location.origin) {
  if (!url) return "";
  try {
    const parsed = new URL(url, base);
    const parts = parsed.pathname.split("/").filter(Boolean);
    if (parts[0] !== "r" || !parts[1] || parts[2] !== "analysis") return "";
    const statusUrl = new URL(`/api/ai-checks/live/${encodeURIComponent(parts[1])}/status`, parsed.origin);
    ["rt", "run_id", "request_id"].forEach((key) => {
      const value = parsed.searchParams.get(key);
      if (value) statusUrl.searchParams.set(key, value);
    });
    return statusUrl.toString();
  } catch {
    return "";
  }
}

function makeEl(tag, className, text) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

function makeSvgEl(tag, attributes = {}) {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
  return el;
}

function makeInfoIcon(className) {
  const icon = makeSvgEl("svg", {
    class: className,
    viewBox: "0 0 16 16",
    "aria-hidden": "true",
    focusable: "false",
  });
  icon.appendChild(makeSvgEl("circle", { cx: "8", cy: "8", r: "6.25" }));
  icon.appendChild(makeSvgEl("path", { d: "M8 7.1v4.1" }));
  icon.appendChild(makeSvgEl("circle", { cx: "8", cy: "4.9", r: "0.75" }));
  return icon;
}

function makeSiteAssistIcon(kind, className) {
  const icon = makeSvgEl("svg", {
    class: className,
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
  });
  if (kind === "hint") {
    icon.appendChild(makeSvgEl("path", { d: "M10 2.8a5.4 5.4 0 0 0-3.5 9.5c.8.7 1.2 1.4 1.2 2.5h4.6c0-1.1.4-1.8 1.2-2.5A5.4 5.4 0 0 0 10 2.8Z" }));
    icon.appendChild(makeSvgEl("path", { d: "M7.8 16.6h4.4" }));
    icon.appendChild(makeSvgEl("path", { d: "M8.5 18h3" }));
    return icon;
  }
  icon.appendChild(makeSvgEl("path", { d: "M10 17.2a7.2 7.2 0 1 0-7.2-7.2 7.2 7.2 0 0 0 7.2 7.2Z" }));
  icon.appendChild(makeSvgEl("path", { d: "M8.3 8.1a1.9 1.9 0 1 1 3.3 1.3c-.7.7-1.4 1.1-1.4 2.2" }));
  icon.appendChild(makeSvgEl("path", { d: "M10.1 14h.01" }));
  return icon;
}

function makeDataSearchIcon(className) {
  const icon = makeSvgEl("svg", {
    class: className,
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
    focusable: "false",
  });
  icon.appendChild(makeSvgEl("path", { d: "M4.8 6.6c0-1.6 2.9-2.8 6.5-2.8s6.5 1.2 6.5 2.8-2.9 2.8-6.5 2.8-6.5-1.2-6.5-2.8Z" }));
  icon.appendChild(makeSvgEl("path", { d: "M4.8 6.6v5.2c0 1.5 2.4 2.7 5.6 2.8" }));
  icon.appendChild(makeSvgEl("path", { d: "M17.8 6.6v3.7" }));
  icon.appendChild(makeSvgEl("path", { d: "M4.8 11.8c0 1.5 2.4 2.7 5.6 2.8" }));
  icon.appendChild(makeSvgEl("path", { class: "website-navigator-data-search-scan", d: "M8 10.5h6.1" }));
  icon.appendChild(makeSvgEl("circle", { cx: "15.4", cy: "15.1", r: "3.2" }));
  icon.appendChild(makeSvgEl("path", { d: "m17.8 17.5 2.3 2.3" }));
  return icon;
}

function makeExternalLinkIcon(className) {
  const icon = makeSvgEl("svg", {
    class: className,
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
  });
  icon.appendChild(makeSvgEl("path", { d: "M8 5H5.8c-1 0-1.8.8-1.8 1.8v7.4c0 1 .8 1.8 1.8 1.8h7.4c1 0 1.8-.8 1.8-1.8V12" }));
  icon.appendChild(makeSvgEl("path", { d: "M11 4h5v5" }));
  icon.appendChild(makeSvgEl("path", { d: "m9.5 10.5 6-6" }));
  return icon;
}

function makeDownloadIcon(className) {
  const icon = makeSvgEl("svg", {
    class: className,
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
  });
  icon.appendChild(makeSvgEl("path", { d: "M10 3.5v8.2" }));
  icon.appendChild(makeSvgEl("path", { d: "m6.4 8.4 3.6 3.6 3.6-3.6" }));
  icon.appendChild(makeSvgEl("path", { d: "M4.5 14.5v1.2c0 .7.6 1.3 1.3 1.3h8.4c.7 0 1.3-.6 1.3-1.3v-1.2" }));
  return icon;
}

function appendText(parent, tag, className, text) {
  const el = makeEl(tag, className, text);
  parent.appendChild(el);
  return el;
}

function makeUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

function isoLocalDayStart(value) {
  const parts = String(value || "").split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function dateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date, days) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

function formatDayLabel(value) {
  const date = isoLocalDayStart(value);
  if (!date) return String(value || "");
  return new Intl.DateTimeFormat(currentLanguage() === "en" ? "en" : "de", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}

function formatCalendarDayNumber(value) {
  const date = isoLocalDayStart(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(currentLanguage() === "en" ? "en" : "de", {
    day: "numeric",
  }).format(date);
}

function formatCalendarMonth(value) {
  const date = isoLocalDayStart(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(currentLanguage() === "en" ? "en" : "de", {
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatWeekdayShort(date) {
  return new Intl.DateTimeFormat(currentLanguage() === "en" ? "en" : "de", {
    weekday: "short",
  }).format(date);
}

function startOfWeek(date) {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = next.getDay() || 7;
  next.setDate(next.getDate() - day + 1);
  return next;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function calendarDaysForPeriod(anchorDay, mode) {
  const anchor = isoLocalDayStart(anchorDay) || new Date();
  if (mode === "month") {
    const monthStart = startOfMonth(anchor);
    const gridStart = startOfWeek(monthStart);
    const nextMonth = addMonths(monthStart, 1);
    const gridEnd = addDays(startOfWeek(nextMonth), 7);
    const days = [];
    for (let day = gridStart; day < gridEnd; day = addDays(day, 1)) {
      days.push(dateInputValue(day));
    }
    return days;
  }
  const weekStart = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, index) => dateInputValue(addDays(weekStart, index)));
}

function periodTitle(anchorDay, mode) {
  const anchor = isoLocalDayStart(anchorDay) || new Date();
  if (mode === "month") return formatCalendarMonth(dateInputValue(anchor));
  const start = startOfWeek(anchor);
  const end = addDays(start, 6);
  const label = labels();
  const formatter = new Intl.DateTimeFormat(currentLanguage() === "en" ? "en" : "de", {
    day: "2-digit",
    month: "2-digit",
  });
  return `${label.calendarWeek} ${isoWeekNumber(dateInputValue(start))} · ${formatter.format(start)} - ${formatter.format(end)}`;
}

function isSameMonth(day, anchorDay) {
  const date = isoLocalDayStart(day);
  const anchor = isoLocalDayStart(anchorDay);
  return Boolean(date && anchor && date.getMonth() === anchor.getMonth() && date.getFullYear() === anchor.getFullYear());
}

function dayInList(days, day) {
  return days.includes(day);
}

function shiftPeriod(anchorDay, mode, direction) {
  const anchor = isoLocalDayStart(anchorDay) || new Date();
  if (mode === "month") return dateInputValue(addMonths(anchor, direction));
  return dateInputValue(addDays(anchor, direction * 7));
}

function zonedParts(value, timezone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timezone || undefined,
  }).formatToParts(date);
  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });
  return map;
}

function dateKeyInTimezone(value, timezone) {
  const parts = zonedParts(value, timezone);
  if (!parts) return "";
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function timeKeyInTimezone(value, timezone) {
  const parts = zonedParts(value, timezone);
  if (!parts) return "";
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return `${hour}:${parts.minute}`;
}

function minutesToTime(minutes) {
  const hour = String(Math.floor(minutes / 60)).padStart(2, "0");
  const minute = String(minutes % 60).padStart(2, "0");
  return `${hour}:${minute}`;
}

function timeToMinutes(value) {
  const parts = String(value || "").split(":");
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0;
  return hour * 60 + minute;
}

function slotStartValue(slot) {
  return slot.start_utc || slot.start_local || slot.start || "";
}

function slotEndValue(slot) {
  return slot.end_utc || slot.end_local || slot.end || "";
}

function slotDurationMinutes(slot) {
  const explicit = Number(slot.duration_minutes);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const start = new Date(slotStartValue(slot));
  const end = new Date(slotEndValue(slot));
  if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
    return Math.round((end.getTime() - start.getTime()) / 60000);
  }
  return 30;
}

function isoWeekNumber(value) {
  const date = isoLocalDayStart(value);
  if (!date) return 0;
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target - yearStart) / 86400000) + 1) / 7);
}

function countryFlag(code) {
  const value = cleanText(code).toUpperCase();
  if (value === "INT") return "\u{1f310}";
  if (value.length !== 2) return value;
  const first = value.charCodeAt(0);
  const second = value.charCodeAt(1);
  if (first < 65 || first > 90 || second < 65 || second > 90) return value;
  return String.fromCodePoint(0x1f1e6 + first - 65, 0x1f1e6 + second - 65);
}

function countryByCode(code) {
  return PHONE_COUNTRIES.find((item) => item.code === code) || PHONE_COUNTRIES[0];
}

function normalizePhoneValue(value) {
  let output = "";
  Array.from(cleanText(value)).forEach((char) => {
    if ((char >= "0" && char <= "9") || (char === "+" && !output)) output += char;
  });
  return output;
}

function buildPhoneValue(countryCode, localValue) {
  const local = normalizePhoneValue(localValue);
  if (!local) return "";
  if (local.startsWith("+")) return local;
  const country = countryByCode(countryCode);
  const digits = local.startsWith("0") ? local.slice(1) : local;
  if (country.code === "INT") return digits.startsWith("+") ? digits : `+${digits}`;
  return `${country.dial}${digits}`;
}

function phoneDraftParts(value) {
  const normalized = normalizePhoneValue(value);
  if (!normalized) return { countryCode: "DE", local: "" };
  const country = [...PHONE_COUNTRIES]
    .filter((item) => item.code !== "INT" && item.dial && item.dial !== "+" && normalized.startsWith(item.dial))
    .sort((left, right) => right.dial.length - left.dial.length)[0];
  if (country) return { countryCode: country.code, local: normalized.slice(country.dial.length) };
  if (normalized.startsWith("+")) return { countryCode: "INT", local: normalized.slice(1) };
  return { countryCode: "DE", local: normalized };
}

function looksLikeEmail(value) {
  const email = cleanText(value).toLowerCase();
  const at = email.indexOf("@");
  if (at <= 0 || at !== email.lastIndexOf("@")) return false;
  if (email.includes(" ")) return false;
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf(".");
  return dot > 0 && dot < domain.length - 1;
}

function slotMatches(left, right) {
  if (!left || !right) return false;
  return cleanText(left.staff_id) === cleanText(right.staff_id) && cleanText(slotStartValue(left)) === cleanText(slotStartValue(right));
}

function slotStaffLabel(slot) {
  return cleanText(slot?.staff_label || slot?.staff_name || slot?.staff_email) || labels().teamMember;
}

function staffInitials(value) {
  const parts = cleanText(value).split(" ").filter(Boolean);
  const first = parts[0]?.charAt(0) || "";
  const second = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${second}`.toUpperCase() || "W";
}

function formatSlotRange(slot, timezone) {
  const start = new Date(slotStartValue(slot));
  const end = new Date(slotEndValue(slot));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const locale = currentLanguage() === "en" ? "en" : "de";
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: timezone || slot?.timezone || undefined,
  });
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone || slot?.timezone || undefined,
  });
  return `${dateFormatter.format(start)}, ${timeFormatter.format(start)} - ${timeFormatter.format(end)}`;
}

function timezoneOptions(selected) {
  const current = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let supported = [];
  try {
    supported = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  } catch {
    supported = [];
  }
  return Array.from(new Set([selected, current, ...BOOKING_TIMEZONE_FALLBACKS, ...supported].filter(Boolean)));
}

function timezoneLabel(value) {
  return cleanText(value).split("_").join(" ");
}

function labels() {
  return localizedCopyGroup("labels");
}

function loadingMessages() {
  return WEBSITE_NAVIGATOR_UI_COPY.loading_messages || {};
}

class WebsiteNavigator {
  constructor(root) {
    this.root = root;
    this.overlay = document.querySelector("[data-website-navigator-overlay]");
    this.launchers = Array.from(document.querySelectorAll("[data-website-navigator-open]"));
    this.closeButtons = Array.from(document.querySelectorAll("[data-website-navigator-close]"));
    this.titleEl = root.querySelector(".website-navigator-title");
    this.titleDefaultHtml = this.titleEl?.innerHTML || "";
    this.messagesEl = root.querySelector("[data-website-navigator-messages]");
    this.bodyEl = root.querySelector(".website-navigator-body");
    this.flowSection = root.querySelector('[data-website-navigator-section="flow"]');
    this.componentsEl = root.querySelector("[data-website-navigator-components]");
    this.inputEl = root.querySelector("[data-website-navigator-input]");
    this.inputRow = root.querySelector(".website-navigator-input-row");
    this.sendButton = root.querySelector("[data-website-navigator-send]");
    this.micButton = root.querySelector("[data-website-navigator-mic]");
    this.bottomBar = document.querySelector("[data-website-navigator-bottom-bar]");
    this.bottomInput = this.bottomBar?.querySelector("[data-website-navigator-bottom-input]");
    this.bottomInputRow = this.bottomBar?.querySelector(".website-navigator-bottom-input-row");
    this.bottomSendButton = this.bottomBar?.querySelector("[data-website-navigator-bottom-send]");
    this.bottomMicButton = this.bottomBar?.querySelector("[data-website-navigator-bottom-mic]");
    this.bottomChipRail = this.bottomBar?.querySelector("[data-website-navigator-bottom-chips]");
    this.bottomToggleButton = this.bottomBar?.querySelector("[data-website-navigator-bottom-toggle]");
    this.infoSection = root.querySelector('[data-website-navigator-section="info"]');
    this.suggestionsSection = root.querySelector('[data-website-navigator-section="suggestions"]');
    this.chatSection = root.querySelector('[data-website-navigator-section="chat"]');
    this.statusEl = root.querySelector("[data-website-navigator-status]");
    this.chipLayer = root.querySelector("[data-website-navigator-chips]");
    this.dragHandleEl = root.querySelector(".website-navigator-head");
    this.ids = null;
    this.sessionToken = safeStorageGet(STORAGE_KEY);
    this.bootstrapping = null;
    this.busy = false;
    this.lastModuleState = {};
    this.lastComponents = [];
    this.lastBookingSetupComponents = [];
    this.lastUserMessage = "";
    this.activeShortcutMode = "";
    this.usedShortcutModes = new Set();
    this.finishedShortcutModes = new Map();
    this.navigatorModules = { target_modules: [], shortcut_modules: [], aliases: {}, routes: [] };
    this.navigatorModuleById = new Map();
    this.sectionStates = new Map();
    this.bookingReservation = null;
    this.activeBooking = null;
    this.bookingCalendarView = { mode: "week", anchorDay: "" };
    this.bookingTimezone = "";
    this.bookingTimeFilterKey = "";
    this.selectedSlot = null;
    this.selectedSlotTimezone = "";
    this.bookingCalendarToggle = null;
    this.bookingFinalizeShell = null;
    this.bookingPhoneCountry = DEFAULT_PHONE_COUNTRY;
    this.bookingPhoneMenuOpen = false;
    this.bookingCountrySearch = "";
    this.bookingContactDraft = { name: "", email: "", phone: "", website_url: "" };
    this.contactPhoneCountry = DEFAULT_PHONE_COUNTRY;
    this.contactPhoneMenuOpen = false;
    this.contactCountrySearch = "";
    this.contactDraft = { name: "", email: "", phone: "", website_url: "", message: "", privacy_consent: false };
    this.contactAttachments = [];
    this.contactUploadPolicy = { allowed_extensions: [], max_file_size_bytes: 0, max_files_per_contact: 0, purpose: "" };
    this.contactUploadInput = null;
    this.contactUploadBusy = false;
    this.contactAttachmentRender = null;
    this.fileMediaOverlay = null;
    this.inquirySubmitted = false;
    this.lastInquiryOutput = {};
    this.websiteCheckJobs = [];
    this.websiteCheckSessionRuns = null;
    this.websiteCheckTerminalNotified = new Set();
    this.websiteCheckForceStart = false;
    this.websiteCheckPollTimer = 0;
    this.websiteCheckPollUrl = "";
    this.websiteCheckPollKey = "";
    this.websiteCheckSummaryRefreshTimer = 0;
    this.websiteCheckSummaryRefreshState = new Map();
    this.stream = null;
    this.overlayDrag = null;
    this.chatHasContent = false;
    this.isRecording = false;
    this.isTranscribing = false;
    this.recorder = null;
    this.mediaStream = null;
    this.recordedChunks = [];
    this.shouldTranscribe = false;
    this.transcribeController = null;
    this.inputDraftBeforeTranscribe = "";
    this.activeVoiceSurface = VOICE_SURFACE_NAVIGATOR;
    this.sendAfterTranscribeSurface = "";
    this.sessionHasNavigatorActivity = false;
    this.sessionHasNavigatorMessage = false;
    this.panelRail = null;
    this.activePanelKey = "";
    this.panelSequence = 0;
    this.panelUsage = new Map();
    this.railFrame = 0;
    this.navigatorLoadingLayer = null;
    this.navigatorOpeningSequence = 0;
    this.moduleOpeningSequence = 0;
    this.pendingIndicatorSequence = 0;
    this.consentGateActive = true;
    this.pendingOpenMode = "";
    this.pendingPrefill = "";
    this.siteAssist = null;
    this.siteAssistTimer = 0;
    this.siteAssistKey = "";
    this.siteAssistRenderedKey = "";
    this.siteAssistInlineSlots = new Map();
    this.siteAssistFloatingItems = [];
    this.questionSuggestions = null;
    this.questionSuggestionsKey = "";
    this.bottomQuestionItemsKey = "";
    this.bottomQuestionItemsCache = null;
    this.bottomChipSignature = "";
    this.assistanceRouteCardComponents = [];
    this.questionActionOptions = [];
    this.serviceFinderIntake = null;
    this.latencyDebug = createWebsiteNavigatorLatencyDebug();
  }

  init() {
    const initToken = this.debugStart("navigator.init", { area: "navigator_core", phase: "frontend" });
    if (this.overlay && this.overlay.parentElement !== document.body) document.body.appendChild(this.overlay);
    this.setConsentGateActive(!this.siteConsentGranted());
    onSiteConsentAccepted(() => {
      this.setConsentGateActive(false);
      this.renderBottomBarChips();
      if (this.overlay?.classList.contains("is-open")) this.flushPendingAfterConsent();
    });
    this.launchers.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!this.requireSiteConsent()) return;
        this.debugEvent("navigator.launcher.click", { area: "navigator_core", phase: "frontend", details: { has_mode: Boolean(cleanText(button.dataset.websiteNavigatorMode)) } });
        const mode = cleanText(button.dataset.websiteNavigatorMode);
        const text = this.promptFromTrigger(button);
        this.open({ defaultOpen: !mode && !text });
        if (mode) this.requestMode(mode);
        if (text) this.requestPrefill(text);
      });
    });
    this.closeButtons.forEach((button) => button.addEventListener("click", () => this.close()));
    this.overlay?.addEventListener("click", (event) => {
      if (event.target === this.overlay) this.close();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.closeFileMediaOverlay()) return;
      if (event.key === "Escape" && this.overlay?.classList.contains("is-open")) this.close();
    });
    document.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const modeTrigger = target?.closest("[data-website-navigator-mode]");
      if (modeTrigger && this.root.contains(modeTrigger)) {
        event.preventDefault();
        const panel = modeTrigger.closest("[data-website-navigator-panel-key]");
        const panelKey = cleanText(panel?.dataset.websiteNavigatorPanelKey);
        if (panelKey) this.markPanelUsed(panelKey);
        this.requestMode(cleanText(modeTrigger.dataset.websiteNavigatorMode));
        return;
      }
      const questionTrigger = target?.closest("[data-website-navigator-question-action]");
      if (questionTrigger && (this.root.contains(questionTrigger) || questionTrigger.closest("[data-wnav-dynamic-slot]"))) {
        event.preventDefault();
        if (!this.requireSiteConsent()) return;
        this.handleQuestionCardClick(questionTrigger);
        return;
      }
      const trigger = target?.closest("[data-website-navigator-prompt],[data-website-navigator-prompt-de],[data-website-navigator-prompt-en],[data-website-navigator-suggestion-prompt]");
      if (!trigger) return;
      event.preventDefault();
      if (!this.requireSiteConsent()) return;
      const text = this.promptFromTrigger(trigger);
      if (trigger.closest("[data-website-navigator-open]") && !this.root.contains(trigger)) {
        this.open();
        if (text) this.requestPrefill(text);
        return;
      }
      const panel = trigger.closest("[data-website-navigator-panel-key]");
      const panelKey = cleanText(panel?.dataset.websiteNavigatorPanelKey);
      if (panelKey) this.markPanelUsed(panelKey);
      const shortcutMode = trigger.classList.contains("website-navigator-tool-chip") ? this.shortcutModeFromTrigger(trigger) : "";
      if (shortcutMode) this.setActiveShortcutMode(shortcutMode);
      else if (text) this.prepareAssistanceQuestion();
      if (text) this.sendMessage(text, { showUserMessage: !trigger.classList.contains("website-navigator-tool-chip") });
    });
    this.sendButton?.addEventListener("click", () => this.sendCurrentInput());
    this.inputEl?.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendCurrentInput();
      }
    });
    this.inputEl?.addEventListener("input", () => this.resizeInput());
    this.micButton?.addEventListener("click", () => {
      if (this.micButton.disabled && !this.isRecording) return;
      if (this.isRecording) {
        this.stopRecording();
        return;
      }
      this.startRecording(VOICE_SURFACE_NAVIGATOR);
    });
    window.addEventListener("website-navigator:open", (event) => {
      const detail = event.detail || {};
      const mode = cleanText(detail.mode);
      const prompt = cleanText(detail.prompt);
      this.open({ defaultOpen: !mode && !prompt });
      if (mode) this.requestMode(mode);
      if (prompt) this.requestPrefill(prompt);
    });
    window.addEventListener("message", (event) => this.handleWebsiteCheckLiveMessage(event));
    this.bindSectionToggles();
    this.setupPanelRail();
    document.addEventListener("language:changed", () => {
      this.applyLanguageText();
      this.updateLoadingCopy();
      this.renderNavigatorShortcuts();
      this.renderSuggestions();
      this.renderBottomBarChips();
      this.syncBottomBarPlaceholder();
      this.updateBottomBarToggleLabel();
    });
    this.bindBottomBar();
    this.bindFloatingMenu();
    this.bindDesktopOverlayDrag();
    this.bindOverlayScrollContainment();
    window.addEventListener("resize", () => this.scheduleRailRows(), { passive: true });
    this.setChatVisible(false);
    this.applyLanguageText();
    this.ensureTranscribeIndicators();
    this.renderNavigatorShortcuts();
    this.renderSuggestions();
    this.renderBottomBarChips();
    this.refreshQuestionSuggestions("init");
    this.warmBottomBarShortcuts();
    this.bindSiteAssist();
    this.syncBottomBarPlaceholder();
    this.syncBottomSendButton();
    this.restoreServicePageHandoff();
    this.debugEnd(initToken);
  }

  debugEvent(name, options = {}) {
    this.latencyDebug?.event?.(name, options);
  }

  debugStart(name, options = {}) {
    return this.latencyDebug?.start?.(name, options) || null;
  }

  debugEnd(token, options = {}) {
    this.latencyDebug?.end?.(token, options);
  }

  loadingText(key, fallback = "") {
    return textFor(loadingMessages()[key]) || cleanText(fallback);
  }

  nestedLoadingText(groupKey, key, fallback = "") {
    const group = loadingMessages()[groupKey] || {};
    return textFor(group[cleanText(key)]) || cleanText(fallback);
  }

  formatLoadingText(key, replacements = {}) {
    return this.formatTemplateText(this.loadingText(key), replacements);
  }

  formatTemplateText(text, replacements = {}) {
    let output = cleanText(text);
    Object.entries(replacements).forEach(([name, value]) => {
      output = output.replaceAll(`{${name}}`, cleanText(value));
    });
    return output;
  }

  servicePageAskText(serviceName) {
    return this.formatTemplateText(labels().servicePageAskPrompt, { service_name: serviceName });
  }

  makeLoadingVisual(kind) {
    if (kind === "data-search") {
      const visual = makeEl("span", "website-navigator-loading-visual is-data-search");
      visual.appendChild(makeDataSearchIcon("website-navigator-data-search-icon"));
      visual.setAttribute("aria-hidden", "true");
      return visual;
    }
    const visual = makeEl("span", `website-navigator-loading-visual is-${kind}`);
    const count = kind === "dots" ? 16 : kind === "bars" || kind === "wave" ? 4 : 1;
    for (let index = 0; index < count; index += 1) {
      visual.appendChild(makeEl("span", ""));
    }
    visual.setAttribute("aria-hidden", "true");
    return visual;
  }

  makeLoadingState(kind, text) {
    const state = makeEl("div", `website-navigator-loading-state is-${kind}`);
    const visualKinds = {
      navigator: "dots",
      module: "bars",
      pending: "data-search",
      transcribing: "wave",
    };
    const visualKind = visualKinds[kind] || "pulse";
    state.appendChild(this.makeLoadingVisual(visualKind));
    state.appendChild(makeEl("span", "website-navigator-loading-text", text));
    return state;
  }

  ensureNavigatorLoadingLayer() {
    if (this.navigatorLoadingLayer) return this.navigatorLoadingLayer;
    const layer = makeEl("div", "website-navigator-loading-layer");
    layer.hidden = true;
    layer.setAttribute("aria-live", "polite");
    layer.dataset.websiteNavigatorLoadingLayer = "true";
    this.root.appendChild(layer);
    this.navigatorLoadingLayer = layer;
    return layer;
  }

  showNavigatorOpening() {
    const layer = this.ensureNavigatorLoadingLayer();
    layer.innerHTML = "";
    layer.appendChild(this.makeLoadingState("navigator", this.loadingText("navigator_opening")));
    layer.hidden = false;
    this.root.classList.add("is-loading-navigator");
    this.navigatorOpeningSequence += 1;
    return this.navigatorOpeningSequence;
  }

  hideNavigatorOpening(sequence) {
    if (sequence !== this.navigatorOpeningSequence) return;
    const layer = this.ensureNavigatorLoadingLayer();
    layer.hidden = true;
    layer.innerHTML = "";
    this.root.classList.remove("is-loading-navigator");
  }

  hideNavigatorOpeningForPending() {
    if (!this.navigatorLoadingLayer || this.navigatorLoadingLayer.hidden) return;
    this.navigatorOpeningSequence += 1;
    this.hideNavigatorOpening(this.navigatorOpeningSequence);
  }

  moduleDisplayName(mode) {
    const normalizedMode = this.normalizeShortcutMode(mode);
    const module = this.moduleForMode(normalizedMode);
    const shortcut = module?.shortcut && typeof module.shortcut === "object" ? module.shortcut : {};
    return cleanText(textFor(shortcut.label_i18n || shortcut.label)) || cleanText(shortcut.label) || cleanText(module?.name) || navigatorText("labels", "navigator");
  }

  moduleForMode(mode) {
    const normalizedMode = this.normalizeShortcutMode(mode);
    if (!normalizedMode) return null;
    return this.navigatorModuleById.get(normalizedMode) || PRESET_NAVIGATOR_SHORTCUT_MODULES.find((module) => this.normalizeShortcutMode(module?.module_id) === normalizedMode) || null;
  }

  moduleDescription(mode) {
    const module = this.moduleForMode(mode);
    const shortcut = module?.shortcut && typeof module.shortcut === "object" ? module.shortcut : {};
    return cleanText(textFor(shortcut.description_i18n || shortcut.description)) || cleanText(shortcut.description) || cleanText(module?.description);
  }

  showModuleOpening(mode) {
    if (!this.componentsEl || !this.flowSection) return 0;
    const moduleName = this.moduleDisplayName(mode);
    this.moduleOpeningSequence += 1;
    this.setConsentGateActive(false);
    this.componentsEl.innerHTML = "";
    this.componentsEl.appendChild(this.makeLoadingState("module", this.formatLoadingText("module_opening", { module: moduleName })));
    this.flowSection.hidden = false;
    this.markPanelUsed("flow");
    this.activatePanel("flow", { markUsed: false });
    this.scheduleRailRows();
    return this.moduleOpeningSequence;
  }

  finishModuleOpening(sequence, callback) {
    window.setTimeout(() => {
      if (sequence && sequence !== this.moduleOpeningSequence) return;
      callback();
    }, MODULE_OPENING_MIN_MS);
  }

  messageLoadingText(mode = "") {
    const targetMode = this.normalizeShortcutMode(mode) || this.normalizeShortcutMode(this.activeShortcutMode) || this.assistanceMode();
    return this.nestedLoadingText("modules", targetMode, this.loadingText("default"));
  }

  actionLoadingText(actionId) {
    return this.nestedLoadingText("actions", actionId, this.messageLoadingText());
  }

  showPendingIndicator(text) {
    if (!this.messagesEl) return "";
    this.pendingIndicatorSequence += 1;
    const token = `pending-${this.pendingIndicatorSequence}`;
    this.chatHasContent = true;
    this.setChatVisible(true);
    const row = makeEl("div", "website-navigator-message-row is-loading");
    row.dataset.websiteNavigatorPending = token;
    const bubble = makeEl("div", "website-navigator-message is-loading");
    bubble.appendChild(this.makeLoadingState("pending", cleanText(text) || this.loadingText("default")));
    row.appendChild(bubble);
    this.messagesEl.appendChild(row);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    this.hideNavigatorOpeningForPending();
    return token;
  }

  hidePendingIndicator(token) {
    const key = cleanText(token);
    if (!key || !this.messagesEl) return;
    this.messagesEl.querySelector(`[data-website-navigator-pending="${key}"]`)?.remove();
  }

  clearPendingIndicators() {
    if (!this.messagesEl) return;
    this.messagesEl.querySelectorAll("[data-website-navigator-pending]").forEach((node) => node.remove());
  }

  sameOriginPath(value) {
    const raw = cleanText(value);
    if (!raw) return "";
    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return "";
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return "";
    }
  }

  servicePageHandoffPayload(service, pageUrl) {
    const path = this.sameOriginPath(pageUrl);
    const components = Array.isArray(this.lastComponents) ? this.lastComponents : [];
    if (!path || !components.length) return null;
    const serviceName = cleanText(textFor(service?.title) || service?.name || service?.service_name || service?.service_id);
    if (!serviceName) return null;
    return {
      version: 1,
      createdAt: Date.now(),
      path,
      activeShortcutMode: cleanText(this.activeShortcutMode),
      serviceName,
      components,
    };
  }

  storeServicePageHandoff(service, pageUrl) {
    const payload = this.servicePageHandoffPayload(service, pageUrl);
    if (!payload) return;
    try {
      safeSessionSet(HANDOFF_STORAGE_KEY, JSON.stringify(payload));
    } catch {
    }
  }

  takeServicePageHandoff() {
    const raw = safeSessionGet(HANDOFF_STORAGE_KEY);
    if (!raw) return null;
    safeSessionRemove(HANDOFF_STORAGE_KEY);
    try {
      const payload = JSON.parse(raw);
      const createdAt = Number(payload?.createdAt);
      if (!Number.isFinite(createdAt) || Date.now() - createdAt > HANDOFF_MAX_AGE_MS) return null;
      if (cleanText(payload?.path) !== this.sameOriginPath(window.location.href)) return null;
      if (!Array.isArray(payload?.components)) return null;
      return payload;
    } catch {
      return null;
    }
  }

  componentsWithServicePageHandoff(payload) {
    const serviceName = cleanText(payload?.serviceName);
    if (!serviceName) return [];
    return (payload.components || []).map((component) => {
      if (cleanText(component?.component_type) !== "service_cards") return component;
      return {
        ...component,
        props: {
          ...(component.props || {}),
          service_page_handoff: { service_name: serviceName },
        },
      };
    });
  }

  restoreServicePageHandoff() {
    const payload = this.takeServicePageHandoff();
    if (!payload) return;
    const components = this.componentsWithServicePageHandoff(payload);
    if (!components.length) return;
    this.open();
    this.ensureBootstrap().then(() => {
      const mode = this.normalizeShortcutMode(payload.activeShortcutMode);
      if (mode) {
        this.markShortcutModeUsed(mode);
        this.setActiveShortcutMode(mode);
      }
      this.collapseInfoCard();
      this.lastComponents = components;
      this.renderComponents(components);
      window.setTimeout(() => this.inputEl?.focus({ preventScroll: true }), 80);
    }).catch(() => { });
  }

  ensureTranscribeIndicators() {
    this.voiceSurfaceList().forEach((nodes) => {
      const shell = nodes.input?.closest(".website-navigator-input-shell");
      if (!shell || shell.querySelector(".website-navigator-transcribe-indicator")) return;
      const indicator = makeEl("div", "website-navigator-transcribe-indicator");
      indicator.appendChild(this.makeLoadingState("transcribing", this.loadingText("transcribing", labels().voiceTranscribing)));
      shell.appendChild(indicator);
    });
  }

  updateTranscribeIndicatorText() {
    const text = this.loadingText("transcribing", labels().voiceTranscribing);
    this.root.querySelectorAll(".website-navigator-transcribe-indicator .website-navigator-loading-text").forEach((node) => {
      node.textContent = text;
    });
    this.bottomBar?.querySelectorAll(".website-navigator-transcribe-indicator .website-navigator-loading-text").forEach((node) => {
      node.textContent = text;
    });
  }

  updateLoadingCopy() {
    this.updateTranscribeIndicatorText();
    if (this.navigatorLoadingLayer && !this.navigatorLoadingLayer.hidden) {
      const target = this.navigatorLoadingLayer.querySelector(".website-navigator-loading-text");
      if (target) target.textContent = this.loadingText("navigator_opening");
    }
  }

  debugOperation(prefix) {
    return this.latencyDebug?.operation?.(prefix) || prefix;
  }

  debugAreaForAction(actionId) {
    const key = cleanText(actionId);
    if (key.startsWith("scheduling.")) return "booking";
    if (key.startsWith("website_check.")) return "website_check";
    if (key.startsWith("inquiry.")) return "contact";
    if (key.startsWith("upload.")) return "upload";
    if (key.startsWith("service_") || key.startsWith("service.")) return "service";
    return "api";
  }

  applyLanguageText() {
    const label = labels();
    if (this.inputEl) this.inputEl.placeholder = label.inputPlaceholder;
    if (this.sendButton) this.sendButton.setAttribute("aria-label", label.sendMessage);
    this.updateMicButtonLabels();
    this.updateBottomBarToggleLabel();
    this.updateMicAvailability();
    this.syncShortcutChipStates(this.activeShortcutMode);
    this.setStatus(this.statusEl?.classList.contains("is-error") ? "error" : this.statusEl?.classList.contains("is-busy") ? "busy" : "ready");
  }

  bindSiteAssist() {
    this.debugEvent("site_assist.bind", { area: "site_assist", phase: "frontend" });
    window.addEventListener("scroll", () => this.handleSiteAssistViewportChange("scroll"), { passive: true });
    window.addEventListener("resize", () => this.handleSiteAssistViewportChange("resize"), { passive: true });
    document.addEventListener("language:changed", () => this.scheduleSiteAssist(60, "language"));
    this.scheduleSiteAssist(80, "init");
  }

  visibleSection() {
    const sections = Array.from(document.querySelectorAll("[data-wnav-scope='section']")).filter((item) => item instanceof HTMLElement);
    const viewportAnchor = window.innerHeight * 0.42;
    let selected = null;
    let selectedDistance = Number.POSITIVE_INFINITY;
    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      const distance = Math.abs(rect.top - viewportAnchor);
      if (distance < selectedDistance) {
        selected = section;
        selectedDistance = distance;
      }
    });
    return selected;
  }

  visibleBlock(section) {
    if (!(section instanceof HTMLElement)) return null;
    const blocks = Array.from(section.querySelectorAll("[data-wnav-scope='block']")).filter((item) => item instanceof HTMLElement);
    let selected = null;
    let selectedDistance = Number.POSITIVE_INFINITY;
    blocks.forEach((block) => {
      const rect = block.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      const distance = Math.abs(rect.top - window.innerHeight * 0.48);
      if (distance < selectedDistance) {
        selected = block;
        selectedDistance = distance;
      }
    });
    return selected;
  }

  siteMetadata() {
    const section = this.visibleSection();
    const block = this.visibleBlock(section);
    return {
      url: window.location.href,
      page_key: pageKeyFromDocument(),
      section_key: cleanText(section?.dataset.wnavSection),
      block_key: cleanText(block?.dataset.wnavBlock),
      site_snapshot_hash: cleanText(this.siteAssist?.snapshot_hash),
    };
  }

  siteAssistCacheKey(metadata) {
    return [metadata.page_key, metadata.section_key, metadata.block_key, currentLanguage()].join(":");
  }

  handleSiteAssistViewportChange(reason = "scroll") {
    const metadata = this.siteMetadata();
    const key = this.siteAssistCacheKey(metadata);
    this.scheduleSiteAssist(key && key !== this.siteAssistRenderedKey ? 90 : 160, reason);
  }

  siteAssistHoldElements() {
    const inlineSlots = Array.from(this.siteAssistInlineSlots.values()).filter((slot) => slot instanceof HTMLElement);
    const floatingChips = Array.from(document.querySelectorAll(".website-navigator-site-assist-chip")).filter((chip) => chip instanceof HTMLElement);
    return [...inlineSlots, ...floatingChips];
  }

  isSiteAssistHeld() {
    const activeElement = document.activeElement;
    return this.siteAssistHoldElements().some((element) => {
      if (element.matches(":hover")) return true;
      return activeElement instanceof HTMLElement && element.contains(activeElement);
    });
  }

  scheduleSiteAssist(delay = 160, reason = "manual") {
    if (this.siteAssistTimer) {
      window.clearTimeout(this.siteAssistTimer);
      this.debugEvent("site_assist.schedule.reset", { area: "site_assist", phase: "frontend", details: { reason } });
    }
    this.debugEvent("site_assist.schedule", { area: "site_assist", phase: "frontend", details: { delay, reason } });
    this.siteAssistTimer = window.setTimeout(() => {
      this.siteAssistTimer = 0;
      this.refreshSiteAssist(reason);
    }, delay);
  }

  async refreshSiteAssist(reason = "manual") {
    const operationId = this.debugOperation("site_assist");
    const token = this.debugStart("site_assist.request", { area: "site_assist", phase: "frontend", operationId, details: { reason } });
    const metadata = this.siteMetadata();
    const key = this.siteAssistCacheKey(metadata);
    this.debugEvent("site_assist.metadata.read", {
      area: "site_assist",
      phase: "frontend",
      operationId,
      details: {
        key,
        page_key: metadata.page_key,
        has_section_key: Boolean(metadata.section_key),
        has_block_key: Boolean(metadata.block_key),
      },
    });
    if (this.isSiteAssistHeld() && key !== this.siteAssistRenderedKey) {
      this.scheduleSiteAssist(140, "held");
      this.debugEnd(token, { status: "skipped", details: { key, reason: "held" } });
      return;
    }
    if (key === this.siteAssistKey && key === this.siteAssistRenderedKey && this.siteAssist?.page) {
      this.debugEvent("site_assist.request.skip_ready_cache", { area: "site_assist", phase: "frontend", operationId, details: { key } });
      this.debugEnd(token, { status: "skipped", details: { key } });
      return;
    }
    this.siteAssistKey = key;
    const params = new URLSearchParams({
      page_key: metadata.page_key,
      url: metadata.url,
      locale: currentLanguage(),
    });
    if (metadata.section_key) params.set("section_key", metadata.section_key);
    if (metadata.block_key) params.set("block_key", metadata.block_key);
    try {
      const data = await this.fetchJson(`/site-assist?${params.toString()}`, {
        debugArea: "site_assist",
        debugOperationId: operationId,
        debugEvent: "site_assist.request",
      });
      this.siteAssist = data || null;
      this.debugEvent("site_assist.request.end", {
        area: "site_assist",
        phase: "frontend",
        operationId,
        details: {
          key,
          status: cleanText(data?.status),
          hints_count: Array.isArray(data?.hints) ? data.hints.length : 0,
          questions_count: Array.isArray(data?.questions) ? data.questions.length : 0,
        },
      });
      this.renderSiteAssist();
      this.refreshQuestionSuggestions("site_assist");
      this.debugEnd(token, { details: { key, status: cleanText(data?.status) } });
    } catch {
      this.siteAssist = null;
      this.renderSiteAssist();
      this.debugEnd(token, { status: "error", details: { key } });
    }
  }

  siteAssistDisplayRole(role) {
    const roles = this.siteAssist?.display_mode_roles;
    return cleanText(roles && typeof roles === "object" ? roles[role] : "");
  }

  defaultSiteAssistDisplayMode() {
    return cleanText(this.siteAssist?.default_display_mode) || this.siteAssistDisplayRole("floating") || "floating";
  }

  siteAssistDisplayMode(item) {
    return cleanText(item?.display_mode) || this.defaultSiteAssistDisplayMode();
  }

  isSiteAssistDisplay(item, role) {
    const roleValue = this.siteAssistDisplayRole(role) || role;
    return Boolean(roleValue && this.siteAssistDisplayMode(item) === roleValue);
  }

  siteAssistItemsForDisplay(items, role) {
    return (Array.isArray(items) ? items : []).filter((item) => this.isSiteAssistDisplay(item, role));
  }

  applySiteAssistQuestionDataset(button, question, label) {
    button.dataset.websiteNavigatorPrompt = cleanText(question.prompt_override_en || question.prompt_en || question.label_en);
    button.dataset.websiteNavigatorQuestionAction = cleanText(question.action);
    button.dataset.websiteNavigatorQuestionBehavior = cleanText(question.frontend_behavior);
    button.dataset.websiteNavigatorQuestionKey = cleanText(question.question_key);
    button.dataset.websiteNavigatorQuestionSource = cleanText(question.source);
    button.dataset.websiteNavigatorQuestionSnapshotHash = cleanText(this.siteAssist?.snapshot_hash);
    button.dataset.websiteNavigatorQuestionLabel = label;
    button.dataset.websiteNavigatorQuestionScope = cleanText(question.target_scope);
    button.dataset.websiteNavigatorQuestionScopeKey = cleanText(question.scope_key);
  }

  makeInlineSiteAssistQuestion(question) {
    const label = cleanText(question.label_en);
    const button = makeEl("button", "wnav-site-question");
    button.type = "button";
    button.appendChild(makeSiteAssistIcon("question", "wnav-site-question-icon"));
    button.appendChild(makeEl("span", "wnav-site-question-label", label));
    this.applySiteAssistQuestionDataset(button, question, label);
    return button;
  }

  siteAssistTargetKey(metadata) {
    if (metadata.block_key) return ["block", metadata.page_key, metadata.section_key, metadata.block_key].join(":");
    if (metadata.section_key) return ["section", metadata.page_key, metadata.section_key].join(":");
    return ["page", metadata.page_key].join(":");
  }

  setInlineSiteAssistSlotVisible(slot, visible) {
    slot.classList.toggle("is-visible", Boolean(visible));
    slot.classList.toggle("is-faded", !visible);
    slot.setAttribute("aria-hidden", visible ? "false" : "true");
    slot.querySelectorAll("button").forEach((button) => {
      if (visible) button.removeAttribute("tabindex");
      else button.setAttribute("tabindex", "-1");
    });
  }

  inlineSiteAssistSlot(key, target) {
    let slot = this.siteAssistInlineSlots.get(key);
    if (!slot || !target.contains(slot)) {
      slot = makeEl("div", "wnav-site-assist-slot is-faded");
      slot.dataset.wnavDynamicSlot = "true";
      slot.setAttribute("aria-hidden", "true");
      target.appendChild(slot);
      this.siteAssistInlineSlots.set(key, slot);
    }
    return slot;
  }

  renderInlineSiteAssistSlot(slot, hints, questions) {
    slot.innerHTML = "";
    hints.forEach((hint) => appendText(slot, "p", "wnav-site-hint", cleanText(hint.text_en)));
    if (questions.length) {
      const questionRow = makeEl("div", "wnav-site-question-row");
      questions.forEach((question) => questionRow.appendChild(this.makeInlineSiteAssistQuestion(question)));
      slot.appendChild(questionRow);
    }
  }

  siteAssistFloatingChipItems(hints, questions) {
    const hintItems = hints
      .map((hint) => ({
        kind: "hint",
        key: cleanText(hint.hint_key || hint.id || hint.text_en),
        label: cleanText(hint.text_en),
      }))
      .filter((item) => item.label);
    const questionItems = questions
      .map((question) => ({
        kind: "question",
        key: cleanText(question.question_key || question.id || question.label_en),
        label: cleanText(question.label_en),
        prompt: cleanText(question.prompt_override_en || question.prompt_en || question.label_en),
        action: cleanText(question.action),
        question,
      }))
      .filter((item) => item.label && item.prompt);
    return [...hintItems, ...questionItems];
  }

  syncBottomBarFloatingState() {
    const hasLeavingItems = Boolean(this.bottomChipRail?.querySelector(".website-navigator-site-assist-chip.is-leaving"));
    const hasFloatingItems = Boolean(this.siteAssistFloatingItems.length) || hasLeavingItems;
    this.bottomBar?.classList.toggle("has-site-assist-chip", hasFloatingItems);
    this.bottomChipRail?.classList.toggle("has-site-assist-chip", hasFloatingItems);
  }

  renderSiteAssist() {
    const token = this.debugStart("site_assist.render", { area: "site_assist", phase: "render" });
    if (this.isSiteAssistHeld()) {
      this.siteAssistRenderedKey = "";
      this.scheduleSiteAssist(140, "held-render");
      this.debugEnd(token, { status: "skipped", details: { reason: "held" } });
      return;
    }
    if (!this.siteAssist || !this.siteAssist.page) {
      this.siteAssistFloatingItems = [];
      this.siteAssistInlineSlots.forEach((slot) => this.setInlineSiteAssistSlotVisible(slot, false));
      this.renderBottomBarChips();
      this.siteAssistRenderedKey = this.siteAssistKey;
      this.debugEvent("site_assist.render.noop", { area: "site_assist", phase: "render", details: { status: cleanText(this.siteAssist?.status) } });
      this.debugEnd(token, { status: "skipped", details: { status: cleanText(this.siteAssist?.status) } });
      return;
    }
    const metadata = this.siteMetadata();
    const target = this.siteAssistTarget(metadata);
    const inlineHints = this.siteAssistItemsForDisplay(this.siteAssist.hints, "inline");
    const floatingHints = this.siteAssistItemsForDisplay(this.siteAssist.hints, "floating");
    const inlineQuestions = this.siteAssistItemsForDisplay(this.siteAssist.questions, "inline");
    const floatingQuestions = this.siteAssistItemsForDisplay(this.siteAssist.questions, "floating");
    this.siteAssistFloatingItems = this.siteAssistFloatingChipItems(floatingHints, floatingQuestions);
    const activeInlineKey = target ? this.siteAssistTargetKey(metadata) : "";
    this.siteAssistInlineSlots.forEach((slot, key) => {
      if (key !== activeInlineKey) this.setInlineSiteAssistSlotVisible(slot, false);
    });
    if (target && (inlineHints.length || inlineQuestions.length)) {
      const slot = this.inlineSiteAssistSlot(activeInlineKey, target);
      this.renderInlineSiteAssistSlot(slot, inlineHints, inlineQuestions);
      window.requestAnimationFrame(() => this.setInlineSiteAssistSlotVisible(slot, true));
    } else if (activeInlineKey && this.siteAssistInlineSlots.has(activeInlineKey)) {
      this.setInlineSiteAssistSlotVisible(this.siteAssistInlineSlots.get(activeInlineKey), false);
    }
    this.renderBottomBarChips();
    this.siteAssistRenderedKey = this.siteAssistKey;
    this.debugEnd(token, {
      details: {
        inline_hints_count: inlineHints.length,
        floating_hints_count: floatingHints.length,
        inline_questions_count: inlineQuestions.length,
        floating_questions_count: floatingQuestions.length,
      },
    });
  }

  siteAssistTarget(metadata) {
    if (metadata.block_key) {
      const block = Array.from(document.querySelectorAll("[data-wnav-scope='block']")).find((item) => item instanceof HTMLElement && cleanText(item.dataset.wnavBlock) === metadata.block_key);
      if (block instanceof HTMLElement) return block;
    }
    if (metadata.section_key) {
      const section = Array.from(document.querySelectorAll("[data-wnav-scope='section']")).find((item) => item instanceof HTMLElement && cleanText(item.dataset.wnavSection) === metadata.section_key);
      if (section instanceof HTMLElement) return section;
    }
    return document.querySelector("main") || document.body;
  }

  setupPanelRail() {
    if (!this.bodyEl) return;
    this.panelRail = makeEl("div", "website-navigator-panel-rail");
    this.panelRail.dataset.websiteNavigatorPanelRail = "true";
    this.panelRail.dataset.rowMode = "one";
    this.bodyEl.appendChild(this.panelRail);
    [
      [this.flowSection, "flow", 0],
      [this.suggestionsSection, "suggestions", 1],
      [this.chatSection, "chat", 2],
    ].forEach(([section, key, order]) => {
      if (!section) return;
      section.dataset.websiteNavigatorPanelKey = key;
      section.dataset.websiteNavigatorBaseOrder = String(order);
    });
  }

  setConsentGateActive(active) {
    this.consentGateActive = Boolean(active);
    this.root.classList.toggle("is-consent-gated", this.consentGateActive);
    this.bodyEl?.classList.toggle("is-consent-gated", this.consentGateActive);
  }

  siteConsentGranted() {
    return hasSiteConsent();
  }

  requireSiteConsent() {
    if (this.siteConsentGranted()) return true;
    requestSiteConsentAttention();
    return false;
  }

  hasConsentGate(components) {
    return (components || []).some((component) => cleanText(component.component_type) === "consent_gate");
  }

  consentGateFromComponents(components) {
    return (components || []).find((component) => cleanText(component.component_type) === "consent_gate") || null;
  }

  filterInternalConsentGate(components) {
    if (!this.siteConsentGranted()) return components || [];
    return (components || []).filter((component) => cleanText(component.component_type) !== "consent_gate");
  }

  async syncNavigatorConsentFromSiteConsent(component) {
    if (!this.siteConsentGranted() || !this.ids || !component) return;
    const props = component.props || {};
    const actionId = cleanText(props.action_id) || "navigator.consent.accept";
    const operationId = this.debugOperation(actionId);
    await this.fetchJson("/component-actions", {
      method: "POST",
      debugArea: "consent",
      debugOperationId: operationId,
      debugEvent: "consent.site_banner_accept",
      actionId,
      body: JSON.stringify({
        journey_id: this.ids.journey_id,
        session_id: this.ids.session_id,
        action_id: actionId,
        component_key: "site_consent_banner",
        idempotency_key: makeUuid(),
        payload: {
          component_key: "site_consent_banner",
          purpose: cleanText(props.purpose),
          text_version: cleanText(props.text_version),
          locale: currentLanguage(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    });
  }

  requestMode(mode) {
    const normalizedMode = this.normalizeShortcutMode(mode);
    if (!normalizedMode) return;
    this.markNavigatorSessionActivity();
    const operationId = this.debugOperation(`mode.${normalizedMode}`);
    const token = this.debugStart("navigator.mode.request", { area: normalizedMode || "navigator_core", phase: "frontend", operationId, details: { mode: normalizedMode } });
    this.setActiveShortcutMode(normalizedMode);
    if (this.consentGateActive) {
      this.pendingOpenMode = normalizedMode;
      this.debugEnd(token, { status: "pending_consent", details: { mode: normalizedMode } });
      return;
    }
    this.openMode(normalizedMode);
    this.debugEnd(token, { details: { mode: normalizedMode } });
  }

  requestPrefill(content) {
    const value = cleanText(content);
    if (!value) return;
    if (this.consentGateActive) {
      this.pendingPrefill = value;
      return;
    }
    this.prefillInput(value);
    this.collapseInfoCard();
  }

  questionPayloadFromTrigger(trigger) {
    return {
      action: cleanText(trigger.dataset.websiteNavigatorQuestionAction),
      frontend_behavior: cleanText(trigger.dataset.websiteNavigatorQuestionBehavior),
      question_key: cleanText(trigger.dataset.websiteNavigatorQuestionKey),
      source: cleanText(trigger.dataset.websiteNavigatorQuestionSource),
      snapshot_id: cleanText(trigger.dataset.websiteNavigatorQuestionSnapshotId),
      snapshot_hash: cleanText(trigger.dataset.websiteNavigatorQuestionSnapshotHash),
      label_en: cleanText(trigger.dataset.websiteNavigatorQuestionLabel || trigger.textContent),
      prompt_en: this.promptFromTrigger(trigger) || cleanText(trigger.textContent),
      scope_type: cleanText(trigger.dataset.websiteNavigatorQuestionScope),
      scope_key: cleanText(trigger.dataset.websiteNavigatorQuestionScopeKey),
    };
  }

  behaviorForAction(action) {
    const selected = this.questionActionOptions.find((item) => cleanText(item.value || item.action) === cleanText(action));
    return cleanText(selected?.frontend_behavior);
  }

  actionForBehavior(behavior) {
    const selected = this.questionActionOptions.find((item) => cleanText(item.frontend_behavior) === cleanText(behavior));
    return cleanText(selected?.value || selected?.action);
  }

  labelForAction(action) {
    const selected = this.questionActionOptions.find((item) => cleanText(item.value || item.action) === cleanText(action));
    return cleanText(textFor(selected?.label));
  }

  handleQuestionCardClick(trigger) {
    const payload = this.questionPayloadFromTrigger(trigger);
    this.markNavigatorSessionActivity();
    const behavior = cleanText(payload.frontend_behavior) || this.behaviorForAction(payload.action);
    if (payload.action) this.logQuestionClick(payload);
    if (behavior === "open_service_finder") {
      this.open();
      this.openServiceMode();
      return;
    }
    if (behavior === "open_booking") {
      this.open();
      this.openBookingMode(payload);
      return;
    }
    if (behavior === "open_contact") {
      this.open();
      this.openContactMode();
      return;
    }
    if (behavior === "open_website_check") {
      this.open();
      this.openWebsiteCheckMode();
      return;
    }
    if (behavior && behavior !== "send_curated_prompt") {
      this.showChip(labels().unavailable, "error");
      return;
    }
    if (!payload.action) {
      this.open();
      const targetMode = this.prepareAssistanceQuestion();
      this.sendMessage(payload.prompt_en, { targetMode });
      return;
    }
    this.open();
    const targetMode = this.prepareAssistanceQuestion();
    this.sendMessage(payload.prompt_en, {
      targetMode,
      metadata: this.curatedQuestionMetadata(payload),
    });
  }

  curatedQuestionMetadata(payload) {
    return {
      curated_action: cleanText(payload.action),
      question_key: cleanText(payload.question_key),
      question_source: cleanText(payload.source),
      snapshot_id: cleanText(payload.snapshot_id),
      snapshot_hash: cleanText(payload.snapshot_hash),
    };
  }

  async logQuestionClick(payload) {
    try {
      await this.ensureBootstrap();
      const metadata = this.siteMetadata();
      await this.fetchJson("/questions/clicks", {
        method: "POST",
        body: JSON.stringify({
          navigator_instance_id: cleanText(this.ids?.navigator_instance_id),
          surface_id: cleanText(this.ids?.surface_id),
          journey_id: cleanText(this.ids?.journey_id),
          session_id: cleanText(this.ids?.session_id),
          snapshot_id: cleanText(payload.snapshot_id),
          snapshot_hash: cleanText(payload.snapshot_hash),
          source: cleanText(payload.source),
          question_key: cleanText(payload.question_key),
          label_en: cleanText(payload.label_en),
          prompt_en: cleanText(payload.prompt_en),
          action: cleanText(payload.action),
          scope_type: cleanText(payload.scope_type),
          scope_key: cleanText(payload.scope_key),
          page_key: cleanText(metadata.page_key),
          section_key: cleanText(metadata.section_key),
          block_key: cleanText(metadata.block_key),
          locale: currentLanguage(),
          trace_id: makeUuid(),
          event_payload: {},
        }),
      });
    } catch (error) {
      this.debugEvent("question_click.log_failed", {
        area: "suggestions",
        phase: "frontend",
        details: { detail: cleanText(error?.message || error?.detail) },
      });
    }
  }

  flushPendingAfterConsent() {
    const mode = this.pendingOpenMode;
    const prefill = this.pendingPrefill;
    this.pendingOpenMode = "";
    this.pendingPrefill = "";
    if (mode && !this.consentGateActive) this.openMode(mode);
    if (prefill && !this.consentGateActive) this.requestPrefill(prefill);
  }

  showDefaultNavigatorView() {
    this.setActiveShortcutMode("");
    this.renderSuggestions();
    if (!this.consentGateActive) this.resetDefaultPanels();
  }

  resetDefaultPanels() {
    if (!this.bodyEl) return;
    this.activePanelKey = "";
    this.bodyEl.classList.remove("is-focus-layout");
    this.root.classList.remove("has-focus-panel");
    if (this.infoSection) {
      this.bodyEl.insertBefore(this.infoSection, this.flowSection || this.suggestionsSection || this.chatSection || this.panelRail || null);
      this.setSectionOpen(this.infoSection, true);
    }
    if (this.flowSection) {
      this.bodyEl.insertBefore(this.flowSection, this.suggestionsSection || this.chatSection || this.panelRail || null);
      this.flowSection.hidden = true;
      this.setSectionOpen(this.flowSection, true);
    }
    if (this.suggestionsSection) {
      this.bodyEl.insertBefore(this.suggestionsSection, this.chatSection || this.panelRail || null);
      this.suggestionsSection.hidden = false;
      this.setSectionOpen(this.suggestionsSection, true);
    }
    if (this.chatSection) {
      this.bodyEl.insertBefore(this.chatSection, this.panelRail || null);
      this.chatSection.hidden = !this.chatHasContent;
      this.setSectionOpen(this.chatSection, this.chatHasContent);
    }
    this.syncInputPlacement();
    this.scheduleRailRows();
  }

  panelList() {
    return [this.flowSection, this.suggestionsSection, this.chatSection].filter(Boolean);
  }

  panelByKey(key) {
    return this.panelList().find((panel) => cleanText(panel.dataset.websiteNavigatorPanelKey) === key) || null;
  }

  markPanelUsed(key) {
    if (!key) return;
    this.panelSequence += 1;
    this.panelUsage.set(key, this.panelSequence);
    const panel = this.panelByKey(key);
    if (panel) panel.classList.add("is-used-pane");
  }

  sortedRailPanels(activeKey) {
    return this.panelList()
      .filter((panel) => cleanText(panel.dataset.websiteNavigatorPanelKey) !== activeKey)
      .filter((panel) => panel !== this.flowSection || !panel.hidden)
      .sort((left, right) => {
        const leftKey = cleanText(left.dataset.websiteNavigatorPanelKey);
        const rightKey = cleanText(right.dataset.websiteNavigatorPanelKey);
        const leftUsage = this.panelUsage.get(leftKey) || 0;
        const rightUsage = this.panelUsage.get(rightKey) || 0;
        if (leftUsage !== rightUsage) return rightUsage - leftUsage;
        return Number(left.dataset.websiteNavigatorBaseOrder || 0) - Number(right.dataset.websiteNavigatorBaseOrder || 0);
      });
  }

  syncInputPlacement() {
    if (!this.inputRow || !this.chatSection || !this.suggestionsSection) return;
    const target = this.chatHasContent || this.activePanelKey === "chat" ? this.chatSection : this.suggestionsSection;
    if (!target.contains(this.inputRow)) target.appendChild(this.inputRow);
  }

  scheduleRailRows() {
    if (!this.panelRail || this.railFrame) return;
    this.railFrame = window.requestAnimationFrame(() => {
      this.railFrame = 0;
      this.updateRailRows();
    });
  }

  updateRailRows() {
    if (!this.panelRail || !this.bodyEl?.classList.contains("is-focus-layout")) return;
    const children = Array.from(this.panelRail.children).filter((child) => child instanceof HTMLElement);
    if (!children.length) {
      this.panelRail.dataset.rowMode = "one";
      return;
    }
    const width = this.panelRail.clientWidth || 0;
    const totalWidth = children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) + Math.max(0, children.length - 1) * 8;
    this.panelRail.dataset.rowMode = totalWidth <= width ? "one" : "two";
  }

  activatePanel(key, options = {}) {
    const panel = this.panelByKey(key);
    if (!panel || !this.bodyEl || !this.panelRail) return;
    this.activePanelKey = key;
    if (options.markUsed !== false) this.markPanelUsed(key);
    this.collapseInfoCard();
    this.bodyEl.classList.add("is-focus-layout");
    this.root.classList.add("has-focus-panel");
    if (this.chatSection) this.chatSection.hidden = false;
    this.panelList().forEach((item) => {
      const itemKey = cleanText(item.dataset.websiteNavigatorPanelKey);
      const isActive = itemKey === key;
      item.classList.toggle("is-active-pane", isActive);
      item.classList.toggle("is-compact-pane", !isActive);
      item.classList.toggle("is-used-pane", this.panelUsage.has(itemKey));
      if (isActive) {
        this.bodyEl.insertBefore(item, this.panelRail);
        this.setSectionOpen(item, true);
      } else {
        this.setSectionOpen(item, false);
      }
    });
    this.sortedRailPanels(key).forEach((item) => this.panelRail.appendChild(item));
    this.syncInputPlacement();
    this.scheduleRailRows();
  }

  setChatVisible(visible) {
    if (!this.chatSection) return;
    this.chatSection.hidden = !visible;
    if (visible) {
      this.markPanelUsed("chat");
      this.activatePanel("chat", { markUsed: false });
    } else {
      this.syncInputPlacement();
    }
  }

  collapseInfoCard() {
    if (this.infoSection) this.setSectionOpen(this.infoSection, false);
  }

  normalizeShortcutMode(mode) {
    const value = cleanText(mode).replace(/-/g, "_").toLowerCase();
    if (!value) return "";
    return cleanText(this.navigatorModules?.aliases?.[value]) || value;
  }

  applyNavigatorModules(payload) {
    const modules = payload && typeof payload === "object" ? payload : {};
    this.navigatorModules = {
      target_modules: Array.isArray(modules.target_modules) ? modules.target_modules : [],
      shortcut_modules: (Array.isArray(modules.shortcut_modules) ? modules.shortcut_modules : []).filter((module) => shortcutModuleEnabledForUi(module)),
      aliases: modules.aliases && typeof modules.aliases === "object" ? modules.aliases : {},
      routes: Array.isArray(modules.routes) ? modules.routes : [],
    };
    this.navigatorModuleById = new Map();
    this.navigatorModules.target_modules.forEach((module) => {
      const moduleId = cleanText(module?.module_id);
      if (moduleId) this.navigatorModuleById.set(moduleId, module);
    });
    if (this.activeShortcutMode) this.activeShortcutMode = this.normalizeShortcutMode(this.activeShortcutMode);
    if (this.pendingOpenMode) this.pendingOpenMode = this.normalizeShortcutMode(this.pendingOpenMode);
    this.renderNavigatorShortcuts();
    this.renderBottomBarChips();
  }

  shortcutChipClass(module) {
    const behavior = slugKey(cleanText(module?.open_behavior || module?.module_id), "");
    const moduleId = slugKey(cleanText(module?.module_id), "");
    const icon = slugKey(cleanText(module?.shortcut?.icon), "");
    const classes = ["website-navigator-tool-chip"];
    [behavior, moduleId, icon].filter(Boolean).forEach((key) => classes.push(`website-navigator-tool-chip--${key}`));
    return classes.join(" ");
  }

  renderNavigatorShortcuts() {
    const host = this.root.querySelector(".website-navigator-info-actions");
    if (!host) return;
    const runtimeModules = Array.isArray(this.navigatorModules?.shortcut_modules) ? this.navigatorModules.shortcut_modules : [];
    const modules = (runtimeModules.length ? runtimeModules : PRESET_NAVIGATOR_SHORTCUT_MODULES).filter((module) => shortcutModuleEnabledForUi(module));
    host.innerHTML = "";
    modules.forEach((module) => {
      const moduleId = cleanText(module?.module_id);
      if (!moduleId) return;
      const shortcut = module.shortcut && typeof module.shortcut === "object" ? module.shortcut : {};
      const label = cleanText(textFor(shortcut.label_i18n)) || cleanText(shortcut.label) || cleanText(module.name) || moduleId;
      const description = cleanText(textFor(shortcut.description_i18n)) || cleanText(shortcut.description);
      const button = makeEl("button", this.shortcutChipClass(module));
      button.type = "button";
      button.dataset.websiteNavigatorMode = moduleId;
      button.dataset.websiteNavigatorShortcutMode = moduleId;
      button.dataset.websiteNavigatorShortcutOrder = String(Number(shortcut.order || 999));
      if (description) button.title = description;
      button.textContent = label;
      host.appendChild(button);
    });
    this.syncShortcutChipStates(this.activeShortcutMode);
  }

  shortcutModeFromTrigger(trigger) {
    if (!(trigger instanceof HTMLElement)) return "";
    return this.normalizeShortcutMode(trigger.dataset.websiteNavigatorMode || trigger.dataset.websiteNavigatorShortcutMode);
  }

  shortcutStateLabel(mode) {
    const normalizedMode = this.normalizeShortcutMode(mode);
    const finishedLabel = cleanText(this.finishedShortcutModes.get(normalizedMode));
    if (finishedLabel) return finishedLabel;
    const module = this.navigatorModuleById.get(normalizedMode);
    const labels = module?.status_labels && typeof module.status_labels === "object" ? module.status_labels : {};
    return cleanText(labels.used) || navigatorText("labels", "modeOpened");
  }

  syncShortcutUsedLabel(button, mode) {
    let label = button.querySelector(".website-navigator-tool-chip-used-label");
    if (!label) {
      label = makeEl("span", "website-navigator-tool-chip-used-label");
      button.appendChild(label);
    }
    label.removeAttribute("aria-hidden");
    label.textContent = this.shortcutStateLabel(mode);
  }

  markShortcutModeUsed(mode) {
    const usedMode = this.normalizeShortcutMode(mode);
    if (!usedMode) return;
    this.usedShortcutModes.add(usedMode);
    this.syncShortcutChipStates(this.activeShortcutMode);
  }

  markShortcutModeFinished(mode, labelKey) {
    const finishedMode = this.normalizeShortcutMode(mode);
    const module = this.navigatorModuleById.get(finishedMode);
    const labels = module?.status_labels && typeof module.status_labels === "object" ? module.status_labels : {};
    const stateLabel = cleanText(labels.finished) || navigatorText("labels", cleanText(labelKey)) || cleanText(labelKey);
    if (!finishedMode || !stateLabel) return;
    this.usedShortcutModes.add(finishedMode);
    this.finishedShortcutModes.set(finishedMode, stateLabel);
    this.syncShortcutChipStates(this.activeShortcutMode);
  }

  clearShortcutModeFinished(mode) {
    const finishedMode = this.normalizeShortcutMode(mode);
    if (!finishedMode) return;
    this.finishedShortcutModes.delete(finishedMode);
    this.syncShortcutChipStates(this.activeShortcutMode);
  }

  syncShortcutChipStates(activeMode) {
    const chips = [
      ...Array.from(this.root.querySelectorAll(".website-navigator-tool-chip")),
      ...Array.from(this.bottomChipRail?.querySelectorAll(".website-navigator-tool-chip") || []),
    ];
    chips.forEach((button) => {
      const chipMode = this.shortcutModeFromTrigger(button);
      const isActive = Boolean(activeMode && chipMode && chipMode === activeMode);
      const isUsed = Boolean(chipMode && this.usedShortcutModes.has(chipMode));
      const isFinished = Boolean(chipMode && this.finishedShortcutModes.has(chipMode));
      if (this.bottomChipRail?.contains(button)) button.querySelector(".website-navigator-tool-chip-used-label")?.remove();
      else this.syncShortcutUsedLabel(button, chipMode);
      button.classList.toggle("is-active", isActive);
      button.classList.toggle("is-used-mode", isUsed);
      button.classList.toggle("is-finished-mode", isFinished);
      if (chipMode) button.setAttribute("aria-pressed", isActive ? "true" : "false");
      else button.removeAttribute("aria-pressed");
    });
  }

  setActiveShortcutMode(mode) {
    const activeMode = this.normalizeShortcutMode(mode);
    this.activeShortcutMode = activeMode;
    this.debugEvent("navigator.shortcut.active_update", { area: activeMode || "navigator_core", phase: "frontend", details: { active_mode: activeMode } });
    this.syncShortcutChipStates(activeMode);
    this.syncShortcutChipOrder(activeMode);
    this.setNavigatorTitleMode(activeMode);
  }

  syncShortcutChipOrder(activeMode) {
    const chips = Array.from(this.root.querySelectorAll(".website-navigator-tool-chip")).filter((button) => button instanceof HTMLElement);
    const containers = Array.from(new Set(chips.map((button) => button.parentElement).filter(Boolean))).filter((container) => container !== this.bottomChipRail);
    containers.forEach((container) => {
      const containerChips = Array.from(container.querySelectorAll(".website-navigator-tool-chip")).filter((button) => button instanceof HTMLElement);
      containerChips.forEach((button, index) => {
        if (!button.dataset.websiteNavigatorShortcutOrder) button.dataset.websiteNavigatorShortcutOrder = String(index);
      });
      containerChips
        .sort((left, right) => {
          const leftActive = Boolean(activeMode && this.shortcutModeFromTrigger(left) === activeMode);
          const rightActive = Boolean(activeMode && this.shortcutModeFromTrigger(right) === activeMode);
          if (leftActive !== rightActive) return leftActive ? -1 : 1;
          return Number(left.dataset.websiteNavigatorShortcutOrder || 0) - Number(right.dataset.websiteNavigatorShortcutOrder || 0);
        })
        .forEach((button) => container.appendChild(button));
    });
  }

  setNavigatorTitleMode(mode) {
    if (!this.titleEl) return;
    const activeMode = this.normalizeShortcutMode(mode);
    const activeChip = activeMode
      ? Array.from(this.root.querySelectorAll(".website-navigator-tool-chip")).find((button) => this.shortcutModeFromTrigger(button) === activeMode)
      : null;
    if (activeChip) {
      this.titleEl.innerHTML = activeChip.innerHTML;
      this.debugEvent("navigator.title.update", { area: activeMode || "navigator_core", phase: "frontend", details: { active_mode: activeMode, source: "active_chip" } });
      return;
    }
    this.titleEl.innerHTML = this.titleDefaultHtml;
    this.debugEvent("navigator.title.update", { area: "navigator_core", phase: "frontend", details: { active_mode: "", source: "default" } });
  }

  shortcutModeFromComponents(components) {
    if (this.hasBookingComponent(components)) return this.bookingMode();
    const modes = new Set();
    (components || []).forEach((component) => {
      modes.add(this.normalizeShortcutMode(component.component_type));
      modes.add(this.normalizeShortcutMode(component.component_key));
    });
    const shortcutModules = Array.isArray(this.navigatorModules?.shortcut_modules) ? this.navigatorModules.shortcut_modules : [];
    const orderedTargets = shortcutModules.map((module) => cleanText(module?.module_id)).filter(Boolean);
    for (const targetId of orderedTargets) {
      if (modes.has(targetId)) return targetId;
    }
    for (const mode of modes) {
      if (this.navigatorModuleById.has(mode)) return mode;
    }
    return "";
  }

  inquirySubmittedStorageKey() {
    const token = cleanText(this.sessionToken || safeStorageGet(STORAGE_KEY));
    return token ? `${INQUIRY_SUBMITTED_STORAGE_PREFIX}:${token}` : "";
  }

  hasSubmittedInquiry() {
    if (this.inquirySubmitted) return true;
    const key = this.inquirySubmittedStorageKey();
    return Boolean(key && safeStorageGet(key) === "1");
  }

  markInquirySubmitted(output = {}) {
    this.inquirySubmitted = true;
    this.lastInquiryOutput = output || {};
    const key = this.inquirySubmittedStorageKey();
    if (key) safeStorageSet(key, "1");
  }

  contactConfirmationComponents(output = this.lastInquiryOutput || {}) {
    return [
      {
        component_key: "contact_confirmation",
        component_type: "contact_confirmation",
        props: {
          inquiry_id: cleanText(output.inquiry_id),
          status: cleanText(output.status) || "submitted",
        },
      },
    ];
  }

  navigatorActivityStorageKey() {
    const token = cleanText(this.sessionToken || safeStorageGet(STORAGE_KEY));
    return token ? `${NAVIGATOR_ACTIVITY_STORAGE_PREFIX}:${token}` : "";
  }

  navigatorMessageStorageKey() {
    const token = cleanText(this.sessionToken || safeStorageGet(STORAGE_KEY));
    return token ? `${NAVIGATOR_MESSAGE_STORAGE_PREFIX}:${token}` : "";
  }

  hasNavigatorSessionActivity() {
    if (this.sessionHasNavigatorActivity) return true;
    const key = this.navigatorActivityStorageKey();
    return Boolean(key && safeStorageGet(key) === "1");
  }

  hasNavigatorSessionMessage() {
    if (this.sessionHasNavigatorMessage) return true;
    const key = this.navigatorMessageStorageKey();
    return Boolean(key && safeStorageGet(key) === "1");
  }

  markNavigatorSessionActivity() {
    this.sessionHasNavigatorActivity = true;
    const key = this.navigatorActivityStorageKey();
    if (key) safeStorageSet(key, "1");
    this.syncBottomBarPlaceholder();
  }

  markNavigatorSessionMessage() {
    this.sessionHasNavigatorMessage = true;
    const key = this.navigatorMessageStorageKey();
    if (key) safeStorageSet(key, "1");
    this.markNavigatorSessionActivity();
  }

  resizeInput() {
    if (!this.inputEl) return;
    this.inputEl.style.height = "auto";
    const nextHeight = Math.min(this.inputEl.scrollHeight, 136);
    this.inputEl.style.height = `${Math.max(38, nextHeight)}px`;
  }

  prefillInput(content) {
    if (!this.inputEl) return;
    const value = cleanText(content);
    if (!value) return;
    this.inputEl.value = value;
    this.resizeInput();
    this.inputEl.focus({ preventScroll: true });
  }

  resizeBottomInput() {
    if (!this.bottomInput) return;
    this.bottomInput.style.height = "auto";
    const nextHeight = Math.min(this.bottomInput.scrollHeight, 96);
    this.bottomInput.style.height = `${Math.max(38, nextHeight)}px`;
  }

  syncBottomSendButton() {
    if (!this.bottomSendButton) return;
    const hasValue = Boolean(cleanText(this.bottomInput?.value));
    const voiceActive = this.activeVoiceSurface === VOICE_SURFACE_BOTTOM && (this.isRecording || this.isTranscribing);
    const visible = hasValue || voiceActive;
    this.bottomSendButton.hidden = !visible;
    this.bottomSendButton.classList.toggle("is-visible", visible);
  }

  syncBottomBarPlaceholder() {
    if (!this.bottomInput) return;
    if (this.hasNavigatorSessionMessage()) {
      const key = currentLanguage() === "de" ? "followupPlaceholderDe" : "followupPlaceholderEn";
      this.bottomInput.placeholder = cleanText(this.bottomInput.dataset[key]) || cleanText(this.bottomInput.dataset.followupPlaceholderEn);
      this.bottomBar?.classList.add("has-session-activity");
      return;
    }
    if (this.hasNavigatorSessionActivity()) {
      this.bottomInput.placeholder = "";
      this.bottomBar?.classList.add("has-session-activity");
      return;
    }
    const key = currentLanguage() === "de" ? "placeholderDe" : "placeholderEn";
    this.bottomInput.placeholder = cleanText(this.bottomInput.dataset[key]) || cleanText(this.bottomInput.dataset.placeholderEn);
    this.bottomBar?.classList.remove("has-session-activity");
  }

  updateBottomBarToggleLabel() {
    if (!this.bottomToggleButton) return;
    const label = labels();
    const collapsed = this.bottomBar?.classList.contains("is-collapsed") === true;
    const text = collapsed ? label.bottomBarExpand : label.bottomBarCollapse;
    this.bottomToggleButton.title = text;
    this.bottomToggleButton.setAttribute("aria-label", text);
    this.bottomToggleButton.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }

  setBottomBarCollapsed(collapsed) {
    if (!this.bottomBar) return;
    this.bottomBar.classList.toggle("is-collapsed", Boolean(collapsed));
    this.updateBottomBarToggleLabel();
  }

  bindBottomBar() {
    if (!this.bottomBar) return;
    this.bottomInput?.addEventListener("input", () => {
      this.resizeBottomInput();
      this.syncBottomSendButton();
    });
    this.bottomInput?.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.submitBottomInput();
      }
    });
    this.bottomSendButton?.addEventListener("click", () => this.submitBottomInput());
    this.bottomMicButton?.addEventListener("click", () => {
      if (!this.requireSiteConsent()) return;
      if (this.bottomMicButton.disabled && !this.isRecording) return;
      if (this.isRecording) {
        this.stopRecording();
        return;
      }
      this.startRecording(VOICE_SURFACE_BOTTOM);
    });
    this.bottomToggleButton?.addEventListener("click", () => {
      this.setBottomBarCollapsed(!this.bottomBar.classList.contains("is-collapsed"));
    });
    this.bottomChipRail?.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const siteQuestionTrigger = target?.closest("[data-website-navigator-site-assist-question]");
      if (siteQuestionTrigger) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.requireSiteConsent()) return;
        this.handleQuestionCardClick(siteQuestionTrigger);
        return;
      }
      const modeTrigger = target?.closest("[data-website-navigator-bottom-mode]");
      if (modeTrigger) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.requireSiteConsent()) return;
        const mode = cleanText(modeTrigger.dataset.websiteNavigatorBottomMode || modeTrigger.dataset.websiteNavigatorMode);
        this.open();
        if (mode) this.requestMode(mode);
        return;
      }
      const questionTrigger = target?.closest("[data-website-navigator-bottom-question]");
      if (!questionTrigger) return;
      event.preventDefault();
      event.stopPropagation();
      if (!this.requireSiteConsent()) return;
      const prompt = this.promptFromTrigger(questionTrigger);
      if (!prompt) return;
      const targetMode = this.prepareAssistanceQuestion();
      this.open();
      this.sendMessage(prompt, { targetMode });
    });
    this.resizeBottomInput();
    this.syncBottomSendButton();
    this.syncBottomBarPlaceholder();
    this.updateBottomBarToggleLabel();
  }

  warmBottomBarShortcuts() {
    if (!this.bottomChipRail) return;
    if (!this.siteConsentGranted()) return;
    this.ensureBootstrap().catch(() => { });
  }

  submitBottomInput() {
    if (this.isTranscribing) return;
    if (this.isRecording) {
      if (this.activeVoiceSurface === VOICE_SURFACE_BOTTOM) this.sendAfterTranscribeSurface = VOICE_SURFACE_BOTTOM;
      this.stopRecording();
      return;
    }
    const value = cleanText(this.bottomInput?.value);
    if (!value) return;
    if (!this.requireSiteConsent()) return;
    if (this.bottomInput) this.bottomInput.value = "";
    this.resizeBottomInput();
    this.syncBottomSendButton();
    this.markNavigatorSessionActivity();
    const targetMode = this.prepareAssistanceQuestion();
    this.open();
    this.sendMessage(value, { targetMode });
  }

  voiceNodes(surface = this.activeVoiceSurface) {
    if (surface === VOICE_SURFACE_BOTTOM) {
      return {
        surface: VOICE_SURFACE_BOTTOM,
        root: this.bottomBar,
        input: this.bottomInput,
        inputRow: this.bottomInputRow,
        sendButton: this.bottomSendButton,
        micButton: this.bottomMicButton,
      };
    }
    return {
      surface: VOICE_SURFACE_NAVIGATOR,
      root: this.root,
      input: this.inputEl,
      inputRow: this.inputRow,
      sendButton: this.sendButton,
      micButton: this.micButton,
    };
  }

  voiceSurfaceList() {
    return [this.voiceNodes(VOICE_SURFACE_NAVIGATOR), this.voiceNodes(VOICE_SURFACE_BOTTOM)].filter((nodes) => nodes.micButton || nodes.input);
  }

  isVoiceAvailable() {
    return Boolean(
      typeof navigator !== "undefined" &&
      navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== "undefined" &&
      (typeof window === "undefined" || window.isSecureContext !== false)
    );
  }

  updateMicButtonLabels() {
    const label = labels();
    const voiceAvailable = this.isVoiceAvailable();
    this.voiceSurfaceList().forEach((nodes) => {
      if (!nodes.micButton) return;
      let text = label.voiceStart;
      if (!voiceAvailable) text = label.voiceUnsupported;
      if (this.isRecording && nodes.surface === this.activeVoiceSurface) text = label.voiceStop;
      nodes.micButton.title = text;
      nodes.micButton.setAttribute("aria-label", text);
    });
  }

  updateMicAvailability() {
    const voiceAvailable = this.isVoiceAvailable();
    this.voiceSurfaceList().forEach((nodes) => {
      if (!nodes.micButton) return;
      const disabled = this.isRecording
        ? nodes.surface !== this.activeVoiceSurface
        : !voiceAvailable || this.isTranscribing || this.busy;
      nodes.micButton.disabled = disabled;
      nodes.micButton.classList.toggle("is-disabled", disabled);
    });
    this.updateMicButtonLabels();
  }

  setVoiceUiState(state) {
    const recording = state === "recording";
    const transcribing = state === "transcribing";
    this.voiceSurfaceList().forEach((nodes) => {
      const active = nodes.surface === this.activeVoiceSurface;
      nodes.root?.classList.toggle("is-recording", recording && active);
      nodes.root?.classList.toggle("is-transcribing", transcribing && active);
      nodes.micButton?.classList.toggle("is-recording", recording && active);
      nodes.inputRow?.classList.toggle("is-recording", recording && active);
      nodes.inputRow?.classList.toggle("is-transcribing", transcribing && active);
      nodes.input?.classList.toggle("is-transcribing", transcribing && active);
      if (nodes.input) nodes.input.readOnly = recording && active;
    });
    if (transcribing) this.updateTranscribeIndicatorText();
    this.updateMicButtonLabels();
  }

  pickPreferredMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    const types = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
  }

  cleanupStream() {
    if (!this.mediaStream) return;
    this.mediaStream.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
  }

  transcribeUrl() {
    const configured = cleanText(this.root.dataset.transcribeBase);
    let base = configured === "origin" || configured === "auto" ? "" : configured;
    if (!base) {
      try {
        if (typeof import.meta !== "undefined" && import.meta.env) {
          base = cleanText(import.meta.env.VITE_WEROS_TRANSCRIBE_BASE);
        }
      } catch {
        base = "";
      }
    }
    if (!base) base = window.location.origin;
    const path = cleanText(this.root.dataset.transcribePath) || TRANSCRIBE_PATH;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${base.replace(/\/$/, "")}${normalizedPath}`;
  }

  extractTranscript(data) {
    if (!data) return "";
    if (typeof data.text === "string") return data.text;
    if (typeof data.transcript === "string") return data.transcript;
    if (typeof data.result === "string") return data.result;
    if (typeof data.output === "string") return data.output;
    if (typeof data?.data?.text === "string") return data.data.text;
    return "";
  }

  resetVoiceState(options = {}) {
    const skipTranscribe = options.skipTranscribe !== false;
    const activeSurface = this.activeVoiceSurface;
    if (skipTranscribe) this.sendAfterTranscribeSurface = "";
    if (this.recorder && this.isRecording) {
      this.shouldTranscribe = !skipTranscribe;
      try {
        this.recorder.stop();
      } catch {
      }
    }
    if (this.transcribeController) this.transcribeController.abort();
    this.transcribeController = null;
    this.cleanupStream();
    this.recorder = null;
    this.recordedChunks = [];
    this.isRecording = false;
    this.isTranscribing = false;
    this.voiceSurfaceList().forEach((nodes) => {
      if (nodes.input) {
        nodes.input.disabled = false;
        nodes.input.readOnly = false;
        nodes.input.classList.remove("is-transcribing");
        nodes.inputRow?.classList.remove("is-transcribing");
        if (nodes.surface === activeSurface && nodes.input.value === this.loadingText("transcribing", labels().voiceTranscribing)) nodes.input.value = this.inputDraftBeforeTranscribe || "";
      }
      if (nodes.sendButton) nodes.sendButton.disabled = false;
    });
    this.setVoiceUiState("idle");
    this.updateMicAvailability();
    this.resizeInput();
    this.resizeBottomInput();
    this.syncBottomSendButton();
  }

  async transcribeAudio(blob) {
    const nodes = this.voiceNodes();
    if (!blob || !nodes.input) return;
    const label = labels();
    let submitAfterTranscribe = false;
    this.ensureTranscribeIndicators();
    this.isTranscribing = true;
    this.setVoiceUiState("transcribing");
    this.inputDraftBeforeTranscribe = nodes.input.value;
    nodes.input.value = this.loadingText("transcribing", label.voiceTranscribing);
    nodes.input.disabled = true;
    if (nodes.sendButton) nodes.sendButton.disabled = true;
    this.updateMicAvailability();
    if (nodes.surface === VOICE_SURFACE_BOTTOM) {
      this.resizeBottomInput();
      this.syncBottomSendButton();
    } else {
      this.resizeInput();
    }
    this.transcribeController = new AbortController();
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      formData.append("lang", currentLanguage());
      const response = await fetch(this.transcribeUrl(), {
        method: "POST",
        body: formData,
        signal: this.transcribeController.signal,
      });
      if (!response.ok) throw new Error(`transcription_failed_${response.status}`);
      const data = await response.json();
      const transcript = cleanText(this.extractTranscript(data));
      if (!transcript) throw new Error("empty_transcript");
      const baseDraft = cleanText(this.inputDraftBeforeTranscribe);
      nodes.input.value = baseDraft ? `${baseDraft} ${transcript}` : transcript;
      nodes.input.focus({ preventScroll: true });
      submitAfterTranscribe = this.sendAfterTranscribeSurface === nodes.surface;
    } catch (error) {
      if (this.sendAfterTranscribeSurface === nodes.surface) this.sendAfterTranscribeSurface = "";
      if (error?.name !== "AbortError") {
        nodes.input.value = this.inputDraftBeforeTranscribe || "";
        this.showChip(label.voiceError, "error");
      }
    } finally {
      this.transcribeController = null;
      this.isTranscribing = false;
      nodes.input.disabled = false;
      nodes.input.readOnly = false;
      nodes.input.classList.remove("is-transcribing");
      nodes.inputRow?.classList.remove("is-transcribing");
      if (nodes.sendButton) nodes.sendButton.disabled = false;
      this.setVoiceUiState("idle");
      this.cleanupStream();
      this.updateMicAvailability();
      if (nodes.surface === VOICE_SURFACE_BOTTOM) {
        this.resizeBottomInput();
        this.syncBottomSendButton();
      } else {
        this.resizeInput();
      }
      if (submitAfterTranscribe) {
        this.sendAfterTranscribeSurface = "";
        window.setTimeout(() => {
          if (nodes.surface === VOICE_SURFACE_BOTTOM) this.submitBottomInput();
          else this.sendCurrentInput();
        }, 0);
      }
    }
  }

  async startRecording(surface = VOICE_SURFACE_NAVIGATOR) {
    const label = labels();
    const nextSurface = surface === VOICE_SURFACE_BOTTOM ? VOICE_SURFACE_BOTTOM : VOICE_SURFACE_NAVIGATOR;
    const nodes = this.voiceNodes(nextSurface);
    if (!nodes.micButton || !nodes.input || this.isRecording || this.isTranscribing || this.busy) return;
    this.activeVoiceSurface = nextSurface;
    if (!this.isVoiceAvailable()) {
      this.showChip(label.voiceUnsupported, "error");
      this.updateMicAvailability();
      return;
    }
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = this.pickPreferredMimeType();
      this.recorder = new MediaRecorder(this.mediaStream, mimeType ? { mimeType } : undefined);
      this.recordedChunks = [];
      this.recorder.addEventListener("dataavailable", (event) => {
        if (event.data && event.data.size > 0) this.recordedChunks.push(event.data);
      });
      this.recorder.addEventListener("stop", () => {
        const shouldRun = this.shouldTranscribe;
        this.shouldTranscribe = false;
        if (!shouldRun) return;
        const blobType = this.recorder?.mimeType || "audio/webm";
        const blob = new Blob(this.recordedChunks, { type: blobType });
        this.recordedChunks = [];
        this.transcribeAudio(blob);
      });
      this.shouldTranscribe = true;
      this.recorder.start(200);
      this.isRecording = true;
      this.setVoiceUiState("recording");
      this.updateMicAvailability();
      this.syncBottomSendButton();
    } catch (error) {
      const denied =
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError" ||
        error?.name === "PermissionDeniedError";
      this.cleanupStream();
      this.recorder = null;
      this.isRecording = false;
      this.setVoiceUiState("idle");
      this.showChip(denied ? label.voiceDenied : label.voiceError, "error");
      this.updateMicAvailability();
      this.syncBottomSendButton();
    }
  }

  stopRecording(options = {}) {
    if (!this.recorder || !this.isRecording) return;
    this.shouldTranscribe = options.skipTranscribe === true ? false : true;
    if (options.skipTranscribe === true) this.sendAfterTranscribeSurface = "";
    this.isRecording = false;
    this.setVoiceUiState("idle");
    try {
      this.recorder.stop();
    } catch {
      this.cleanupStream();
      this.recorder = null;
    }
    this.updateMicAvailability();
    this.syncBottomSendButton();
  }

  isDesktopOverlay() {
    return window.matchMedia?.("(min-width: 992px)")?.matches ?? window.innerWidth >= 992;
  }

  setOverlayDesktopLeft(left) {
    if (!this.overlay) return;
    const rect = this.overlay.getBoundingClientRect();
    const margin = 16;
    const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
    const nextLeft = Math.min(maxLeft, Math.max(margin, left));
    this.overlay.style.left = `${Math.round(nextLeft)}px`;
    this.overlay.style.right = "auto";
  }

  syncOverlayDesktopPosition() {
    if (!this.overlay) return;
    if (!this.isDesktopOverlay()) {
      this.overlay.style.removeProperty("left");
      this.overlay.style.removeProperty("right");
      this.overlay.classList.remove("is-dragging");
      this.overlayDrag = null;
      return;
    }
    const currentLeft = Number.parseFloat(this.overlay.style.left);
    if (Number.isFinite(currentLeft)) this.setOverlayDesktopLeft(currentLeft);
  }

  endOverlayDrag(event) {
    if (!this.overlayDrag) return;
    if (event && this.overlayDrag.pointerId !== event.pointerId) return;
    if (event) {
      try {
        this.dragHandleEl?.releasePointerCapture(event.pointerId);
      } catch {
      }
    }
    this.overlay?.classList.remove("is-dragging");
    this.overlayDrag = null;
  }

  bindDesktopOverlayDrag() {
    if (!this.overlay || !this.dragHandleEl) return;
    this.dragHandleEl.addEventListener("pointerdown", (event) => {
      if (!this.isDesktopOverlay() || event.button !== 0 || event.pointerType === "touch") return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("button,a,input,textarea,select,label,[role='button']")) return;
      const rect = this.overlay.getBoundingClientRect();
      this.overlayDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startLeft: rect.left,
        active: false,
      };
      try {
        this.dragHandleEl.setPointerCapture(event.pointerId);
      } catch {
      }
    });
    this.dragHandleEl.addEventListener("pointermove", (event) => {
      if (!this.overlayDrag || this.overlayDrag.pointerId !== event.pointerId || !this.isDesktopOverlay()) return;
      const deltaX = event.clientX - this.overlayDrag.startX;
      if (!this.overlayDrag.active && Math.abs(deltaX) < OVERLAY_DRAG_THRESHOLD) return;
      event.preventDefault();
      this.overlayDrag.active = true;
      this.overlay.classList.add("is-dragging");
      this.setOverlayDesktopLeft(this.overlayDrag.startLeft + deltaX);
    });
    this.dragHandleEl.addEventListener("pointerup", (event) => this.endOverlayDrag(event));
    this.dragHandleEl.addEventListener("pointercancel", (event) => this.endOverlayDrag(event));
    window.addEventListener("resize", () => this.syncOverlayDesktopPosition(), { passive: true });
  }

  canScrollInsideOverlay(target, deltaX, deltaY) {
    let node = target instanceof Element ? target : null;
    while (node && node !== this.overlay) {
      if (node instanceof HTMLElement) {
        const style = window.getComputedStyle(node);
        const overflowX = style.overflowX;
        const overflowY = style.overflowY;
        const scrollableX = (overflowX === "auto" || overflowX === "scroll") && node.scrollWidth > node.clientWidth + 1;
        const scrollableY = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight + 1;
        if (scrollableX && Math.abs(deltaX) > 0) {
          if (deltaX < 0 && node.scrollLeft > 0) return true;
          if (deltaX > 0 && node.scrollLeft + node.clientWidth < node.scrollWidth - 1) return true;
        }
        if (scrollableY && Math.abs(deltaY) > 0) {
          if (deltaY < 0 && node.scrollTop > 0) return true;
          if (deltaY > 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1) return true;
        }
      }
      node = node.parentElement;
    }
    return false;
  }

  bindOverlayScrollContainment() {
    if (!this.overlay) return;
    this.overlay.addEventListener("wheel", (event) => {
      if (!this.overlay?.classList.contains("is-open")) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !this.overlay.contains(target)) return;
      event.stopPropagation();
      const deltaX = event.deltaX || (event.shiftKey ? event.deltaY : 0);
      const deltaY = event.shiftKey ? 0 : event.deltaY;
      if (!this.canScrollInsideOverlay(target, deltaX, deltaY)) event.preventDefault();
    }, { passive: false });
  }

  bindFloatingMenu() {
    const floatingRoot = document.querySelector(".wr-floating-header");
    const toggle = document.querySelector(".wr-floating-menu-toggle");
    const dock = document.querySelector(".wr-floating-cta-dock");
    const group = document.querySelector(".wr-floating-cta-group");
    const heroSection =
      document.getElementById("section-hero") ||
      document.querySelector(".section-hero") ||
      document.querySelector(".hero-section");
    if (!floatingRoot || !toggle || !group) return;
    if (floatingRoot.parentElement !== document.body) document.body.appendChild(floatingRoot);
    if (dock && group.parentElement !== dock) dock.appendChild(group);
    floatingRoot.classList.add("is-animated", "has-floating-ctas");
    group.classList.add("is-animated");

    const heroSurfaceBuffer = 8;
    const compactDeviceQuery = window.matchMedia?.("(max-width: 991px)");
    let frame = 0;
    const updateSurface = () => {
      if (!heroSection) {
        floatingRoot.classList.remove("is-over-hero");
        return;
      }
      const floatingRect = floatingRoot.getBoundingClientRect();
      const heroRect = heroSection.getBoundingClientRect();
      const probeY = floatingRect.top + floatingRect.height / 2;
      floatingRoot.classList.toggle("is-over-hero", heroRect.bottom > probeY + heroSurfaceBuffer);
    };
    const scheduleSurfaceUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateSurface();
      });
    };
    const setExpandedState = (expanded) => {
      floatingRoot.classList.toggle("is-collapsed", !expanded);
      floatingRoot.classList.toggle("is-expanded", expanded);
      toggle.setAttribute("aria-expanded", String(expanded));
    };
    const tooltipItems = Array.from(floatingRoot.querySelectorAll(".wr-floating-header-item"));
    const clearTooltipSuppression = () => {
      tooltipItems.forEach((item) => item.classList.remove("is-tooltip-suppressed"));
    };
    tooltipItems.forEach((item) => {
      item.addEventListener("pointerleave", () => item.classList.remove("is-tooltip-suppressed"));
    });
    floatingRoot.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const item = target?.closest(".wr-floating-header-item");
      if (!item || !floatingRoot.contains(item)) return;
      clearTooltipSuppression();
      item.classList.add("is-tooltip-suppressed");
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && item.contains(activeElement)) activeElement.blur();
    }, true);
    const servicesDropdown = floatingRoot.querySelector(".wr-floating-nav-dropdown");
    toggle.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const expanded = floatingRoot.classList.contains("is-collapsed") || !floatingRoot.classList.contains("is-expanded");
      setExpandedState(expanded);
    });
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      if (!floatingRoot.classList.contains("is-expanded")) return;
      setExpandedState(false);
    });
    document.addEventListener("click", (event) => {
      if (!servicesDropdown?.open) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target && servicesDropdown.contains(target)) return;
      servicesDropdown.removeAttribute("open");
    });
    window.addEventListener("scroll", () => {
      scheduleSurfaceUpdate();
    }, { passive: true });
    window.addEventListener("resize", () => {
      if (dock && group.parentElement !== dock) dock.appendChild(group);
      scheduleSurfaceUpdate();
    }, { passive: true });
    setExpandedState(!compactDeviceQuery?.matches);
    updateSurface();
  }

  promptFromTrigger(trigger) {
    const lang = currentLanguage();
    const direct = cleanText(trigger.dataset.websiteNavigatorPromptDe || trigger.dataset.websiteNavigatorPromptEn);
    if (trigger.dataset.websiteNavigatorPromptDe || trigger.dataset.websiteNavigatorPromptEn) {
      return cleanText(lang === "en" ? trigger.dataset.websiteNavigatorPromptEn : trigger.dataset.websiteNavigatorPromptDe) || direct;
    }
    return cleanText(trigger.dataset.websiteNavigatorPrompt || trigger.dataset.websiteNavigatorSuggestionPrompt);
  }

  apiRoot() {
    const configured = cleanText(this.root.dataset.apiBase);
    let base = configured === "origin" || configured === "auto" ? "" : configured;
    if (!base) {
      try {
        if (typeof import.meta !== "undefined" && import.meta.env) {
          base = cleanText(import.meta.env.VITE_WEBSITE_NAVIGATOR_API_BASE);
        }
      } catch {
        base = "";
      }
    }
    if (!base) base = window.location.origin;
    return `${base.replace(/\/$/, "")}/api/website-navigator`;
  }

  apiOrigin() {
    try {
      return new URL(this.apiRoot()).origin;
    } catch {
      return window.location.origin;
    }
  }

  async fetchJson(path, options = {}) {
    const {
      debugArea = "",
      debugOperationId = "",
      debugEvent = "",
      actionId = "",
      ...fetchOptions
    } = options;
    const requestToken = this.latencyDebug?.requestStart?.(path, {
      ...fetchOptions,
      debugArea,
      operationId: debugOperationId,
      actionId,
    });
    let response = null;
    let responseText = "";
    try {
      response = await fetch(`${this.apiRoot()}${path}`, {
        ...fetchOptions,
        headers: {
          "Content-Type": "application/json",
          ...(fetchOptions.headers || {}),
          ...(this.latencyDebug?.requestHeaders?.(requestToken) || {}),
        },
      });
      this.debugEvent("api.fetch.headers_received", {
        area: debugArea || "api",
        phase: "api_client",
        operationId: debugOperationId,
        details: {
          path: path.split("?")[0],
          status: response.status,
        },
      });
      responseText = await response.text();
      this.debugEvent("api.fetch.body_text_read", {
        area: debugArea || "api",
        phase: "api_client",
        operationId: debugOperationId,
        details: {
          path: path.split("?")[0],
          response_bytes: new Blob([responseText]).size,
        },
      });
    } catch (error) {
      this.latencyDebug?.requestError?.(requestToken, error);
      throw error;
    }
    let data = null;
    const parseToken = this.debugStart("api.fetch.json_parse", { area: debugArea || "api", phase: "api_client", operationId: debugOperationId });
    const parseStartedAt = performance.now();
    let jsonParseMs = null;
    try {
      data = responseText ? JSON.parse(responseText) : null;
      jsonParseMs = performance.now() - parseStartedAt;
      this.debugEnd(parseToken);
    } catch {
      jsonParseMs = performance.now() - parseStartedAt;
      this.debugEnd(parseToken, { status: "error" });
      const error = new Error("navigator_connection_failed");
      error.detail = "navigator_connection_failed";
      error.status = response.status;
      this.latencyDebug?.requestEnd?.(requestToken, {
        status: response.status,
        ok: false,
        responseText,
        data: null,
        serverTiming: response.headers.get("server-timing"),
        requestId: response.headers.get("x-wnav-request-id"),
        actionId,
        jsonParseMs,
      });
      throw error;
    }
    this.latencyDebug?.requestEnd?.(requestToken, {
      status: response.status,
      ok: response.ok,
      responseText,
      data,
      serverTiming: response.headers.get("server-timing"),
      requestId: response.headers.get("x-wnav-request-id"),
      actionId,
      jsonParseMs,
    });
    if (debugEvent) {
      this.debugEvent(`${debugEvent}.api_end`, {
        area: debugArea || "api",
        phase: "api_client",
        operationId: debugOperationId,
        details: {
          path: path.split("?")[0],
          status: response.status,
        },
      });
    }
    if (!response.ok) {
      const error = new Error(cleanText(data?.detail) || "navigator_request_failed");
      error.detail = cleanText(data?.detail) || "navigator_request_failed";
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async ensureBootstrap() {
    if (this.ids) return this.ids;
    if (this.bootstrapping) return this.bootstrapping;
    const operationId = this.debugOperation("bootstrap");
    const token = this.debugStart("bootstrap", { area: "bootstrap", phase: "frontend", operationId });
    this.setStatus("busy");
    this.bootstrapping = this.fetchJson("/bootstrap", {
      method: "POST",
      debugArea: "bootstrap",
      debugOperationId: operationId,
      debugEvent: "bootstrap.request",
      body: JSON.stringify({
        origin: window.location.origin,
        language: currentLanguage(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        session_token: this.sessionToken || undefined,
        metadata: {
          ...this.siteMetadata(),
          referrer: document.referrer || "",
        },
      }),
    })
      .then(async (data) => {
        if (!data.ids?.journey_id || !data.ids?.session_id) {
          const error = new Error("navigator_connection_failed");
          error.detail = "navigator_connection_failed";
          throw error;
        }
        this.ids = data.ids;
        this.sessionToken = data.session_token;
        if (this.sessionToken) safeStorageSet(STORAGE_KEY, this.sessionToken);
        if (this.sessionHasNavigatorActivity) {
          const activityKey = this.navigatorActivityStorageKey();
          if (activityKey) safeStorageSet(activityKey, "1");
        }
        if (this.sessionHasNavigatorMessage) {
          const messageKey = this.navigatorMessageStorageKey();
          if (messageKey) safeStorageSet(messageKey, "1");
        }
        this.syncBottomBarPlaceholder();
        this.applyNavigatorModules(data.state_view?.navigator_modules);
        this.applyContactBootstrap(data.state_view || {});
        const bootstrapComponents = (data.components || []).filter((component) => cleanText(component.component_type) !== "navigator_shell");
        const internalConsentGate = this.consentGateFromComponents(bootstrapComponents);
        if (internalConsentGate && this.siteConsentGranted()) await this.syncNavigatorConsentFromSiteConsent(internalConsentGate);
        const renderableComponents = this.filterInternalConsentGate(bootstrapComponents);
        const consentRequired = this.hasConsentGate(renderableComponents);
        this.debugEvent("bootstrap.components_received", {
          area: "bootstrap",
          phase: "frontend",
          operationId,
          details: {
            session_reused: data.session_reused === true,
            components_count: renderableComponents.length,
            component_types: renderableComponents.map((component) => cleanText(component.component_type)).filter(Boolean),
            consent_required: consentRequired,
          },
        });
        if (renderableComponents.length) {
          this.lastComponents = renderableComponents;
          this.renderComponents(renderableComponents);
        } else {
          this.setConsentGateActive(false);
        }
        if (!consentRequired) this.flushPendingAfterConsent();
        this.setStatus("ready");
        this.debugEvent("bootstrap.stream.open_requested", { area: "bootstrap", phase: "frontend", operationId });
        this.openStream();
        this.debugEnd(token, { details: { session_reused: data.session_reused === true, components_count: renderableComponents.length } });
        return this.ids;
      })
      .catch((error) => {
        this.setStatus("error");
        this.setConsentGateActive(false);
        this.renderNavigatorShortcuts();
        this.renderBottomBarChips();
        this.showConnectionError(error);
        this.debugEnd(token, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
        throw error;
      })
      .finally(() => {
        this.bootstrapping = null;
      });
    return this.bootstrapping;
  }

  open(options = {}) {
    if (!this.requireSiteConsent()) return;
    if ((this.isRecording || this.isTranscribing) && this.activeVoiceSurface === VOICE_SURFACE_BOTTOM) this.resetVoiceState({ skipTranscribe: true });
    this.latencyDebug?.startOpen?.({ already_open: this.overlay?.classList.contains("is-open") === true });
    const operationId = this.debugOperation("navigator.open");
    const token = this.debugStart("navigator.open", { area: "navigator_core", phase: "frontend", operationId });
    const openingSequence = this.showNavigatorOpening();
    const openingDelay = new Promise((resolve) => window.setTimeout(resolve, NAVIGATOR_OPENING_MIN_MS));
    this.overlay?.classList.add("is-open");
    this.overlay?.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("website-navigator-overlay-open");
    document.body.classList.add("website-navigator-overlay-open");
    if (options.defaultOpen === true) this.showDefaultNavigatorView();
    this.debugEvent("navigator.open.overlay_class_applied", { area: "navigator_core", phase: "frontend", operationId });
    window.requestAnimationFrame(() => {
      this.debugEvent("navigator.open.shell_visible", { area: "navigator_core", phase: "frontend", operationId });
      window.requestAnimationFrame(() => {
        this.debugEvent("navigator.open.first_animation_frame", { area: "navigator_core", phase: "frontend", operationId });
        this.debugEvent("navigator.open.first_interactive", { area: "navigator_core", phase: "frontend", operationId });
        this.debugEnd(token);
      });
    });
    this.syncOverlayDesktopPosition();
    this.launchers.forEach((launcher) => launcher.setAttribute("aria-expanded", "true"));
    const bootstrapReady = this.ensureBootstrap()
      .then(() => {
        if (options.defaultOpen === true) this.showDefaultNavigatorView();
        if (!this.consentGateActive) window.setTimeout(() => this.inputEl?.focus(), 80);
      })
      .catch(() => { });
    Promise.allSettled([openingDelay, bootstrapReady]).then(() => this.hideNavigatorOpening(openingSequence));
  }

  close() {
    this.debugEvent("navigator.close", { area: "navigator_core", phase: "frontend" });
    this.closeFileMediaOverlay();
    this.endOverlayDrag();
    this.resetVoiceState({ skipTranscribe: true });
    this.navigatorOpeningSequence += 1;
    this.hideNavigatorOpening(this.navigatorOpeningSequence);
    this.overlay?.classList.remove("is-open");
    this.overlay?.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("website-navigator-overlay-open");
    document.body.classList.remove("website-navigator-overlay-open");
    this.launchers.forEach((launcher) => launcher.setAttribute("aria-expanded", "false"));
  }

  setStatus(status) {
    const label = labels();
    this.statusEl?.classList.remove("is-busy", "is-error");
    if (status === "busy") {
      this.statusEl?.classList.add("is-busy");
      this.setStatusText(label.busy);
      return;
    }
    if (status === "error") {
      this.statusEl?.classList.add("is-error");
      this.setStatusText(label.error);
      return;
    }
    this.setStatusText(label.ready);
  }

  setStatusText(text) {
    const target = this.statusEl?.querySelector(".website-navigator-status-text");
    if (target) target.textContent = text;
  }

  showChip(message, kind = "info") {
    if (!this.chipLayer) return;
    const chip = makeEl("div", `website-navigator-overlay-chip${kind === "error" ? " is-error" : ""}`, message);
    this.chipLayer.appendChild(chip);
    window.setTimeout(() => chip.remove(), 4000);
  }

  showConnectionError(error) {
    const label = labels();
    const detail = cleanText(error?.detail);
    const message = detail === "adapter_unavailable" ? label.unavailable : label.connectionFailed;
    if (this.chatHasContent) this.addMessage(message, "error");
    this.showChip(message, "error");
  }

  sendCurrentInput() {
    if (this.isTranscribing) return;
    if (this.isRecording) {
      if (this.activeVoiceSurface === VOICE_SURFACE_NAVIGATOR) this.sendAfterTranscribeSurface = VOICE_SURFACE_NAVIGATOR;
      this.stopRecording();
      return;
    }
    const value = cleanText(this.inputEl?.value);
    if (!value) return;
    if (!this.requireSiteConsent()) return;
    this.inputEl.value = "";
    this.resizeInput();
    const targetMode = this.prepareAssistanceQuestion();
    this.sendMessage(value, { targetMode });
  }

  async sendMessage(content, options = {}) {
    content = cleanText(content);
    if (!content) return;
    if (this.busy) return;
    this.markNavigatorSessionMessage();
    this.collapseInfoCard();
    this.busy = true;
    this.setStatus("busy");
    this.updateMicAvailability();
    const requestTargetMode = this.normalizeShortcutMode(options.targetMode) || this.normalizeShortcutMode(this.activeShortcutMode) || this.assistanceMode();
    if (options.showUserMessage !== false) this.addMessage(content, "user");
    let pendingToken = this.showPendingIndicator(this.messageLoadingText(requestTargetMode));
    try {
      await this.ensureBootstrap();
      const data = await this.fetchJson("/messages", {
        method: "POST",
        body: JSON.stringify({
          journey_id: this.ids.journey_id,
          session_id: this.ids.session_id,
          role: "user",
          content,
          trace_id: makeUuid(),
          metadata: {
            locale: currentLanguage(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            current_target_module_id: requestTargetMode,
            ...this.siteMetadata(),
            ...(options.metadata || {}),
          },
        }),
      });
      this.lastUserMessage = content;
      this.hidePendingIndicator(pendingToken);
      pendingToken = "";
      this.handleMessageResponse(data, { userContent: content, requestTargetMode });
      this.openStream();
      this.setStatus("ready");
    } catch (error) {
      this.hidePendingIndicator(pendingToken);
      pendingToken = "";
      this.setStatus("error");
      this.showConnectionError(error);
    } finally {
      this.hidePendingIndicator(pendingToken);
      this.busy = false;
      this.updateMicAvailability();
    }
  }

  handleMessageResponse(data, options = {}) {
    const hint = data?.stream_hint || {};
    const output = hint.module_output || {};
    const userContent = cleanText(options.userContent || this.lastUserMessage);
    const requestTargetMode = this.normalizeShortcutMode(options.requestTargetMode) || this.normalizeShortcutMode(this.activeShortcutMode) || this.assistanceMode();
    const assistanceMode = this.assistanceMode();
    const responseTargetMode = this.normalizeShortcutMode(hint.workflow?.target_module_id || hint.route?.target_module_id);
    const routedFromAssistance = Boolean(responseTargetMode && requestTargetMode === assistanceMode && responseTargetMode !== assistanceMode);
    this.lastModuleState = {
      ...(this.lastModuleState || {}),
      ...(output.state_patch || {}),
    };
    if (hint.status === "no_match") {
      if (requestTargetMode === assistanceMode) this.assistanceRouteCardComponents = [];
      const message = labels().noMatch;
      this.addMessage(message, "system", {
        actions: [{
          label: labels().contactTeam,
          className: "website-navigator-action is-primary",
          onClick: () => this.openContactMode({ prefillSummary: userContent }),
        }],
      });
      this.showChip(message);
      this.renderComponents([]);
      return;
    }
    const outputComponents = Array.isArray(output.components) ? output.components : [];
    const hasAnswerOutput = Boolean(cleanText(output.assistant_text)) || Boolean(this.answerComponentFromComponents(outputComponents));
    if (routedFromAssistance && !hasAnswerOutput) this.setAssistanceRouteCard(responseTargetMode);
    else if (requestTargetMode === assistanceMode) this.assistanceRouteCardComponents = [];
    if (this.shouldOpenBookingSetupFromResponse(hint, outputComponents)) {
      this.openBookingMode();
      return;
    }
    if (outputComponents.length && this.renderAnswerComponentsInChat(outputComponents, { userContent })) {
      const sidecarComponents = this.componentsAfterChatAnswer(outputComponents);
      this.lastComponents = sidecarComponents;
      this.renderComponents(sidecarComponents);
      if (responseTargetMode) {
        this.markShortcutModeUsed(responseTargetMode);
        this.setActiveShortcutMode(responseTargetMode);
      }
      return;
    }
    if (output.assistant_text && !outputComponents.length) {
      this.addMessage(output.assistant_text, "bot");
    }
    if (Array.isArray(output.components)) {
      this.lastComponents = outputComponents;
      this.renderComponents(outputComponents);
      if (responseTargetMode) {
        this.markShortcutModeUsed(responseTargetMode);
        this.setActiveShortcutMode(responseTargetMode);
      }
      return;
    }
    if (responseTargetMode) {
      this.markShortcutModeUsed(responseTargetMode);
      this.setActiveShortcutMode(responseTargetMode);
    }
  }

  answerComponentFromComponents(components) {
    return (components || []).find((component) => {
      const type = cleanText(component?.component_type);
      return type === "answer_card" || type === "grounded_answer";
    }) || null;
  }

  componentsAfterChatAnswer(components) {
    return (components || []).filter((component) => {
      const type = cleanText(component?.component_type);
      return type !== "answer_card" && type !== "grounded_answer" && type !== "file_cards";
    });
  }

  chatActionsFromComponentActions(actions, fallbackSummary = "") {
    if (!Array.isArray(actions)) return [];
    return actions.map((action) => {
      const actionKey = cleanText(action?.action || action?.action_key || action?.value);
      const behavior = cleanText(action?.frontend_behavior) || this.behaviorForAction(actionKey);
      if (behavior !== "open_contact") return null;
      const prefillSummary = cleanText(action?.prefill_summary || action?.message || fallbackSummary);
      return {
        label: textFor(action?.label) || this.labelForAction(actionKey) || labels().contactTeam,
        className: "website-navigator-action is-primary",
        onClick: () => this.openContactMode({ prefillSummary }),
      };
    }).filter(Boolean);
  }

  renderAnswerComponentsInChat(components, options = {}) {
    const component = this.answerComponentFromComponents(components);
    const props = component?.props || {};
    const answer = cleanText(props.answer) || labels().noMatch;
    if (!component || !answer) return false;
    const card = makeEl("article", "website-navigator-chat-answer-card");
    appendText(card, "h4", "website-navigator-chat-answer-title", navigatorText("flow_titles", "answer"));
    appendText(card, "p", "website-navigator-chat-answer-text", answer);
    const fileCardNodes = this.renderChatFileCardComponents(components);
    fileCardNodes.forEach((node) => card.appendChild(node));
    const fallbackSummary = cleanText(props.prefill_summary || props.question || options.userContent || this.lastUserMessage);
    const actions = this.chatActionsFromComponentActions(props.actions, fallbackSummary);
    this.addMessage("", "bot", { node: card, actions });
    return true;
  }

  renderChatFileCardComponents(components) {
    return (components || []).map((component) => {
      if (cleanText(component?.component_type) !== "file_cards") return null;
      const files = Array.isArray(component?.props?.files) ? component.props.files : [];
      const groups = this.fileCardGroups(files);
      if (!groups.length) return null;
      const wrapper = makeEl("div", "website-navigator-chat-file-cards");
      groups.forEach((group) => wrapper.appendChild(group));
      return wrapper;
    }).filter(Boolean);
  }

  addMessage(content, role = "bot", options = {}) {
    const node = options.node instanceof HTMLElement ? options.node : null;
    if (!this.messagesEl || (!content && !node)) return;
    this.chatHasContent = true;
    this.setChatVisible(true);
    const row = makeEl("div", `website-navigator-message-row${role === "user" ? " is-user" : ""}`);
    const extraClass = cleanText(options.className);
    const bubble = makeEl("div", `website-navigator-message is-${role}${extraClass ? ` ${extraClass}` : ""}`);
    if (node) bubble.appendChild(node);
    else appendText(bubble, "p", "mb-0", content);
    const actions = Array.isArray(options.actions) ? options.actions : [];
    if (actions.length) {
      const actionRow = makeEl("div", "website-navigator-action-row");
      actions.forEach((action) => {
        const label = cleanText(action?.label);
        if (!label) return;
        const button = makeEl("button", cleanText(action.className) || "website-navigator-action", label);
        button.type = "button";
        if (typeof action.onClick === "function") button.addEventListener("click", action.onClick);
        actionRow.appendChild(button);
      });
      if (actionRow.children.length) bubble.appendChild(actionRow);
    }
    row.appendChild(bubble);
    this.messagesEl.appendChild(row);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  async refreshQuestionSuggestions(reason = "manual") {
    const metadata = this.siteMetadata();
    const key = [metadata.page_key, metadata.section_key, currentLanguage()].join(":");
    if (key === this.questionSuggestionsKey && this.questionSuggestions?.status === "ready") return;
    this.questionSuggestionsKey = key;
    const params = new URLSearchParams({
      page_key: metadata.page_key,
      max_count: String(WEBSITE_NAVIGATOR_SUGGESTIONS.visibleCount || 7),
    });
    if (metadata.section_key) params.set("section_key", metadata.section_key);
    try {
      const data = await this.fetchJson(`/questions/suggestions?${params.toString()}`, {
        debugArea: "suggestions",
        debugEvent: "question_suggestions.request",
      });
      if (Array.isArray(data?.actions)) this.questionActionOptions = data.actions;
      if (data?.service_finder_intake && typeof data.service_finder_intake === "object") this.serviceFinderIntake = data.service_finder_intake;
      if (data?.status === "ready" && Array.isArray(data.items)) {
        this.questionSuggestions = data;
        this.renderSuggestions("knowledge");
        this.renderBottomBarChips();
      }
    } catch (error) {
      this.debugEvent("question_suggestions.request.failed", {
        area: "suggestions",
        phase: "frontend",
        details: { reason, detail: cleanText(error?.message || error?.detail) },
      });
    }
  }

  suggestionPool(staticMode = "knowledge") {
    const dynamicItems = Array.isArray(this.questionSuggestions?.items) ? this.questionSuggestions.items : [];
    if (dynamicItems.length) return dynamicItems;
    const pageKey = document.querySelector('meta[name="page-context"]')?.content || "";
    const context = WEBSITE_NAVIGATOR_SUGGESTIONS.contexts?.[pageKey];
    const selectedMode = cleanText(staticMode) || "knowledge";
    const source = context?.modes?.[selectedMode] || WEBSITE_NAVIGATOR_SUGGESTIONS.defaults.modes[selectedMode] || WEBSITE_NAVIGATOR_SUGGESTIONS.defaults.modes.knowledge;
    return Array.isArray(source?.pool) ? source.pool : [];
  }

  bottomQuestionItems() {
    const metadata = this.siteMetadata();
    const cacheKey = [
      metadata.page_key,
      metadata.section_key,
      currentLanguage(),
      cleanText(this.questionSuggestions?.snapshot_hash || this.questionSuggestionsKey || "static"),
    ].join(":");
    if (cacheKey === this.bottomQuestionItemsKey && Array.isArray(this.bottomQuestionItemsCache)) return this.bottomQuestionItemsCache;
    const items = this.suggestionPool("knowledge")
      .map((item) => {
        const label = cleanText(textFor(item.label_i18n || item.label || item.label_en || item));
        const prompt = cleanText(textFor(item.prompt_i18n || item.prompt_en || item.prompt || label));
        return { label: label || prompt, prompt };
      })
      .filter((item) => item.label && item.prompt);
    for (let index = items.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
    }
    const selected = items.slice(0, 5);
    this.bottomQuestionItemsKey = cacheKey;
    this.bottomQuestionItemsCache = selected;
    return selected;
  }

  bottomShortcutModules() {
    const assistanceMode = this.targetModeForBehavior("assistance", "assistance");
    const runtimeModules = Array.isArray(this.navigatorModules?.shortcut_modules) ? this.navigatorModules.shortcut_modules : [];
    const modules = runtimeModules.length ? runtimeModules : PRESET_NAVIGATOR_SHORTCUT_MODULES;
    return modules
      .filter((module) => shortcutModuleEnabledForUi(module))
      .filter((module) => {
        const moduleId = this.normalizeShortcutMode(module?.module_id);
        return Boolean(moduleId && moduleId !== assistanceMode);
      })
      .slice(0, 4);
  }

  bottomChipRenderSignature(shortcutModules, questionItems, floatingItems) {
    return [
      currentLanguage(),
      ...floatingItems.map((item) => ["f", cleanText(item.kind), cleanText(item.key), cleanText(item.label), cleanText(item.prompt), cleanText(item.action)].join("\u001f")),
      ...shortcutModules.map((module) => {
        const shortcut = module.shortcut && typeof module.shortcut === "object" ? module.shortcut : {};
        return ["s", cleanText(module?.module_id), cleanText(module?.open_behavior), cleanText(textFor(shortcut.label_i18n)) || cleanText(shortcut.label) || cleanText(module.name), cleanText(shortcut.icon)].join("\u001f");
      }),
      ...questionItems.map((item) => ["q", cleanText(item.label), cleanText(item.prompt)].join("\u001f")),
    ].join("\u001e");
  }

  bottomChipElement(key) {
    if (!this.bottomChipRail) return null;
    return Array.from(this.bottomChipRail.querySelectorAll("[data-website-navigator-bottom-chip-key]")).find((element) => cleanText(element.dataset.websiteNavigatorBottomChipKey) === key) || null;
  }

  makeBottomSiteAssistChip(item) {
    const label = cleanText(item.label);
    const isQuestion = item.kind === "question" && item.question;
    const chip = makeEl(isQuestion ? "button" : "span", `website-navigator-suggestion-chip website-navigator-site-assist-chip website-navigator-site-assist-chip--${isQuestion ? "question" : "hint"}`);
    chip.dataset.websiteNavigatorBottomChipKey = `f:${cleanText(item.kind)}:${cleanText(item.key)}`;
    chip.appendChild(makeSiteAssistIcon(isQuestion ? "question" : "hint", "website-navigator-site-assist-icon"));
    chip.appendChild(makeEl("span", "website-navigator-site-assist-label", label));
    if (isQuestion) {
      chip.type = "button";
      chip.dataset.websiteNavigatorSiteAssistQuestion = "true";
      this.applySiteAssistQuestionDataset(chip, item.question, label);
    } else {
      chip.setAttribute("role", "note");
    }
    return chip;
  }

  makeBottomShortcutChip(module) {
    const moduleId = cleanText(module?.module_id);
    if (!moduleId) return null;
    const shortcut = module.shortcut && typeof module.shortcut === "object" ? module.shortcut : {};
    const label = cleanText(textFor(shortcut.label_i18n)) || cleanText(shortcut.label) || cleanText(module.name) || moduleId;
    const description = cleanText(textFor(shortcut.description_i18n)) || cleanText(shortcut.description);
    const button = makeEl("button", this.shortcutChipClass(module));
    button.type = "button";
    button.dataset.websiteNavigatorBottomChipKey = `s:${moduleId}`;
    button.dataset.websiteNavigatorMode = moduleId;
    button.dataset.websiteNavigatorShortcutMode = moduleId;
    button.dataset.websiteNavigatorBottomMode = moduleId;
    if (description) button.title = description;
    button.textContent = label;
    return button;
  }

  makeBottomQuestionChip(item, index) {
    const button = makeEl("button", "website-navigator-suggestion-chip website-navigator-bottom-question-chip");
    button.type = "button";
    button.dataset.websiteNavigatorBottomChipKey = `q:${index}:${cleanText(item.label)}:${cleanText(item.prompt)}`;
    button.dataset.websiteNavigatorBottomQuestion = "true";
    button.dataset.websiteNavigatorPrompt = item.prompt;
    button.dataset.websiteNavigatorSuggestionPrompt = item.prompt;
    button.textContent = item.label;
    return button;
  }

  bottomChipDescriptors(shortcutModules, questionItems, floatingItems) {
    return [
      ...floatingItems.map((item) => ({
        key: `f:${cleanText(item.kind)}:${cleanText(item.key)}`,
        create: () => this.makeBottomSiteAssistChip(item),
      })),
      ...shortcutModules.map((module) => ({
        key: `s:${cleanText(module?.module_id)}`,
        create: () => this.makeBottomShortcutChip(module),
      })),
      ...questionItems.map((item, index) => ({
        key: `q:${index}:${cleanText(item.label)}:${cleanText(item.prompt)}`,
        create: () => this.makeBottomQuestionChip(item, index),
      })),
    ].filter((descriptor) => cleanText(descriptor.key) && !descriptor.key.endsWith(":"));
  }

  placeBottomChip(element, previous) {
    if (!this.bottomChipRail || !element) return;
    const reference = previous ? previous.nextSibling : this.bottomChipRail.firstChild;
    if (element !== reference) this.bottomChipRail.insertBefore(element, reference);
  }

  animateBottomChipIn(element) {
    if (!(element instanceof HTMLElement) || !element.classList.contains("website-navigator-site-assist-chip")) return;
    element.classList.add("is-entering");
    window.requestAnimationFrame(() => element.classList.remove("is-entering"));
  }

  bottomChipLayoutRects() {
    if (!this.bottomChipRail) return new Map();
    return new Map(Array.from(this.bottomChipRail.querySelectorAll("[data-website-navigator-bottom-chip-key]")).map((element) => [cleanText(element.dataset.websiteNavigatorBottomChipKey), element.getBoundingClientRect()]));
  }

  animateBottomChipLayoutFrom(rects) {
    if (!this.bottomChipRail || !(rects instanceof Map)) return;
    Array.from(this.bottomChipRail.querySelectorAll("[data-website-navigator-bottom-chip-key]")).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const rect = rects.get(cleanText(element.dataset.websiteNavigatorBottomChipKey));
      if (!rect) return;
      const nextRect = element.getBoundingClientRect();
      const deltaX = rect.left - nextRect.left;
      const deltaY = rect.top - nextRect.top;
      if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;
      element.animate([
        { transform: `translate3d(${deltaX}px, ${deltaY}px, 0)` },
        { transform: "translate3d(0, 0, 0)" },
      ], { duration: 320, easing: "cubic-bezier(0.2, 1.35, 0.36, 1)" });
    });
  }

  animateBottomChipOut(element) {
    if (!(element instanceof HTMLElement)) {
      element?.remove?.();
      return;
    }
    if (!element.classList.contains("website-navigator-site-assist-chip")) {
      element.remove();
      return;
    }
    const token = `${Date.now()}:${Math.random()}`;
    element.dataset.websiteNavigatorChipLeaving = token;
    element.classList.add("is-leaving");
    this.syncBottomBarFloatingState();
    window.setTimeout(() => {
      if (element.dataset.websiteNavigatorChipLeaving !== token) return;
      const rects = this.bottomChipLayoutRects();
      element.remove();
      this.syncBottomBarFloatingState();
      this.animateBottomChipLayoutFrom(rects);
    }, 320);
  }

  renderBottomBarChips() {
    if (!this.bottomChipRail) return;
    const shortcutModules = this.bottomShortcutModules();
    const questionItems = this.bottomQuestionItems();
    const floatingItems = Array.isArray(this.siteAssistFloatingItems) ? this.siteAssistFloatingItems : [];
    const signature = this.bottomChipRenderSignature(shortcutModules, questionItems, floatingItems);
    if (signature === this.bottomChipSignature) {
      this.syncBottomBarFloatingState();
      this.syncShortcutChipStates(this.activeShortcutMode);
      return;
    }
    this.bottomChipSignature = signature;
    const rects = this.bottomChipLayoutRects();
    const descriptors = this.bottomChipDescriptors(shortcutModules, questionItems, floatingItems);
    const desiredKeys = new Set(descriptors.map((descriptor) => descriptor.key));
    let previous = null;
    descriptors.forEach((descriptor) => {
      let element = this.bottomChipElement(descriptor.key);
      const isNew = !element;
      if (!element) element = descriptor.create();
      if (!element) return;
      element.dataset.websiteNavigatorBottomChipKey = descriptor.key;
      delete element.dataset.websiteNavigatorChipLeaving;
      element.classList.remove("is-leaving");
      this.placeBottomChip(element, previous);
      if (isNew) this.animateBottomChipIn(element);
      previous = element;
    });
    Array.from(this.bottomChipRail.querySelectorAll("[data-website-navigator-bottom-chip-key]")).forEach((element) => {
      const key = cleanText(element.dataset.websiteNavigatorBottomChipKey);
      if (desiredKeys.has(key)) return;
      this.animateBottomChipOut(element);
    });
    this.syncBottomBarFloatingState();
    this.syncShortcutChipStates(this.activeShortcutMode);
    this.animateBottomChipLayoutFrom(rects);
  }

  renderSuggestions(mode = "knowledge", options = {}) {
    const host = this.root.querySelector("[data-website-navigator-suggestions]");
    if (!host) return;
    if (options.collapseInfo === true) this.collapseInfoCard();
    const staticMode = cleanText(options.staticMode) || "knowledge";
    const items = this.suggestionPool(staticMode).slice(0, WEBSITE_NAVIGATOR_SUGGESTIONS.visibleCount || 7);
    host.innerHTML = "";
    items.forEach((item) => {
      const button = makeEl("button", "website-navigator-suggestion-chip");
      button.type = "button";
      const label = cleanText(textFor(item.label_i18n || item.label || item.label_en || item));
      const prompt = cleanText(textFor(item.prompt_i18n || item.prompt_en || item.prompt || label));
      const action = cleanText(item.action);
      if (action) {
        button.dataset.websiteNavigatorQuestionAction = action;
        button.dataset.websiteNavigatorQuestionBehavior = cleanText(item.frontend_behavior) || this.behaviorForAction(action);
        button.dataset.websiteNavigatorQuestionKey = cleanText(item.question_key);
        button.dataset.websiteNavigatorQuestionSource = cleanText(item.source);
        button.dataset.websiteNavigatorQuestionSnapshotId = cleanText(item.snapshot_id);
        button.dataset.websiteNavigatorQuestionSnapshotHash = cleanText(item.snapshot_hash || this.questionSuggestions?.snapshot_hash);
        button.dataset.websiteNavigatorQuestionLabel = label;
        button.dataset.websiteNavigatorQuestionScope = cleanText(item.scope_type);
        button.dataset.websiteNavigatorQuestionScopeKey = cleanText(item.scope_key);
      } else {
        button.dataset.websiteNavigatorSuggestionPrompt = prompt;
      }
      button.dataset.websiteNavigatorPrompt = prompt;
      button.textContent = label || prompt;
      host.appendChild(button);
    });
  }

  targetModeForBehavior(behavior, fallback = "") {
    const behaviorKey = cleanText(behavior);
    const match = (this.navigatorModules.target_modules || []).find((module) => cleanText(module?.open_behavior) === behaviorKey);
    return cleanText(match?.module_id) || this.normalizeShortcutMode(fallback || behaviorKey);
  }

  assistanceMode() {
    return this.targetModeForBehavior("assistance", "assistance");
  }

  prepareAssistanceQuestion() {
    const targetMode = this.assistanceMode();
    this.assistanceRouteCardComponents = [];
    this.lastComponents = [];
    this.clearPendingIndicators();
    this.renderComponents([]);
    this.markShortcutModeUsed(targetMode);
    this.setActiveShortcutMode(targetMode);
    return targetMode;
  }

  setAssistanceRouteCard(mode) {
    const targetMode = this.normalizeShortcutMode(mode);
    const assistanceMode = this.assistanceMode();
    this.assistanceRouteCardComponents = [];
    if (targetMode && targetMode !== assistanceMode) this.addRouteNoticeMessage(targetMode);
  }

  addRouteNoticeMessage(mode) {
    const targetMode = this.normalizeShortcutMode(mode);
    if (!targetMode) return;
    const notice = makeEl("div", "website-navigator-route-notice");
    notice.appendChild(makeDataSearchIcon("website-navigator-route-notice-icon"));
    const copy = makeEl("span", "website-navigator-route-notice-copy");
    appendText(copy, "span", "website-navigator-route-notice-kicker", navigatorText("labels", "modeOpened"));
    copy.appendChild(document.createTextNode(" "));
    appendText(copy, "span", "website-navigator-route-notice-target", this.moduleDisplayName(targetMode));
    notice.appendChild(copy);
    this.addMessage("", "bot", { node: notice, className: "is-route-notice" });
  }

  bookingMode() {
    return this.targetModeForBehavior(BOOKING_BEHAVIOR, BOOKING_TARGET_ID);
  }

  hasBookingComponent(components) {
    return (components || []).some((component) => {
      const key = cleanText(component?.component_key);
      const type = cleanText(component?.component_type);
      return BOOKING_COMPONENT_KEYS.has(key) || BOOKING_COMPONENT_TYPES.has(type);
    });
  }

  shouldOpenBookingSetupFromResponse(hint, components) {
    const workflowId = cleanText(hint?.workflow?.workflow_id || hint?.route?.workflow_id);
    return workflowId === BOOKING_WORKFLOW_ID && !this.hasBookingComponent(components);
  }

  bookingSetupComponents() {
    return [
      {
        component_key: BOOKING_CALENDAR_COMPONENT_KEY,
        component_type: BOOKING_CALENDAR_COMPONENT_TYPE,
        props: {},
      },
      {
        component_key: BOOKING_CONTACT_COMPONENT_KEY,
        component_type: BOOKING_CONTACT_COMPONENT_TYPE,
        props: {},
      },
    ];
  }

  ensureFlowPanelActive() {
    if (!this.flowSection || this.flowSection.hidden || !this.componentsEl?.children.length) return;
    this.markPanelUsed("flow");
    this.activatePanel("flow", { markUsed: false });
  }

  scheduleBookingFlowActivation() {
    this.ensureFlowPanelActive();
    window.requestAnimationFrame(() => this.ensureFlowPanelActive());
    window.setTimeout(() => this.ensureFlowPanelActive(), 120);
  }

  modeDescriptionForTarget(mode, fallbackKey = "") {
    const targetMode = this.normalizeShortcutMode(mode);
    const module = this.navigatorModuleById.get(targetMode);
    const shortcut = module?.shortcut && typeof module.shortcut === "object" ? module.shortcut : {};
    return cleanText(textFor(shortcut.description_i18n)) || cleanText(shortcut.description) || modeDescription(fallbackKey);
  }

  openMode(mode) {
    const normalizedMode = this.normalizeShortcutMode(mode);
    const module = this.navigatorModuleById.get(normalizedMode);
    const behavior = cleanText(module?.open_behavior || normalizedMode);
    const operationId = this.debugOperation(`mode.open.${normalizedMode || "default"}`);
    const token = this.debugStart("navigator.mode.open", { area: normalizedMode || "navigator_core", phase: "frontend", operationId, details: { mode: normalizedMode, behavior } });
    this.setActiveShortcutMode(normalizedMode);
    const methodName = `open${behavior.split(/[_-]+/).filter(Boolean).map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`).join("")}Mode`;
    const handler = this[methodName];
    if (typeof handler === "function") {
      handler.call(this);
      this.debugEnd(token, { details: { mode: normalizedMode } });
      return;
    }
    const opening = this.showModuleOpening(normalizedMode);
    this.finishModuleOpening(opening, () => {
      this.renderSuggestions(normalizedMode || "knowledge", { collapseInfo: true });
      this.debugEnd(token, { details: { mode: normalizedMode || "knowledge" } });
    });
  }

  openAssistanceMode() {
    const mode = this.targetModeForBehavior("assistance", "assistance");
    const token = this.debugStart("assistance.open", { area: mode || "assistance", phase: "frontend" });
    this.markNavigatorSessionActivity();
    this.markShortcutModeUsed(mode);
    this.setActiveShortcutMode(mode);
    this.collapseInfoCard();
    const opening = this.showModuleOpening(mode);
    this.finishModuleOpening(opening, () => {
      this.renderSuggestions("knowledge");
      const routeComponents = Array.isArray(this.assistanceRouteCardComponents) ? this.assistanceRouteCardComponents : [];
      this.lastComponents = routeComponents;
      if (routeComponents.length) this.setChatVisible(true);
      this.renderComponents(routeComponents);
      this.setActiveShortcutMode(mode);
      if (!routeComponents.length) this.setChatVisible(true);
      this.inputEl?.focus({ preventScroll: true });
      this.debugEnd(token);
    });
  }

  openServiceMode() {
    const mode = this.targetModeForBehavior("service", "find_right_service");
    const token = this.debugStart("service.open", { area: mode || "service", phase: "frontend" });
    this.markNavigatorSessionActivity();
    this.markShortcutModeUsed(mode);
    this.setActiveShortcutMode(mode);
    this.collapseInfoCard();
    this.renderSuggestions("knowledge");
    const opening = this.showModuleOpening(mode);
    this.finishModuleOpening(opening, () => {
      const intake = this.serviceFinderIntake || {};
      const examples = listFor(intake.examples);
      const components = [
        {
          component_key: "service_finder_intake",
          component_type: "service_finder_intake",
          props: {
            title: textFor(intake.title),
            prompt: textFor(intake.prompt),
            placeholder: textFor(intake.placeholder),
            submit_label: textFor(intake.submit_label),
            min_prompt_chars: Number(intake.min_prompt_chars),
            examples_label: textFor(intake.examples_label),
            examples,
            variant: "initial",
          },
        },
      ];
      this.lastComponents = components;
      this.renderComponents(components);
      this.debugEnd(token, { details: { components_count: components.length } });
    });
  }

  openBookingMode(questionPayload = {}) {
    const mode = this.bookingMode();
    const token = this.debugStart("booking.open", { area: mode || "booking", phase: "frontend" });
    this.markNavigatorSessionActivity();
    this.markShortcutModeUsed(mode);
    this.setActiveShortcutMode(mode);
    this.collapseInfoCard();
    this.renderSuggestions("knowledge");
    const opening = this.showModuleOpening(mode);
    this.finishModuleOpening(opening, () => {
      const components = this.bookingSetupComponents();
      this.lastComponents = components;
      this.renderComponents(components);
      this.debugEnd(token, { details: { components_count: components.length } });
    });
  }

  openWebsiteCheckMode() {
    const mode = this.targetModeForBehavior("website_check", "website_check");
    const token = this.debugStart("website_check.open", { area: mode || "website_check", phase: "frontend" });
    this.markNavigatorSessionActivity();
    this.markShortcutModeUsed(mode);
    this.setActiveShortcutMode(mode);
    this.collapseInfoCard();
    this.renderSuggestions("index");
    const opening = this.showModuleOpening(mode);
    this.lastModuleState = {
      ...(this.lastModuleState || {}),
      website_check_status: "ready",
      requires_consent: true,
    };
    this.websiteCheckForceStart = false;
    this.finishModuleOpening(opening, () => {
      this.renderWebsiteCheckFlow();
      this.debugEnd(token);
    });
  }

  openContactMode(options = {}) {
    const mode = this.targetModeForBehavior("contact", "contact");
    const token = this.debugStart("contact.open", { area: mode || "contact", phase: "frontend" });
    this.markNavigatorSessionActivity();
    this.markShortcutModeUsed(mode);
    this.setActiveShortcutMode(mode);
    this.collapseInfoCard();
    this.renderSuggestions("contact");
    const submitted = this.hasSubmittedInquiry();
    const prefillSummary = cleanText(options.prefillSummary);
    this.lastModuleState = {
      ...(this.lastModuleState || {}),
      inquiry_status: submitted ? "submitted" : "ready",
      contact_prefill_message: prefillSummary || cleanText(this.lastModuleState?.contact_prefill_message),
      next_steps: this.lastModuleState?.next_steps || [],
      service_ids: this.lastModuleState?.service_ids || [],
      lead_score: this.lastModuleState?.lead_score || {},
    };
    const components = submitted
      ? this.contactConfirmationComponents()
      : [
        {
          component_key: "contact_microform",
          component_type: "contact_microform",
          props: {
            direct_open: true,
          },
        },
      ];
    const opening = this.showModuleOpening(mode);
    this.finishModuleOpening(opening, () => {
      this.lastComponents = components;
      this.renderComponents(components);
      this.debugEnd(token, { details: { submitted, components_count: components.length } });
    });
  }

  renderComponents(components) {
    if (!this.componentsEl) return;
    this.moduleOpeningSequence += 1;
    const sourceComponentList = Array.isArray(components) ? components : [];
    const hasServiceCards = sourceComponentList.some((component) => cleanText(component.component_type) === "service_cards");
    const componentList = hasServiceCards ? sourceComponentList.filter((component) => cleanText(component.component_type) !== "next_actions") : sourceComponentList;
    const hasIncomingComponents = componentList.length > 0;
    const token = this.debugStart("render.components", {
      area: "render",
      phase: "render",
      details: {
        component_count: componentList.length,
        component_types: componentList.map((component) => cleanText(component.component_type)).filter(Boolean),
        dom_nodes_before: this.componentsEl.querySelectorAll("*").length,
      },
    });
    const hasConsentGate = this.hasConsentGate(componentList);
    const componentMode = this.shortcutModeFromComponents(componentList);
    const hasBookingFlow = this.hasBookingComponent(componentList);
    if (componentMode) this.markShortcutModeUsed(componentMode);
    this.setConsentGateActive(hasConsentGate);
    this.setActiveShortcutMode(componentMode || (hasConsentGate ? this.pendingOpenMode || this.activeShortcutMode : hasIncomingComponents ? this.activeShortcutMode : ""));
    this.componentsEl.innerHTML = "";
    if (componentList.some((component) => cleanText(component.component_key) === BOOKING_CALENDAR_COMPONENT_KEY)) {
      this.lastBookingSetupComponents = componentList.filter((component) => BOOKING_SETUP_COMPONENT_KEYS.has(cleanText(component.component_key)));
    }
    componentList.forEach((component) => {
      const node = this.renderComponent(component);
      if (node) this.componentsEl.appendChild(node);
    });
    const hasComponents = Boolean(this.componentsEl.children.length);
    if (this.flowSection) this.flowSection.hidden = !hasComponents;
    if (hasComponents) {
      if (this.consentGateActive) {
        this.bodyEl?.classList.remove("is-focus-layout");
        this.root.classList.remove("has-focus-panel");
        if (this.flowSection) this.setSectionOpen(this.flowSection, true);
        this.syncInputPlacement();
        this.scheduleRailRows();
        this.debugEnd(token, { details: { has_consent_gate: true, dom_nodes_after: this.componentsEl.querySelectorAll("*").length } });
        return;
      }
      this.markPanelUsed("flow");
      this.activatePanel("flow", { markUsed: false });
      if (hasBookingFlow) this.scheduleBookingFlowActivation();
      this.debugEnd(token, { details: { has_components: true, dom_nodes_after: this.componentsEl.querySelectorAll("*").length } });
      return;
    }
    if (this.activePanelKey === "flow") this.activatePanel(this.chatHasContent ? "chat" : "suggestions");
    this.debugEnd(token, { details: { has_components: false, dom_nodes_after: this.componentsEl.querySelectorAll("*").length } });
  }

  renderComponent(component) {
    const type = cleanText(component.component_type);
    const props = component.props || {};
    const token = this.debugStart("render.component", { area: this.normalizeShortcutMode(type) || "render", phase: "render", details: { component_type: type, component_key: cleanText(component.component_key) } });
    try {
      let node = null;
      if (type === "service_cards") node = this.renderServiceCards(type, props);
      else if (type === "service_finder_intake") node = this.renderServiceFinderIntake(type, props);
      else if (type === "consent_gate") node = this.renderConsentGate(component.component_key || type, props);
      else if (type === "problem_cards") node = this.renderProblemCards(type, props);
      else if (type === "service_match_summary") node = this.renderSummary(type, props);
      else if (type === "lead_score") node = this.renderLeadScore(type, props);
      else if (type === "comparison_matrix") node = this.renderComparison(type, props);
      else if (type === "answer_card") node = this.renderAnswerCard(type, props);
      else if (type === "file_cards") node = this.renderFileCards(type, props);
      else if (type === "grounded_answer") node = this.renderGroundedAnswer(type, props);
      else if (type === "source_list") node = this.renderSources(type, props);
      else if (type === "booking_summary") node = this.renderBookingSummary(type, props);
      else if (type === "calendar") node = this.renderCalendar(type, props);
      else if (type === "contact_microform") node = this.renderContactForm(component.component_key || type, props);
      else if (type === "contact_confirmation") node = this.renderContactConfirmation(type, props);
      else if (type === "website_check_form") node = this.renderWebsiteCheckForm(type, props);
      else if (type === "website_check_progress") node = this.renderWebsiteCheckProgress(type, props);
      else if (type === "module_route_card") node = this.renderModuleRouteCard(type, props);
      else if (type === "inquiry_summary") node = this.renderInquirySummary(type, props);
      else if (type === "upload_dropzone") node = this.renderUpload(type, props);
      else if (type === "page_hint") node = this.renderPageHint(type, props);
      else if (type === "question_chips") node = this.renderQuestionChips(type, props);
      else if (type === "ambient_hint") node = this.renderAmbientHint(type, props);
      else if (type === "next_actions") node = this.renderNextActions(type, props);
      else node = this.renderGeneric(type, props);
      this.debugEnd(token, { details: { component_type: type, rendered: Boolean(node) } });
      return node;
    } catch (error) {
      this.debugEnd(token, { status: "error", details: { component_type: type, detail: cleanText(error?.message) } });
      throw error;
    }
  }

  moduleRouteComponent(mode) {
    return {
      component_key: "module_route_card",
      component_type: "module_route_card",
      props: {
        target_module_id: this.normalizeShortcutMode(mode),
      },
    };
  }

  renderModuleRouteCard(type, props) {
    const targetMode = this.normalizeShortcutMode(props.target_module_id);
    if (!targetMode) return null;
    const moduleName = this.moduleDisplayName(targetMode);
    const description = this.moduleDescription(targetMode);
    const shell = this.componentShell(navigatorText("flow_titles", "default"), type);
    const card = makeEl("article", "website-navigator-card website-navigator-route-card");
    card.appendChild(makeDataSearchIcon("website-navigator-route-card-icon"));
    const copy = makeEl("div", "website-navigator-route-card-copy");
    appendText(copy, "span", "website-navigator-route-card-kicker", navigatorText("labels", "modeOpened"));
    appendText(copy, "h4", "", moduleName);
    if (description) appendText(copy, "p", "", description);
    card.appendChild(copy);
    shell.appendChild(card);
    return shell;
  }

  bindSectionToggles() {
    this.root.querySelectorAll("[data-website-navigator-section]").forEach((section) => {
      const key = cleanText(section.dataset.websiteNavigatorSection);
      if (key && !this.sectionStates.has(key)) this.sectionStates.set(key, section.classList.contains("is-open"));
    });
  }

  setSectionOpen(section, isOpen) {
    section.classList.toggle("is-open", isOpen);
    const key = cleanText(section.dataset.websiteNavigatorSection);
    if (key) this.sectionStates.set(key, isOpen);
  }

  componentShell(title, sectionKey, options = {}) {
    const key = cleanText(sectionKey || title || "component");
    const isOpen = this.sectionStates.has(key) ? this.sectionStates.get(key) : true;
    const descriptionText = cleanText(options.description);
    const shell = makeEl("section", `website-navigator-component website-navigator-section${isOpen ? " is-open" : ""}`);
    shell.dataset.websiteNavigatorSection = key;
    if (descriptionText) appendText(shell, "p", "website-navigator-mode-description", descriptionText);
    return shell;
  }

  renderServiceCards(type, props) {
    const label = labels();
    const shell = this.componentShell(navigatorText("flow_titles", "service_cards"), type, { description: modeDescription("service_results") });
    const handoff = props.service_page_handoff && typeof props.service_page_handoff === "object" ? props.service_page_handoff : null;
    if (handoff) shell.appendChild(this.renderServicePageHandoffCard(handoff));
    const grid = makeEl("div", "website-navigator-card-grid");
    (props.services || []).forEach((service) => {
      const card = makeEl("article", "website-navigator-card website-navigator-service-card");
      appendText(card, "h4", "", textFor(service.title));
      appendText(card, "p", "", textFor(service.short_description));
      const pageUrl = cleanText(service.page_url);
      if (pageUrl) {
        const actions = makeEl("div", "website-navigator-service-card-actions");
        const link = makeEl("a", "website-navigator-card-link");
        link.href = pageUrl;
        link.target = pageUrl.startsWith("http") ? "_blank" : "_self";
        link.rel = "noreferrer";
        link.addEventListener("click", () => this.storeServicePageHandoff(service, pageUrl));
        link.append(makeExternalLinkIcon("website-navigator-card-link-icon"), makeEl("span", "", label.openServicePage));
        actions.appendChild(link);
        card.appendChild(actions);
      }
      grid.appendChild(card);
    });
    shell.appendChild(grid);
    return shell;
  }

  renderServicePageHandoffCard(handoff) {
    const card = makeEl("article", "website-navigator-card website-navigator-service-handoff-card");
    appendText(card, "p", "", this.servicePageAskText(cleanText(handoff.service_name)));
    return card;
  }

  renderServiceFinderIntake(type, props) {
    const label = labels();
    const shell = this.componentShell(cleanText(props.title) || navigatorText("flow_titles", "service_fit"), type, { description: cleanText(props.prompt) || this.modeDescriptionForTarget("find_right_service", "service") });
    const contextStatus = props.context_status && typeof props.context_status === "object" ? props.context_status : {};
    const isNeedsContext = cleanText(props.variant) === "needs_context" || contextStatus.has_enough_context === false;
    shell.classList.toggle("is-service-followup", isNeedsContext);
    const examples = Array.isArray(props.examples) ? props.examples.map((item) => cleanText(item)).filter(Boolean) : [];
    if (!isNeedsContext && examples.length) {
      const group = makeEl("div", "website-navigator-intake-examples");
      const examplesLabel = cleanText(props.examples_label);
      if (examplesLabel) appendText(group, "span", "website-navigator-intake-examples-label", examplesLabel);
      const list = makeEl("div", "website-navigator-intake-example-chips");
      examples.forEach((item, index) => {
        const button = makeEl("button", `website-navigator-action website-navigator-intake-example-chip is-service-example-${Math.min(index + 1, 3)}`, item);
        button.type = "button";
        button.addEventListener("click", () => this.sendMessage(item));
        list.appendChild(button);
      });
      group.appendChild(list);
      shell.appendChild(group);
    }
    const helper = cleanText(props.helper);
    if (isNeedsContext && helper) appendText(shell, "p", "website-navigator-intake-helper", helper);
    const textarea = makeEl("textarea", "website-navigator-intake-input");
    textarea.rows = 7;
    textarea.placeholder = isNeedsContext ? "" : cleanText(props.placeholder);
    shell.appendChild(textarea);
    const row = makeEl("div", "website-navigator-form-actions");
    const button = makeEl("button", "website-navigator-action is-service-finder-submit", cleanText(props.submit_label) || label.serviceFinderSubmit);
    button.type = "button";
    button.addEventListener("click", () => {
      const value = cleanText(textarea.value);
      const minChars = Number(props.min_prompt_chars);
      if (!Number.isFinite(minChars) || minChars <= 0) {
        this.showChip(label.unavailable, "error");
        return;
      }
      if (value.length < minChars) {
        this.showChip(label.addMoreContext, "error");
        textarea.focus();
        return;
      }
      this.sendMessage(value);
    });
    row.appendChild(button);
    shell.appendChild(row);
    return shell;
  }

  renderConsentGate(type, props) {
    const label = labels();
    const shell = this.componentShell(cleanText(props.title) || navigatorText("flow_titles", "consent"), type);
    shell.classList.add("website-navigator-consent-gate");
    const copy = makeEl("p", "website-navigator-intake-copy");
    const text = cleanText(props.text);
    if (text) copy.appendChild(document.createTextNode(`${text} `));
    const legal = makeEl("a", "website-navigator-inline-link", label.privacyLegal);
    legal.href = "/legal";
    legal.target = "_blank";
    legal.rel = "noopener noreferrer";
    copy.appendChild(legal);
    shell.appendChild(copy);
    const row = makeEl("div", "website-navigator-form-actions");
    const button = makeEl("button", "website-navigator-action", cleanText(props.button_label) || label.allow);
    button.type = "button";
    button.addEventListener("click", async () => {
      try {
        const output = await this.runAction(cleanText(props.action_id) || "navigator.consent.accept", "", {
          component_key: type,
          purpose: cleanText(props.purpose),
          text_version: cleanText(props.text_version),
        }, { button, idempotent: true });
        if (output.status === "consented") {
          this.showChip(label.navigatorEnabled);
          this.setConsentGateActive(false);
          this.renderComponents([]);
          if (this.pendingOpenMode || this.pendingPrefill) {
            this.flushPendingAfterConsent();
          } else {
            this.resetDefaultPanels();
            window.setTimeout(() => this.inputEl?.focus({ preventScroll: true }), 80);
          }
        }
      } catch {
      }
    });
    row.appendChild(button);
    shell.appendChild(row);
    return shell;
  }

  renderAnswerCard(type, props) {
    const shell = this.componentShell(navigatorText("flow_titles", "answer"), type, { description: this.modeDescriptionForTarget(this.activeShortcutMode, "") });
    appendText(shell, "p", "mb-0", cleanText(props.answer) || labels().noMatch);
    return shell;
  }

  renderFileCards(type, props) {
    const files = Array.isArray(props.files) ? props.files : [];
    if (!files.length) return null;
    const shell = this.componentShell(navigatorText("flow_titles", "files"), type);
    shell.classList.add("website-navigator-file-shell");
    const groups = this.fileCardGroups(files);
    groups.forEach((group) => shell.appendChild(group));
    if (!groups.length) return null;
    return shell;
  }

  fileCardGroups(files) {
    const documents = [];
    const media = [];
    files.forEach((file) => {
      if (this.isMediaFileCard(file)) media.push(file);
      else documents.push(file);
    });
    const groups = [];
    if (documents.length) groups.push(this.renderFileCardGroup(labels().fileDocumentsTitle || "Fitting Documents for more details about this:", documents, "documents"));
    if (media.length) groups.push(this.renderFileCardGroup(labels().fileMediaTitle || "Fitting Media pieces:", media, "media"));
    return groups;
  }

  renderFileCardGroup(title, files, variant) {
    const group = makeEl("section", `website-navigator-file-group is-${variant}`);
    appendText(group, "h4", "website-navigator-file-group-title", title);
    const rail = makeEl("div", "website-navigator-file-rail");
    files.forEach((file) => {
      rail.appendChild(variant === "media" ? this.renderMediaFileCard(file) : this.renderDocumentFileCard(file));
    });
    group.appendChild(rail);
    return group;
  }

  renderDocumentFileCard(file) {
    const label = labels();
    const card = makeEl("article", "website-navigator-card website-navigator-file-card is-document");
    card.appendChild(this.renderFileMetaRow(file));
    appendText(card, "h4", "", cleanText(file.title || file.display_filename || file.filename || label.fileDocumentFallback || "Document"));
    const description = cleanText(file.short_description);
    if (description) appendText(card, "p", "", description);
    return card;
  }

  renderMediaFileCard(file) {
    const label = labels();
    const card = makeEl("article", "website-navigator-card website-navigator-file-card website-navigator-media-card");
    const mediaKind = this.fileMediaKind(file);
    const mediaUrl = cleanText(file.open_url);
    const preview = makeEl("button", "website-navigator-media-preview");
    preview.type = "button";
    preview.setAttribute("aria-label", label.fileMediaOpenLabel || label.open || "Open media");
    if (!mediaUrl || !mediaKind) preview.disabled = true;
    if (mediaKind === "image" && mediaUrl) {
      const image = makeEl("img", "website-navigator-media-preview-image");
      image.src = mediaUrl;
      image.alt = cleanText(file.title || file.display_filename || file.filename || label.fileMediaFallback || "Media");
      image.loading = "lazy";
      preview.appendChild(image);
    } else if (mediaKind === "video" && mediaUrl) {
      const video = makeEl("video", "website-navigator-media-preview-video");
      video.src = mediaUrl;
      video.muted = true;
      video.playsInline = true;
      video.preload = "metadata";
      preview.appendChild(video);
    } else {
      appendText(preview, "span", "website-navigator-media-preview-placeholder", this.fileTypeLabel(file));
    }
    if (mediaUrl && mediaKind) preview.addEventListener("click", () => this.openFileMediaOverlay(file));
    card.appendChild(preview);
    card.appendChild(this.renderFileMetaRow(file));
    appendText(card, "h4", "", cleanText(file.title || file.display_filename || file.filename || label.fileMediaFallback || "Media"));
    const description = cleanText(file.short_description);
    if (description) appendText(card, "p", "", description);
    return card;
  }

  renderFileMetaRow(file) {
    const row = makeEl("div", "website-navigator-file-meta");
    appendText(row, "span", "website-navigator-file-kind", this.fileTypeLabel(file));
    const actions = this.renderFileIconActions(file);
    if (actions.children.length) row.appendChild(actions);
    return row;
  }

  renderFileIconActions(file) {
    const label = labels();
    const row = makeEl("div", "website-navigator-file-icon-actions");
    const openUrl = cleanText(file.open_url);
    if (openUrl) {
      const open = makeEl("a", "website-navigator-file-icon-action");
      open.href = openUrl;
      open.target = "_blank";
      open.rel = "noreferrer";
      open.setAttribute("aria-label", label.open);
      open.title = label.open;
      open.appendChild(makeExternalLinkIcon("website-navigator-file-action-icon"));
      row.appendChild(open);
    }
    const downloadUrl = cleanText(file.download_url || file.open_url);
    if (downloadUrl) {
      const download = makeEl("a", "website-navigator-file-icon-action");
      download.href = downloadUrl;
      download.setAttribute("download", "");
      download.setAttribute("aria-label", label.download);
      download.title = label.download;
      download.appendChild(makeDownloadIcon("website-navigator-file-action-icon"));
      row.appendChild(download);
    }
    return row;
  }

  isMediaFileCard(file) {
    return Boolean(this.fileMediaKind(file));
  }

  fileMediaKind(file) {
    const kind = cleanText(file?.kind || file?.file_kind).toLowerCase();
    const mime = cleanText(file?.mime_type || file?.media_type || file?.content_type).toLowerCase();
    if (kind === "image" || mime.startsWith("image/")) return "image";
    if (kind === "video" || mime.startsWith("video/")) return "video";
    return "";
  }

  fileTypeLabel(file) {
    const kind = cleanText(file?.kind || file?.file_kind).toUpperCase();
    if (kind) return kind;
    const mime = cleanText(file?.mime_type || file?.media_type || file?.content_type);
    if (mime) return mime.split("/").pop().toUpperCase();
    const name = cleanText(file?.display_filename || file?.filename || file?.title);
    const cleanName = name.split("?")[0].split("#")[0];
    const parts = cleanName.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
  }

  openFileMediaOverlay(file) {
    const mediaKind = this.fileMediaKind(file);
    const mediaUrl = cleanText(file?.open_url);
    if (!mediaKind || !mediaUrl) return;
    this.closeFileMediaOverlay();
    const label = labels();
    const overlay = makeEl("div", "website-navigator-file-media-overlay");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    const panel = makeEl("div", "website-navigator-file-media-dialog");
    const close = makeEl("button", "website-navigator-close-btn website-navigator-file-media-close");
    close.type = "button";
    close.setAttribute("aria-label", label.fileMediaCloseLabel || "Close media preview");
    close.appendChild(makeEl("span", "website-navigator-close-icon"));
    close.addEventListener("click", () => this.closeFileMediaOverlay());
    panel.appendChild(close);
    const stage = makeEl("div", "website-navigator-file-media-stage");
    if (mediaKind === "image") {
      const image = makeEl("img", "website-navigator-file-media-full");
      image.src = mediaUrl;
      image.alt = cleanText(file?.title || file?.display_filename || file?.filename || label.fileMediaFallback || "Media");
      stage.appendChild(image);
    } else {
      const video = makeEl("video", "website-navigator-file-media-full");
      video.src = mediaUrl;
      video.controls = true;
      video.playsInline = true;
      video.preload = "metadata";
      stage.appendChild(video);
    }
    panel.appendChild(stage);
    const caption = makeEl("div", "website-navigator-file-media-caption");
    appendText(caption, "h4", "", cleanText(file?.title || file?.display_filename || file?.filename || label.fileMediaFallback || "Media"));
    const description = cleanText(file?.short_description);
    if (description) appendText(caption, "p", "", description);
    panel.appendChild(caption);
    overlay.appendChild(panel);
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) this.closeFileMediaOverlay();
    });
    document.body.appendChild(overlay);
    this.fileMediaOverlay = overlay;
    window.requestAnimationFrame(() => overlay.classList.add("is-open"));
    close.focus({ preventScroll: true });
  }

  closeFileMediaOverlay() {
    if (!this.fileMediaOverlay) return false;
    const overlay = this.fileMediaOverlay;
    this.fileMediaOverlay = null;
    overlay.querySelectorAll("video").forEach((video) => video.pause());
    overlay.remove();
    return true;
  }

  renderProblemCards(type, props) {
    const cards = props.cards || [];
    if (!cards.length && cleanText(props.status) === "not_configured") return null;
    const shell = this.componentShell(labels().situationTitle, type);
    const grid = makeEl("div", "website-navigator-card-grid");
    cards.forEach((card) => {
      const node = makeEl("article", "website-navigator-card");
      appendText(node, "h4", "", textFor(card.title || card.label || card.problem_key));
      appendText(node, "p", "", textFor(card.description || card.summary || ""));
      grid.appendChild(node);
    });
    if (grid.children.length) shell.appendChild(grid);
    if (!grid.children.length) appendText(shell, "p", "mb-0", this.statusCopy(props.status || "idle"));
    return shell;
  }

  renderLeadScore(type, props) {
    if (cleanText(props.status) === "not_configured") return null;
    const shell = this.componentShell(labels().fitTitle, type);
    const label = props.label || props.bucket || props.status || props.score;
    appendText(shell, "p", "mb-0", textFor(label));
    return shell;
  }

  renderSummary(type, props) {
    const shell = this.componentShell(labels().summaryTitle, type);
    if (props.explanation) appendText(shell, "p", "mb-0", props.explanation);
    return shell;
  }

  renderComparison(type, props) {
    const shell = this.componentShell(navigatorText("flow_titles", "comparison"), type, { description: this.modeDescriptionForTarget("find_right_service", "comparison") });
    const grid = makeEl("div", "website-navigator-card-grid");
    (props.services || []).forEach((service) => {
      const card = makeEl("article", "website-navigator-card");
      appendText(card, "h4", "", textFor(service.title));
      appendText(card, "p", "", textFor(service.short_description));
      grid.appendChild(card);
    });
    shell.appendChild(grid);
    (props.rows || []).slice(0, 5).forEach((row) => appendText(shell, "p", "mb-0", textFor(row.label || row.axis || row.recommendation || row)));
    return shell;
  }

  renderGroundedAnswer(type, props) {
    const shell = this.componentShell(navigatorText("flow_titles", "answer"), type, { description: this.modeDescriptionForTarget(this.activeShortcutMode, "") });
    appendText(shell, "p", "mb-0", props.answer || labels().noMatch);
    return shell;
  }

  renderSources(type, props) {
    const sources = props.sources || [];
    if (!sources.length) return null;
    const shell = this.componentShell(labels().sourcesTitle, type);
    const grid = makeEl("div", "website-navigator-card-grid");
    sources.slice(0, 4).forEach((source) => {
      const card = makeEl("article", "website-navigator-card");
      appendText(card, "h4", "", textFor(source.title || source.source_title || source.chunk_id));
      appendText(card, "p", "", textFor(source.summary || source.text || source.source_uri));
      grid.appendChild(card);
    });
    shell.appendChild(grid);
    return shell;
  }

  applyActionComponents(output) {
    if (!Array.isArray(output?.components)) return false;
    this.lastComponents = output.components;
    this.renderComponents(output.components);
    return true;
  }

  openBookingSetup(button) {
    this.activeBooking = null;
    this.bookingReservation = null;
    this.selectedSlot = null;
    this.selectedSlotTimezone = "";
    const components = Array.isArray(this.lastBookingSetupComponents) ? this.lastBookingSetupComponents : [];
    if (!components.length) {
      this.showChip(labels().unavailable, "error");
      return;
    }
    button?.blur?.();
    this.sectionStates.set("calendar", true);
    this.lastComponents = components;
    this.renderComponents(components);
  }

  renderCalendar(type, props) {
    const token = this.debugStart("booking.calendar.shell.render", { area: "booking", phase: "render" });
    const label = labels();
    const hasReservation = Boolean(this.bookingReservation?.reservation_id);
    const shell = this.componentShell(hasReservation ? label.slotSelected : label.selectSlot, type, { description: this.modeDescriptionForTarget("appointment", "booking") });
    shell.classList.toggle("is-slot-selected", hasReservation);
    this.bookingCalendarToggle = shell;
    if (hasReservation) this.setSectionOpen(shell, false);
    const initialTimezone = props.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!this.bookingTimezone) this.bookingTimezone = initialTimezone;
    const timezone = this.bookingTimezone;
    const ui = props.calendar_ui || {};
    const minLead = Number(props.min_lead_time_hours || 36);
    const durationMinutes = Number(props.duration_minutes || 30);
    const lookaheadDays = Math.max(1, Math.min(31, Number(ui.lookahead_days || props.lookahead_days || 14)));
    const visibleStartHour = Math.max(0, Math.min(23, Number(ui.visible_start_hour || props.visible_start_hour || 8)));
    const visibleEndHour = Math.max(visibleStartHour + 1, Math.min(24, Number(ui.visible_end_hour || props.visible_end_hour || 18)));
    const firstDate = addDays(new Date(Date.now() + minLead * 60 * 60 * 1000), 1);
    const firstDateKey = dateInputValue(firstDate);
    const days = Array.from({ length: lookaheadDays }, (_, index) => dateInputValue(addDays(isoLocalDayStart(firstDateKey), index)));
    const meta = makeEl("div", "website-navigator-calendar-meta");
    meta.append(makeEl("div", "website-navigator-time-filter-host"), this.renderTimezoneSelect(timezone));
    shell.appendChild(meta);
    const calendarHost = makeEl("div", "website-navigator-booking-calendar-host");
    shell.appendChild(calendarHost);
    this.fetchBookingSlots({
      shell,
      host: calendarHost,
      timezone,
      days,
      durationMinutes,
      visibleStartHour,
      visibleEndHour,
      label,
    });
    this.debugEnd(token, {
      details: {
        timezone,
        days_count: days.length,
        calendar_view_mode: this.bookingCalendarView.mode,
        has_reservation: hasReservation,
      },
    });
    return shell;
  }

  renderTimezoneSelect(timezone) {
    const label = labels();
    const field = makeEl("label", "website-navigator-timezone-field");
    appendText(field, "span", "website-navigator-calendar-control-label", label.timezoneSetting);
    const select = makeEl("select", "website-navigator-timezone-select");
    timezoneOptions(timezone).forEach((value) => {
      const option = makeEl("option", "", timezoneLabel(value));
      option.value = value;
      select.appendChild(option);
    });
    select.value = timezone;
    select.addEventListener("change", () => {
      const next = cleanText(select.value);
      if (!next || next === this.bookingTimezone) return;
      this.bookingTimezone = next;
      this.bookingReservation = null;
      this.selectedSlot = null;
      this.selectedSlotTimezone = "";
      this.bookingCalendarView.anchorDay = "";
      this.renderComponents(this.lastComponents);
    });
    field.appendChild(select);
    return field;
  }

  async fetchBookingSlots(config) {
    const { shell, host, timezone, days, label } = config;
    const operationId = this.debugOperation("booking.fetch_slots");
    const token = this.debugStart("booking.fetch_slots.operation", {
      area: "booking",
      phase: "frontend",
      operationId,
      details: {
        timezone,
        days_count: days.length,
        calendar_view_mode: this.bookingCalendarView.mode,
      },
    });
    host.innerHTML = "";
    appendText(host, "p", "website-navigator-calendar-note mb-0", label.loadingSlots);
    this.debugEvent("booking.calendar.loading_visible", { area: "booking", phase: "render", operationId });
    const start = isoLocalDayStart(days[0]);
    const end = addDays(isoLocalDayStart(days[days.length - 1]), 1);
    if (!start || !end) {
      this.debugEnd(token, { status: "skipped" });
      return;
    }
    try {
      const output = await this.runAction("scheduling.fetch_slots", "booking.initial_consultation", {
        timezone,
        range_start: start.toISOString(),
        range_end: end.toISOString(),
      }, { debugOperationId: operationId });
      if (!shell.isConnected) {
        this.debugEnd(token, { status: "skipped", details: { reason: "shell_disconnected" } });
        return;
      }
      if (this.applyActionComponents(output)) {
        this.debugEnd(token, { details: { status: cleanText(output.status), component_updates: true } });
        return;
      }
      this.renderBookingCalendar(host, output, config);
      this.debugEnd(token, {
        details: {
          status: cleanText(output.status),
          slots_count: Array.isArray(output.slots) ? output.slots.length : 0,
          staff_options_count: Array.isArray(output.staff_options) ? output.staff_options.length : 0,
        },
      });
    } catch {
      if (!shell.isConnected) {
        this.debugEnd(token, { status: "error", details: { reason: "shell_disconnected" } });
        return;
      }
      host.innerHTML = "";
      appendText(host, "p", "website-navigator-calendar-note mb-0", labels().connectionFailed);
      this.debugEnd(token, { status: "error" });
    }
  }

  renderBookingCalendar(host, output, config) {
    const token = this.debugStart("booking.calendar.render", { area: "booking", phase: "render" });
    output = output || {};
    const { timezone, days, label } = config;
    host.innerHTML = "";
    if (output.status && output.status !== "ready") {
      appendText(host, "p", "website-navigator-calendar-note mb-0", this.statusCopy(output.status));
      this.debugEnd(token, { status: "skipped", details: { status: cleanText(output.status) } });
      return;
    }
    const slots = output.slots || [];
    const slotsByDay = this.groupBookingSlots(slots, timezone);
    const availableDay = days.find((day) => this.countDaySlots(slotsByDay, day) > 0);
    let selectedDay = this.selectedSlot ? dateKeyInTimezone(slotStartValue(this.selectedSlot), timezone) : "";
    if (!dayInList(days, selectedDay)) selectedDay = availableDay || days[0];
    const filteredDay = this.firstMatchingBookingDay(days, slotsByDay);
    if (this.bookingTimeFilterKey && filteredDay && !this.dayHasMatchingBookingSlots(slotsByDay, selectedDay)) selectedDay = filteredDay;
    if (!dayInList(days, this.bookingCalendarView.anchorDay)) this.bookingCalendarView.anchorDay = selectedDay;
    const filterHost = config.shell?.querySelector(".website-navigator-time-filter-host") || makeEl("div", "website-navigator-time-filter-host");
    const pickerHost = makeEl("div", "website-navigator-day-picker-host");
    const gridHost = makeEl("div", "website-navigator-calendar-grid-host");
    host.appendChild(pickerHost);
    host.appendChild(gridHost);

    const renderAll = (day) => {
      this.debugEvent("booking.calendar.day_select", { area: "booking", phase: "frontend", details: { selected_day: day } });
      selectedDay = day;
      this.bookingCalendarView.anchorDay = day;
      this.renderBookingTimeFilters(filterHost, {
        slotsByDay,
        days,
        onFilterChange: () => {
          const nextDay = this.firstMatchingBookingDay(days, slotsByDay) || selectedDay;
          renderAll(nextDay);
        },
      });
      this.renderBookingDayPicker(pickerHost, {
        days,
        slotsByDay,
        selectedDay,
        label,
        onSelect: renderAll,
        onViewChange,
        onPeriodShift,
      });
      this.renderBookingSlotGrid(gridHost, {
        ...config,
        output,
        slotsByDay,
        selectedDay,
        selectedSlot: this.selectedSlot,
        onDayShift: (direction) => {
          const currentIndex = Math.max(0, days.indexOf(selectedDay));
          const nextDay = days[currentIndex + direction];
          if (nextDay) renderAll(nextDay);
        },
      });
    };
    const onViewChange = (mode) => {
      this.bookingCalendarView.mode = mode;
      this.bookingCalendarView.anchorDay = selectedDay;
      this.debugEvent("booking.calendar.view_change", { area: "booking", phase: "frontend", details: { calendar_view_mode: mode, selected_day: selectedDay } });
      this.renderBookingDayPicker(pickerHost, {
        days,
        slotsByDay,
        selectedDay,
        label,
        onSelect: renderAll,
        onViewChange,
        onPeriodShift,
      });
    };
    const onPeriodShift = (direction) => {
      const nextAnchor = shiftPeriod(this.bookingCalendarView.anchorDay || selectedDay, this.bookingCalendarView.mode, direction);
      this.bookingCalendarView.anchorDay = nextAnchor;
      this.renderBookingDayPicker(pickerHost, {
        days,
        slotsByDay,
        selectedDay,
        label,
        onSelect: renderAll,
        onViewChange,
        onPeriodShift,
      });
    };
    renderAll(selectedDay);
    this.debugEnd(token, {
      details: {
        timezone,
        days_count: days.length,
        slots_count: slots.length,
        selected_day: selectedDay,
        visible_slots_count: this.countDaySlots(slotsByDay, selectedDay),
      },
    });
  }

  activeBookingTimeFilter() {
    return BOOKING_TIME_FILTERS.find((filter) => filter.key === this.bookingTimeFilterKey) || null;
  }

  timeMatchesBookingFilter(time) {
    const filter = this.activeBookingTimeFilter();
    if (!filter) return false;
    const minute = timeToMinutes(time);
    return minute >= filter.startMinute && minute < filter.endMinute;
  }

  dayHasMatchingBookingSlots(slotsByDay, day) {
    const filter = this.activeBookingTimeFilter();
    if (!filter) return false;
    const daySlots = slotsByDay.get(day);
    if (!daySlots) return false;
    return Array.from(daySlots.entries()).some(([time, slots]) => slots.length > 0 && this.timeMatchesBookingFilter(time));
  }

  firstMatchingBookingDay(days, slotsByDay) {
    if (!this.activeBookingTimeFilter()) return "";
    return days.find((day) => this.dayHasMatchingBookingSlots(slotsByDay, day)) || "";
  }

  renderBookingTimeFilters(host, options) {
    const token = this.debugStart("booking.calendar.time_filters.render", { area: "booking", phase: "render" });
    const label = labels();
    host.innerHTML = "";
    const wrap = makeEl("div", "website-navigator-time-filter");
    appendText(wrap, "span", "website-navigator-time-filter-label website-navigator-calendar-control-label", label.preferredTime);
    const optionsWrap = makeEl("div", "website-navigator-time-filter-options");
    BOOKING_TIME_FILTERS.forEach((filter) => {
      const active = filter.key === this.bookingTimeFilterKey;
      const button = makeEl("button", `website-navigator-time-filter-chip${active ? " is-active" : ""}`, textFor(filter.label));
      button.type = "button";
      button.setAttribute("aria-pressed", active ? "true" : "false");
      button.addEventListener("click", () => {
        this.bookingTimeFilterKey = active ? "" : filter.key;
        options.onFilterChange?.();
      });
      optionsWrap.appendChild(button);
    });
    wrap.appendChild(optionsWrap);
    host.appendChild(wrap);
    this.debugEnd(token, { details: { filters_count: BOOKING_TIME_FILTERS.length, active_filter: cleanText(this.bookingTimeFilterKey) } });
  }

  groupBookingSlots(slots, timezone) {
    const slotsByDay = new Map();
    slots.forEach((slot) => {
      const start = slotStartValue(slot);
      const day = dateKeyInTimezone(start, timezone);
      const time = timeKeyInTimezone(start, timezone);
      if (!day || !time) return;
      if (!slotsByDay.has(day)) slotsByDay.set(day, new Map());
      const daySlots = slotsByDay.get(day);
      if (!daySlots.has(time)) daySlots.set(time, []);
      daySlots.get(time).push(slot);
    });
    return slotsByDay;
  }

  countDaySlots(slotsByDay, day) {
    const daySlots = slotsByDay.get(day);
    if (!daySlots) return 0;
    return Array.from(daySlots.values()).reduce((sum, slots) => sum + slots.length, 0);
  }

  renderBookingDayPicker(host, options) {
    const token = this.debugStart("booking.calendar.day_picker.render", { area: "booking", phase: "render" });
    const { days, slotsByDay, selectedDay, label, onSelect, onViewChange, onPeriodShift } = options;
    const mode = this.bookingCalendarView.mode === "month" ? "month" : "week";
    const anchorDay = this.bookingCalendarView.anchorDay || selectedDay;
    const calendarDays = calendarDaysForPeriod(anchorDay, mode);
    const selectedCount = this.countDaySlots(slotsByDay, selectedDay);
    const minDay = days[0] || selectedDay;
    const maxDay = days[days.length - 1] || selectedDay;
    const previousAnchor = shiftPeriod(anchorDay, mode, -1);
    const nextAnchor = shiftPeriod(anchorDay, mode, 1);
    const previousDays = calendarDaysForPeriod(previousAnchor, mode);
    const nextDays = calendarDaysForPeriod(nextAnchor, mode);
    const hasPrevious = previousDays.some((day) => day >= minDay && day <= maxDay);
    const hasNext = nextDays.some((day) => day >= minDay && day <= maxDay);
    host.innerHTML = "";
    const picker = makeEl("div", "website-navigator-calendar-picker");
    const toolbar = makeEl("div", "website-navigator-calendar-toolbar");
    const previous = makeEl("button", "website-navigator-calendar-nav");
    previous.type = "button";
    previous.disabled = !hasPrevious;
    previous.setAttribute("aria-label", label.previousPeriod);
    previous.textContent = "\u2039";
    previous.addEventListener("click", () => onPeriodShift?.(-1));
    const title = makeEl("strong", "website-navigator-calendar-title", periodTitle(anchorDay, mode));
    const next = makeEl("button", "website-navigator-calendar-nav");
    next.type = "button";
    next.disabled = !hasNext;
    next.setAttribute("aria-label", label.nextPeriod);
    next.textContent = "\u203a";
    next.addEventListener("click", () => onPeriodShift?.(1));
    const switcher = makeEl("div", "website-navigator-calendar-switch");
    [
      ["week", label.weekView],
      ["month", label.monthView],
    ].forEach(([value, text]) => {
      const button = makeEl("button", `website-navigator-calendar-switch-button${mode === value ? " is-active" : ""}`, text);
      button.type = "button";
      button.setAttribute("aria-pressed", mode === value ? "true" : "false");
      button.addEventListener("click", () => onViewChange?.(value));
      switcher.appendChild(button);
    });
    toolbar.append(previous, title, next, switcher);
    const weekdays = makeEl("div", "website-navigator-calendar-weekdays");
    calendarDaysForPeriod(anchorDay, "week").forEach((day) => {
      const date = isoLocalDayStart(day);
      appendText(weekdays, "span", "", date ? formatWeekdayShort(date) : "");
    });
    const grid = makeEl("div", `website-navigator-calendar-days is-${mode}`);
    calendarDays.forEach((day) => {
      const count = this.countDaySlots(slotsByDay, day);
      const inRange = dayInList(days, day);
      const selectable = inRange && count > 0;
      const optionClasses = [
        "website-navigator-calendar-day",
        day === selectedDay ? "is-selected" : "",
        count > 0 ? "is-available-day" : "is-empty-day",
        this.dayHasMatchingBookingSlots(slotsByDay, day) ? "is-matching-day" : "",
        !isSameMonth(day, anchorDay) ? "is-outside-period" : "",
      ].filter(Boolean).join(" ");
      const option = makeEl("button", optionClasses);
      option.type = "button";
      option.disabled = !selectable && day !== selectedDay;
      if (day === selectedDay) {
        option.tabIndex = -1;
        option.setAttribute("aria-disabled", "true");
      }
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", day === selectedDay ? "true" : "false");
      appendText(option, "strong", "website-navigator-calendar-day-number", formatCalendarDayNumber(day));
      appendText(option, "span", "website-navigator-calendar-day-label", count ? `${count} ${count === 1 ? label.slotSingular : label.slotPlural}` : label.zeroSlots);
      option.addEventListener("click", () => {
        if (option.disabled || day === selectedDay) return;
        onSelect(day);
      });
      grid.appendChild(option);
    });
    picker.append(toolbar, weekdays, grid);
    host.appendChild(picker);
    this.bindHorizontalNavigation(picker, { hasPrevious, hasNext, onNavigate: onPeriodShift });
    this.debugEnd(token, {
      details: {
        days_count: days.length,
        calendar_days_count: calendarDays.length,
        calendar_view_mode: mode,
        selected_day: selectedDay,
        selected_day_slots_count: selectedCount,
      },
    });
  }

  bindHorizontalNavigation(node, options) {
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let swiping = false;
    let wheelAt = 0;
    let suppressClick = false;
    const isTextControl = (target) => target instanceof Element && Boolean(target.closest("input,select,textarea,a"));
    const isButton = (target) => target instanceof Element && Boolean(target.closest("button"));
    const blocked = (direction) => {
      node.dataset.swipeBlocked = direction < 0 ? "previous" : "next";
      node.classList.remove("is-swipe-blocked");
      void node.offsetWidth;
      node.classList.add("is-swipe-blocked");
      window.setTimeout(() => {
        node.classList.remove("is-swipe-blocked");
        delete node.dataset.swipeBlocked;
      }, 260);
    };
    const canNavigate = (direction) => direction < 0 ? options.hasPrevious : options.hasNext;
    const navigate = (direction) => {
      if (!canNavigate(direction)) {
        blocked(direction);
        return;
      }
      options.onNavigate?.(direction);
    };
    node.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 && event.pointerType === "mouse") return;
      if (isTextControl(event.target)) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      swiping = false;
      node.classList.add("is-dragging");
      if (!isButton(event.target)) node.setPointerCapture?.(pointerId);
    });
    node.addEventListener("pointermove", (event) => {
      if (pointerId !== event.pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) > 18 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        swiping = true;
        event.preventDefault();
      }
    });
    const finish = (event) => {
      if (pointerId !== event.pointerId) return;
      if (node.hasPointerCapture?.(pointerId)) node.releasePointerCapture?.(pointerId);
      node.classList.remove("is-dragging");
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      pointerId = null;
      if (!swiping || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
      suppressClick = true;
      const direction = dx < 0 ? 1 : -1;
      navigate(direction);
    };
    node.addEventListener("pointerup", finish);
    node.addEventListener("pointercancel", (event) => {
      node.classList.remove("is-dragging");
      if (pointerId === event.pointerId) pointerId = null;
    });
    node.addEventListener("wheel", (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 18) return;
      event.preventDefault();
      const now = Date.now();
      if (now - wheelAt < 420) return;
      wheelAt = now;
      navigate(event.deltaX > 0 ? 1 : -1);
    });
    node.addEventListener("click", (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  renderBookingSlotGrid(host, options) {
    const token = this.debugStart("booking.calendar.slot_grid.render", { area: "booking", phase: "render" });
    const { output, slotsByDay, selectedDay, timezone, durationMinutes, visibleStartHour, visibleEndHour, label, days, onDayShift } = options;
    host.innerHTML = "";
    const slots = (output || {}).slots || [];
    const firstSlot = slots[0] || {};
    const slotMinutes = Math.max(5, Number(output.duration_minutes || durationMinutes || slotDurationMinutes(firstSlot) || 30));
    const slotsPerHour = Math.max(1, Math.round(60 / slotMinutes) || 1);
    const daySlots = slotsByDay.get(selectedDay) || new Map();
    let startMinute = visibleStartHour * 60;
    let endMinute = visibleEndHour * 60;
    Array.from(daySlots.keys()).forEach((time) => {
      const minute = timeToMinutes(time);
      startMinute = Math.min(startMinute, Math.floor(minute / 60) * 60);
      endMinute = Math.max(endMinute, Math.ceil((minute + slotMinutes) / 60) * 60);
    });
    const selectedIndex = days.indexOf(selectedDay);
    const area = makeEl("div", "website-navigator-slot-area");
    appendText(area, "h4", "website-navigator-slot-title", label.selectSlot);
    const nav = makeEl("div", "website-navigator-slot-nav");
    const previous = makeEl("button", "website-navigator-slot-day-button");
    previous.type = "button";
    previous.disabled = selectedIndex <= 0;
    previous.setAttribute("aria-label", label.previousDay);
    previous.textContent = "\u2039";
    previous.addEventListener("click", () => onDayShift?.(-1));
    const note = makeEl("p", "website-navigator-calendar-note website-navigator-slot-note mb-0");
    note.textContent = this.countDaySlots(slotsByDay, selectedDay) > 0
      ? `${label.slots}: ${formatDayLabel(selectedDay)}`
      : `${formatDayLabel(selectedDay)}: ${label.noSlotsAvailable}`;
    const next = makeEl("button", "website-navigator-slot-day-button");
    next.type = "button";
    next.disabled = selectedIndex < 0 || selectedIndex >= days.length - 1;
    next.setAttribute("aria-label", label.nextDay);
    next.textContent = "\u203a";
    next.addEventListener("click", () => onDayShift?.(1));
    nav.append(previous, note, next);
    area.appendChild(nav);
    const scroll = makeEl("div", "website-navigator-calendar-scroll");
    const table = makeEl("div", "website-navigator-calendar-table");
    table.style.setProperty("--website-navigator-slots-per-hour", String(slotsPerHour));
    for (let hour = startMinute; hour < endMinute; hour += 60) {
      const row = makeEl("div", "website-navigator-calendar-row");
      appendText(row, "div", "website-navigator-calendar-time-cell", minutesToTime(hour));
      const cell = makeEl("div", "website-navigator-calendar-slot-cell");
      const hourGrid = makeEl("div", "website-navigator-calendar-hour");
      for (let index = 0; index < slotsPerHour; index += 1) {
        const minute = hour + index * slotMinutes;
        if (minute >= endMinute) continue;
        const time = minutesToTime(minute);
        const slotOptions = daySlots.get(time) || [];
        const slot = slotOptions[0];
        const available = Boolean(slot);
        const isSelected = available && slotMatches(slot, this.selectedSlot);
        const isMatching = available && this.timeMatchesBookingFilter(time);
        const slotButton = makeEl("button", `website-navigator-calendar-slot${available ? " is-available" : " is-unavailable"}${isSelected ? " is-selected" : ""}${isMatching ? " is-matching-slot" : ""}`);
        slotButton.type = "button";
        slotButton.disabled = !available;
        slotButton.setAttribute("aria-disabled", available ? "false" : "true");
        slotButton.title = available
          ? `${time} - ${minutesToTime(minute + slotMinutes)} | ${label.availableSlot}`
          : `${time} - ${minutesToTime(minute + slotMinutes)} | ${label.unavailableSlot}`;
        appendText(slotButton, "strong", "", time);
        appendText(slotButton, "span", "", available ? label.availableSlot : label.unavailableSlot);
        if (available) {
          slotButton.addEventListener("click", () => this.reserveBookingSlot(slotButton, slot, timezone));
        }
        hourGrid.appendChild(slotButton);
      }
      cell.appendChild(hourGrid);
      row.appendChild(cell);
      table.appendChild(row);
    }
    scroll.appendChild(table);
    area.appendChild(scroll);
    host.appendChild(area);
    this.bindHorizontalNavigation(area, {
      hasPrevious: selectedIndex > 0,
      hasNext: selectedIndex >= 0 && selectedIndex < days.length - 1,
      onNavigate: onDayShift,
    });
    this.debugEnd(token, {
      details: {
        selected_day: selectedDay,
        visible_slots_count: this.countDaySlots(slotsByDay, selectedDay),
        slots_count: slots.length,
        slot_grid_buttons_count: area.querySelectorAll(".website-navigator-calendar-slot").length,
        timezone,
      },
    });
  }

  async reserveBookingSlot(button, slot, timezone) {
    const operationId = this.debugOperation("booking.reserve_slot");
    const token = this.debugStart("booking.reserve_slot.operation", {
      area: "booking",
      phase: "frontend",
      operationId,
      details: {
        timezone,
        has_staff_id: Boolean(cleanText(slot.staff_id)),
        has_slot_id: Boolean(cleanText(slot.slot_id)),
      },
    });
    this.debugEvent("booking.slot.click", { area: "booking", phase: "frontend", operationId, details: { timezone } });
    this.selectedSlot = slot;
    this.selectedSlotTimezone = timezone;
    const table = button.closest(".website-navigator-calendar-table");
    table?.querySelectorAll(".website-navigator-calendar-slot").forEach((child) => child.classList.remove("is-selected"));
    button.classList.add("is-selected");
    await this.runAction("scheduling.reserve_slot", "booking.initial_consultation", {
      slot_id: slot.slot_id,
      staff_id: slot.staff_id,
      start: slot.start_utc,
      end: slot.end_utc,
      timezone,
      confirmed: true,
    }, { idempotent: true, button, debugOperationId: operationId }).then((output) => {
      if (output.status === "reserved") {
        this.selectedSlot = {
          ...slot,
          ...(output.slot || {}),
          slot_id: slot.slot_id || output.slot?.slot_id,
          staff_label: slot.staff_label || output.slot?.staff_label,
          staff_avatar_url: slot.staff_avatar_url || output.slot?.staff_avatar_url,
        };
        this.selectedSlotTimezone = timezone;
        this.bookingReservation = output;
        if (this.bookingCalendarToggle) this.setSectionOpen(this.bookingCalendarToggle, false);
        this.showChip(labels().slotReserved);
        this.renderComponents(this.lastComponents);
      } else {
        if (this.applyActionComponents(output)) {
          this.debugEnd(token, { details: { status: cleanText(output.status), component_updates: true } });
          return;
        }
        this.showChip(this.statusCopy(output.status), "error");
      }
      this.debugEnd(token, { details: { status: cleanText(output.status) } });
    }).catch((error) => {
      this.debugEnd(token, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
      throw error;
    });
  }

  renderSelectedSlotSummary(slot, timezone, options = {}) {
    const label = labels();
    const summary = makeEl("div", "website-navigator-selected-slot");
    if (options.allowChange === false) summary.classList.add("is-static");
    const avatar = makeEl("div", "website-navigator-selected-slot-avatar");
    const name = slotStaffLabel(slot);
    const avatarUrl = cleanText(slot?.staff_avatar_url || slot?.staff_avatar);
    if (avatarUrl) {
      const image = makeEl("img");
      image.alt = name;
      image.loading = "lazy";
      image.src = avatarUrl.startsWith("http") ? avatarUrl : `${this.apiOrigin()}${avatarUrl}`;
      image.addEventListener("error", () => {
        image.remove();
        avatar.textContent = staffInitials(name);
      });
      avatar.appendChild(image);
    } else {
      avatar.textContent = staffInitials(name);
    }
    const body = makeEl("div", "website-navigator-selected-slot-body");
    appendText(body, "span", "website-navigator-selected-slot-kicker", options.kicker || label.selectedSlot);
    appendText(body, "strong", "", formatSlotRange(slot, timezone));
    appendText(body, "span", "", `${label.withStaff} ${name}`);
    summary.append(avatar, body);
    if (options.allowChange !== false) {
      const change = makeEl("button", "website-navigator-action website-navigator-slot-change", label.changeSlot);
      change.type = "button";
      change.addEventListener("click", () => {
        if (this.bookingFinalizeShell) this.setSectionOpen(this.bookingFinalizeShell, false);
        if (this.bookingCalendarToggle) this.setSectionOpen(this.bookingCalendarToggle, true);
      });
      summary.appendChild(change);
    }
    return summary;
  }

  renderBookingSummary(type, props) {
    const label = labels();
    const booking = props.booking || this.activeBooking || {};
    const cancellation = props.cancellation || {};
    const isCancelled = cleanText(booking.status) === "cancelled" || props.is_cancelled === true;
    const cancelWindowExpired = cancellation.cancel_window_expired === true;
    const canCancel = cancellation.can_cancel !== false && !cancelWindowExpired;
    const canBookAgainAfterCancel = cancellation.can_book_again_after_cancel !== false && canCancel;
    this.activeBooking = booking;
    if (isCancelled) this.clearShortcutModeFinished("booking");
    else this.markShortcutModeFinished("booking", "modeBooked");
    if (Array.isArray(props.setup_components)) this.lastBookingSetupComponents = props.setup_components;
    const shell = this.componentShell(isCancelled ? label.appointmentCancelled : label.appointmentConfirmed, type, { description: this.modeDescriptionForTarget("appointment", isCancelled ? "booking_cancelled" : "booking_summary") });
    shell.classList.add("website-navigator-booking-summary-shell");
    const card = makeEl("article", "website-navigator-booking-confirmation");
    card.appendChild(this.renderSelectedSlotSummary(booking, booking.timezone, {
      allowChange: false,
      kicker: isCancelled ? label.appointmentCancelled : label.appointmentConfirmed,
    }));
    const notes = makeEl("div", "website-navigator-booking-confirmation-notes");
    const isFinalCancellableBooking = !isCancelled && canCancel && cancellation.can_book_again_after_cancel === false;
    if (isCancelled) {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.appointmentCancelledNotice);
      appendText(notes, "p", "website-navigator-booking-confirmation-note", cancellation.can_book_again_after_cancel === false ? label.cancelLimitReached : label.cancelledCanBookAgain);
    } else {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.bookingEmailNotice);
    }
    if (!isCancelled && isFinalCancellableBooking) {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.alreadyRescheduledTwice);
    } else if (!isCancelled && cancellation.rebooking_requires_previous_cancellation) {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.rebookingRequiresCancel);
    }
    if (isFinalCancellableBooking) {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.rebookingLimitNotice);
    }
    if (!isCancelled && cancelWindowExpired) {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.cancelWindowExpiredNotice);
    }
    const actions = makeEl("div", "website-navigator-form-actions website-navigator-booking-confirmation-actions");
    if (isCancelled && cancellation.can_book_again_after_cancel !== false) {
      const bookNew = makeEl("button", "website-navigator-action is-primary website-navigator-action-book-new", label.bookNewConsultation);
      bookNew.type = "button";
      bookNew.addEventListener("click", () => this.openBookingSetup(bookNew));
      actions.appendChild(bookNew);
    } else if (!isCancelled && canCancel) {
      if (canBookAgainAfterCancel) {
        const reschedule = makeEl("button", "website-navigator-action is-primary website-navigator-action-reschedule", label.rescheduleBooking);
        reschedule.type = "button";
        reschedule.addEventListener("click", () => this.runBookingLifecycleAction("scheduling.reschedule_booking", booking, reschedule));
        actions.appendChild(reschedule);
      }
      const cancel = makeEl("button", "website-navigator-action website-navigator-action-cancel", label.cancelBooking);
      cancel.type = "button";
      cancel.addEventListener("click", () => this.runBookingLifecycleAction("scheduling.cancel_booking", booking, cancel));
      actions.appendChild(cancel);
    } else if (!isCancelled && cancelWindowExpired) {
      const reschedule = makeEl("button", "website-navigator-action is-primary is-fixed website-navigator-action-reschedule", label.rescheduleBooking);
      reschedule.type = "button";
      reschedule.disabled = true;
      actions.appendChild(reschedule);
      const cancel = makeEl("button", "website-navigator-action is-fixed website-navigator-action-cancel", label.cancelBooking);
      cancel.type = "button";
      cancel.disabled = true;
      actions.appendChild(cancel);
    } else if (!isCancelled) {
      appendText(notes, "p", "website-navigator-booking-confirmation-note", label.cancelLimitReached);
    }
    if (notes.children.length) card.appendChild(notes);
    if (actions.children.length) card.appendChild(actions);
    shell.appendChild(card);
    return shell;
  }

  async runBookingLifecycleAction(actionId, booking, button) {
    const label = labels();
    const bookingId = cleanText(booking.booking_id || booking.id);
    if (!bookingId) {
      this.showChip(label.connectionFailed, "error");
      return;
    }
    const output = await this.runAction(actionId, "booking.initial_consultation", {
      booking_id: bookingId,
      confirmed: true,
    }, { idempotent: true, button });
    const cancelled = output.status === "cancelled" || output.status === "cancelled_for_rebooking";
    if (!cancelled) {
      if (!this.applyActionComponents(output)) this.showChip(this.statusCopy(output.status), "error");
      return;
    }
    this.showChip(label.bookingCancelled);
    this.activeBooking = null;
    this.bookingReservation = null;
    this.selectedSlot = null;
    this.selectedSlotTimezone = "";
    if (output.status === "cancelled_for_rebooking" && output.can_book_again) {
      if (this.applyActionComponents(output)) return;
      if (this.lastBookingSetupComponents.length) {
        this.renderComponents(this.lastBookingSetupComponents);
        return;
      }
    }
    this.renderComponents([
      {
        component_type: "booking_summary",
        component_key: "booking_summary",
        props: {
          booking: { ...booking, status: "cancelled" },
          cancellation: {
            can_cancel: false,
            can_book_again_after_cancel: output.can_book_again !== false,
            max_cancel_rebook_cycles_per_session: output.max_cancel_rebook_cycles_per_session,
            cancel_rebook_cycles_used: output.cancel_rebook_cycles_used,
          },
          setup_components: this.lastBookingSetupComponents,
          is_cancelled: true,
        },
      },
    ]);
  }

  renderPhoneField(labelText, options = {}) {
    const label = labels();
    const statePrefix = options.scope === "contact" ? "contact" : "booking";
    const countryKey = statePrefix === "contact" ? "contactPhoneCountry" : "bookingPhoneCountry";
    const menuOpenKey = statePrefix === "contact" ? "contactPhoneMenuOpen" : "bookingPhoneMenuOpen";
    const searchKey = statePrefix === "contact" ? "contactCountrySearch" : "bookingCountrySearch";
    const field = makeEl("div", "website-navigator-field website-navigator-phone-field");
    appendText(field, "span", "", labelText);
    const wrap = makeEl("div", "website-navigator-phone-input");
    const prefix = makeEl("div", "website-navigator-phone-prefix");
    const trigger = makeEl("button", "website-navigator-phone-prefix-trigger");
    trigger.type = "button";
    const flag = appendText(trigger, "span", "website-navigator-phone-flag", "");
    const dial = appendText(trigger, "span", "website-navigator-phone-dial", "");
    const menu = makeEl("div", "website-navigator-phone-prefix-menu");
    const search = makeEl("input", "website-navigator-phone-prefix-search");
    search.type = "search";
    search.placeholder = label.searchCountry;
    search.value = this[searchKey];
    const list = makeEl("div", "website-navigator-phone-prefix-list");
    const renderList = () => {
      const query = cleanText(search.value).toLowerCase();
      list.innerHTML = "";
      PHONE_COUNTRIES.filter((country) => {
        if (!query) return true;
        return country.code.toLowerCase().includes(query)
          || country.name.toLowerCase().includes(query)
          || country.dial.includes(query);
      }).forEach((country) => {
        const item = makeEl("button", "website-navigator-phone-prefix-item");
        item.type = "button";
        appendText(item, "span", "website-navigator-phone-flag", countryFlag(country.code));
        appendText(item, "span", "website-navigator-phone-code", country.code);
        appendText(item, "span", "website-navigator-phone-name", country.name);
        appendText(item, "span", "website-navigator-phone-dial", country.dial);
        item.addEventListener("click", () => {
          this[countryKey] = country.code;
          this[menuOpenKey] = false;
          this[searchKey] = "";
          search.value = "";
          sync();
          input.focus();
        });
        list.appendChild(item);
      });
    };
    const sync = () => {
      const country = countryByCode(this[countryKey]);
      flag.textContent = countryFlag(country.code);
      dial.textContent = country.dial;
      prefix.classList.toggle("is-open", this[menuOpenKey]);
      menu.hidden = !this[menuOpenKey];
      renderList();
      if (this[menuOpenKey]) window.setTimeout(() => search.focus(), 0);
    };
    trigger.addEventListener("click", () => {
      this[menuOpenKey] = !this[menuOpenKey];
      sync();
    });
    search.addEventListener("input", () => {
      this[searchKey] = search.value;
      renderList();
    });
    menu.append(search, list);
    prefix.append(trigger, menu);
    const input = makeEl("input");
    input.type = "tel";
    input.className = "website-navigator-phone-number";
    wrap.append(prefix, input);
    field.appendChild(wrap);
    sync();
    return { field, input, value: () => buildPhoneValue(this[countryKey], input.value) };
  }

  applyContactBootstrap(stateView) {
    const uploadPolicy = stateView.contact_upload_policy || {};
    this.contactUploadPolicy = {
      allowed_extensions: Array.isArray(uploadPolicy.allowed_extensions) ? uploadPolicy.allowed_extensions.map((item) => cleanText(item).toLowerCase()).filter(Boolean) : [],
      max_file_size_bytes: Number(uploadPolicy.max_file_size_bytes) || 0,
      max_files_per_contact: Number(uploadPolicy.max_files_per_contact) || 0,
      purpose: cleanText(uploadPolicy.purpose),
    };
    const contactDraft = stateView.contact_draft || {};
    const draft = contactDraft.draft || {};
    this.contactDraft = {
      name: cleanText(draft.name),
      email: cleanText(draft.email),
      phone: cleanText(draft.phone),
      website_url: cleanText(draft.website_url),
      message: cleanText(draft.message),
      privacy_consent: draft.privacy_consent === true,
    };
    this.contactAttachments = Array.isArray(contactDraft.attachments)
      ? contactDraft.attachments.map((item) => this.normalizeContactAttachment(item)).filter((item) => item.upload_id)
      : [];
  }

  normalizeContactAttachment(item) {
    const filename = cleanText(item?.display_filename || item?.file_name || item?.filename);
    const extension = cleanText(item?.extension).toLowerCase() || this.fileExtension(filename);
    return {
      upload_id: cleanText(item?.upload_id),
      display_filename: filename,
      mime_type: cleanText(item?.mime_type),
      detected_mime_type: cleanText(item?.detected_mime_type),
      size_bytes: Number(item?.size_bytes) || 0,
      status: cleanText(item?.status),
      is_ready: item?.is_ready === true || item?.consumable === true,
      scan_status: cleanText(item?.scan_status),
      scan_level: cleanText(item?.scan_level),
      scan_result: cleanText(item?.scan_result),
      scan_error: cleanText(item?.scan_error),
      extension,
    };
  }

  fileExtension(filename) {
    const value = cleanText(filename).toLowerCase();
    const index = value.lastIndexOf(".");
    if (index < 0 || index === value.length - 1) return "";
    return value.slice(index + 1);
  }

  contactAttachmentLimit() {
    return Number(this.contactUploadPolicy.max_files_per_contact) || 0;
  }

  contactAttachmentPurpose() {
    return cleanText(this.contactUploadPolicy.purpose);
  }

  contactAttachmentIds() {
    return this.contactAttachments.map((item) => cleanText(item.upload_id)).filter(Boolean);
  }

  readyContactAttachmentIds() {
    return this.contactAttachments.filter((item) => item.is_ready === true).map((item) => cleanText(item.upload_id)).filter(Boolean);
  }

  pendingContactAttachments() {
    return this.contactAttachments.filter((item) => item.is_ready !== true);
  }

  refreshContactAttachments() {
    if (typeof this.contactAttachmentRender === "function") this.contactAttachmentRender();
  }

  upsertContactAttachment(item) {
    const next = this.normalizeContactAttachment(item);
    if (!next.upload_id) return;
    const index = this.contactAttachments.findIndex((attachment) => cleanText(attachment.upload_id) === next.upload_id);
    if (index >= 0) this.contactAttachments.splice(index, 1, { ...this.contactAttachments[index], ...next });
    else this.contactAttachments.push(next);
    this.refreshContactAttachments();
  }

  removeContactAttachment(uploadId, collectDraft) {
    const id = cleanText(uploadId);
    this.contactAttachments = this.contactAttachments.filter((item) => cleanText(item.upload_id) !== id);
    this.refreshContactAttachments();
    this.saveContactDraft(collectDraft()).catch(() => { });
  }

  formatContactAttachmentName(filename) {
    const value = cleanText(filename) || "file";
    const maxLength = 15;
    if (value.length <= maxLength) return value;
    const dotIndex = value.lastIndexOf(".");
    const extension = dotIndex > 0 ? value.slice(dotIndex) : "";
    if (extension && extension.length < maxLength - 4) {
      const stemLength = Math.max(1, maxLength - extension.length - 3);
      return `${value.slice(0, stemLength)}...${extension}`;
    }
    return `${value.slice(0, maxLength - 3)}...`;
  }

  renderContactFileTypeIcon(extension) {
    const value = cleanText(extension).toLowerCase();
    const slug = value.replace(/[^a-z0-9_-]+/g, "-") || "file";
    const label = value || "file";
    const span = makeEl("span", `website-navigator-file-type-icon is-${slug}`);
    span.setAttribute("aria-label", label);
    span.setAttribute("role", "img");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 40 40");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M11 5h12l6 6v24H11Z");
    const fold = document.createElementNS("http://www.w3.org/2000/svg", "path");
    fold.setAttribute("d", "M23 5v7h6");
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", "20");
    text.setAttribute("y", "28");
    text.setAttribute("text-anchor", "middle");
    text.textContent = value.slice(0, 3).toUpperCase() || "FILE";
    svg.append(path, fold, text);
    span.appendChild(svg);
    return span;
  }

  contactAttachmentStatusText(attachment) {
    if (attachment.is_ready === true) return labels().fileReady;
    return labels().uploadingFile;
  }

  renderContactAttachmentRow(attachment, collectDraft) {
    const row = makeEl("div", "website-navigator-contact-attachment-row");
    const filename = cleanText(attachment.display_filename) || "file";
    row.appendChild(this.renderContactFileTypeIcon(attachment.extension || this.fileExtension(filename)));
    const meta = makeEl("div", "website-navigator-contact-attachment-meta");
    const title = makeEl("strong", "", this.formatContactAttachmentName(filename));
    title.title = filename;
    const status = makeEl("span", "website-navigator-contact-attachment-status", this.contactAttachmentStatusText(attachment));
    meta.append(title, status);
    const remove = makeEl("button", "website-navigator-contact-attachment-remove");
    remove.type = "button";
    remove.setAttribute("aria-label", labels().removeFile);
    remove.title = labels().removeFile;
    remove.addEventListener("click", () => this.removeContactAttachment(attachment.upload_id, collectDraft));
    remove.appendChild(makeEl("span", "website-navigator-contact-attachment-remove-icon"));
    row.append(meta, remove);
    return row;
  }

  renderContactAttachmentZone(collectDraft) {
    const label = labels();
    const zone = makeEl("div", "website-navigator-contact-upload-zone");
    const list = makeEl("div", "website-navigator-contact-attachments");
    const input = makeEl("input");
    input.type = "file";
    input.multiple = true;
    input.hidden = true;
    input.accept = (this.contactUploadPolicy.allowed_extensions || []).map((item) => `.${cleanText(item).toLowerCase()}`).join(",");
    const button = makeEl("button", "website-navigator-action website-navigator-contact-attach-button", label.attachFiles);
    button.type = "button";
    button.addEventListener("click", () => input.click());
    input.addEventListener("change", () => {
      const files = Array.from(input.files || []);
      input.value = "";
      this.handleContactFiles(files, collectDraft, button);
    });
    zone.append(list, button, input);
    this.contactUploadInput = input;
    this.contactAttachmentRender = () => {
      list.replaceChildren(...this.contactAttachments.map((item) => this.renderContactAttachmentRow(item, collectDraft)));
      list.hidden = this.contactAttachments.length === 0;
      const limit = this.contactAttachmentLimit();
      button.disabled = this.contactUploadBusy || (limit > 0 && this.contactAttachments.length >= limit);
    };
    this.refreshContactAttachments();
    return { zone, button };
  }

  bindContactFileDrop(shell, collectDraft, button) {
    const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");
    const activate = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      shell.classList.add("is-contact-file-drag-active");
    };
    shell.addEventListener("dragenter", activate);
    shell.addEventListener("dragover", activate);
    shell.addEventListener("dragleave", (event) => {
      const nextTarget = event.relatedTarget;
      if (nextTarget && shell.contains(nextTarget)) return;
      shell.classList.remove("is-contact-file-drag-active");
    });
    shell.addEventListener("drop", (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      shell.classList.remove("is-contact-file-drag-active");
      this.handleContactFiles(Array.from(event.dataTransfer?.files || []), collectDraft, button);
    });
  }

  async saveContactDraft(draft, options = {}) {
    const actionOptions = {
      button: options.button,
      debugArea: "contact",
      debugOperationId: options.debugOperationId || this.debugOperation("contact.draft.save"),
      showLoading: options.showLoading !== false,
    };
    const output = await this.runAction("contact.draft.save", "", {
      draft: {
        name: cleanText(draft.name),
        email: cleanText(draft.email),
        phone: cleanText(draft.phone),
        website_url: cleanText(draft.website_url),
        message: cleanText(draft.message),
        privacy_consent: draft.privacy_consent === true,
      },
      upload_ids: this.contactAttachmentIds(),
    }, actionOptions);
    if (output.contact_draft) {
      this.applyContactBootstrap({ contact_draft: output.contact_draft, contact_upload_policy: this.contactUploadPolicy });
      this.refreshContactAttachments();
    }
    return output;
  }

  async handleContactFiles(files, collectDraft, button) {
    const selected = Array.from(files || []).filter(Boolean);
    if (!selected.length) return;
    const limit = this.contactAttachmentLimit();
    const remaining = limit > 0 ? Math.max(0, limit - this.contactAttachments.length) : selected.length;
    if (remaining <= 0) {
      this.showChip(labels().tooManyFiles, "info");
      return;
    }
    const accepted = selected.slice(0, remaining);
    if (accepted.length < selected.length) this.showChip(labels().tooManyFiles, "info");
    this.contactUploadBusy = true;
    this.refreshContactAttachments();
    try {
      await this.saveContactDraft(collectDraft(), { button });
      for (const file of accepted) {
        await this.prepareAndUploadContactFile(file, collectDraft, button);
      }
    } finally {
      this.contactUploadBusy = false;
      this.refreshContactAttachments();
    }
  }

  async prepareAndUploadContactFile(file, collectDraft, button) {
    const purpose = this.contactAttachmentPurpose();
    if (!purpose) {
      this.showChip(labels().connectionFailed, "error");
      return;
    }
    const operationId = this.debugOperation("contact.upload.prepare");
    const output = await this.runAction("upload.prepare", "upload_handoff.prepare_upload", {
      file_name: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      purpose,
      confirmed: true,
      consent_granted: true,
    }, { idempotent: true, button, debugOperationId: operationId });
    this.upsertContactAttachment({
      upload_id: output.upload_id,
      display_filename: output.display_filename || file.name,
      mime_type: file.type,
      size_bytes: file.size,
      status: output.status,
      is_ready: output.is_ready === true || output.consumable === true,
      extension: output.extension || this.fileExtension(file.name),
    });
    await this.saveContactDraft(collectDraft(), { button });
    await this.uploadContactFile(output, file, collectDraft, button, operationId);
  }

  async uploadContactFile(prepared, file, collectDraft, button, operationId = "") {
    if (!prepared.upload_url) return;
    const form = new FormData();
    form.append("file", file);
    const uploadUrl = prepared.upload_url.startsWith("http") ? prepared.upload_url : `${this.apiOrigin()}${prepared.upload_url}`;
    const requestToken = this.latencyDebug?.requestStart?.(uploadUrl, {
      method: "POST",
      body: form,
      debugArea: "contact",
      operationId: operationId || this.debugOperation("contact.upload.file"),
      actionId: "contact.upload.file",
    });
    let requestFinished = false;
    const pendingToken = this.showPendingIndicator(this.actionLoadingText("upload.file"));
    try {
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: form,
        headers: {
          ...(this.latencyDebug?.requestHeaders?.(requestToken) || {}),
        },
      });
      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : null;
      this.latencyDebug?.requestEnd?.(requestToken, {
        status: response.status,
        ok: response.ok,
        responseText,
        data,
        serverTiming: response.headers.get("server-timing"),
        requestId: response.headers.get("x-wnav-request-id"),
        actionId: "contact.upload.file",
      });
      requestFinished = true;
      if (!response.ok) throw new Error(cleanText(data?.detail) || "upload_failed");
      this.upsertContactAttachment({
        upload_id: data.upload_id,
        display_filename: prepared.display_filename || file.name,
        mime_type: file.type,
        detected_mime_type: data.detected_mime_type,
        size_bytes: data.size_bytes || file.size,
        status: data.status,
        is_ready: data.is_ready === true || data.consumable === true,
        extension: prepared.extension || this.fileExtension(file.name),
      });
      this.showChip(labels().uploadReceived);
      await this.saveContactDraft(collectDraft(), { button });
      this.pollContactUpload(data.upload_id, collectDraft);
    } catch (error) {
      if (!requestFinished) this.latencyDebug?.requestError?.(requestToken, error);
      this.showChip(labels().connectionFailed, "error");
    } finally {
      this.hidePendingIndicator(pendingToken);
    }
  }

  pollContactUpload(uploadId, collectDraft) {
    let count = 0;
    const operationId = this.debugOperation("contact.upload.poll");
    const pendingToken = this.showPendingIndicator(this.actionLoadingText("upload.status"));
    const tick = async () => {
      count += 1;
      try {
        const data = await this.fetchJson(`/uploads/${encodeURIComponent(uploadId)}/status`, {
          debugArea: "contact",
          debugOperationId: operationId,
          debugEvent: "contact.upload.status",
        });
        this.upsertContactAttachment(data);
        await this.saveContactDraft(collectDraft(), { showLoading: false });
        if (data.is_ready === true || data.consumable === true) {
          this.showChip(labels().fileReady);
          this.hidePendingIndicator(pendingToken);
          return;
        }
      } catch {
        this.showChip(labels().connectionFailed, "error");
        this.hidePendingIndicator(pendingToken);
        return;
      }
      if (count < 12) window.setTimeout(tick, 2500);
      else this.hidePendingIndicator(pendingToken);
    };
    tick();
  }

  renderContactForm(componentKey, props) {
    const isBooking = componentKey === "booking_contact_microform";
    const area = isBooking ? "booking" : "contact";
    const token = this.debugStart(isBooking ? "booking.contact_form.render" : "contact.form.render", { area, phase: "render" });
    const label = labels();
    if (isBooking && !this.bookingReservation?.reservation_id) {
      this.debugEnd(token, { status: "skipped", details: { reason: "missing_reservation" } });
      return null;
    }
    if (!isBooking && this.hasSubmittedInquiry()) {
      const node = this.renderContactConfirmation("contact_confirmation", this.lastInquiryOutput || {});
      this.debugEnd(token, { details: { already_submitted: true } });
      return node;
    }
    const shell = this.componentShell(isBooking ? label.finalizeBooking : label.forwardInquiry, componentKey || "contact_microform", { description: this.modeDescriptionForTarget(isBooking ? "appointment" : "contact", isBooking ? "booking_finalize" : "contact") });
    const draft = isBooking ? this.bookingContactDraft || {} : this.contactDraft || {};
    const phoneDraft = phoneDraftParts(draft.phone);
    if (isBooking && draft.phone) this.bookingPhoneCountry = phoneDraft.countryCode;
    if (!isBooking && draft.phone) this.contactPhoneCountry = phoneDraft.countryCode;
    if (isBooking) {
      this.bookingFinalizeShell = shell;
      this.setSectionOpen(shell, true);
      shell.appendChild(this.renderSelectedSlotSummary(this.selectedSlot || this.bookingReservation.slot || {}, this.selectedSlotTimezone || props.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone));
    }
    const fields = makeEl("div", "website-navigator-field-grid");
    const summary = isBooking ? null : this.textAreaField(label.inquiryMessage);
    const name = this.formField(label.name, "text");
    const email = this.formField(label.email, "email");
    const phone = this.renderPhoneField(label.phoneOptional, { scope: isBooking ? "booking" : "contact" });
    const website = this.formField(label.websiteOptional, "url");
    website.input.placeholder = "https://";
    name.input.value = cleanText(draft.name);
    email.input.value = cleanText(draft.email);
    phone.input.value = cleanText(phoneDraft.local);
    website.input.value = cleanText(draft.website_url);
    fields.append(name.field, email.field, phone.field, website.field);
    if (summary) {
      summary.field.classList.add("is-wide");
      const prefillMessage = cleanText(props.prefill_message) || cleanText(this.lastModuleState?.contact_prefill_message);
      summary.input.value = cleanText(draft.message) || prefillMessage;
      summary.input.dispatchEvent(new Event("input", { bubbles: true }));
      fields.appendChild(summary.field);
    }
    const collectDraft = () => ({
      name: cleanText(name.input.value),
      email: cleanText(email.input.value),
      phone: phone.value(),
      website_url: cleanText(website.input.value),
      message: summary ? cleanText(summary.input.value) : "",
      privacy_consent: true,
    });
    shell.appendChild(fields);
    let attachmentButton = null;
    if (!isBooking) {
      const attachmentZone = this.renderContactAttachmentZone(collectDraft);
      attachmentButton = attachmentZone.button;
      shell.appendChild(attachmentZone.zone);
    }
    if (!isBooking && attachmentButton) this.bindContactFileDrop(shell, collectDraft, attachmentButton);
    const cyclesUsed = Number(this.bookingReservation?.cancel_rebook_cycles_used);
    const maxCycles = Number(this.bookingReservation?.max_cancel_rebook_cycles_per_session);
    if (isBooking && Number.isFinite(cyclesUsed) && Number.isFinite(maxCycles) && maxCycles > 0 && cyclesUsed >= maxCycles - 1) {
      appendText(shell, "p", "website-navigator-booking-final-note", label.finalBookingNotice);
    }
    const actions = makeEl("div", "website-navigator-form-actions");
    const button = makeEl("button", `website-navigator-action is-primary${isBooking ? " is-confirm" : " is-send"}`, isBooking ? label.confirmBooking : label.send);
    button.type = "button";
    actions.appendChild(button);
    shell.appendChild(actions);
    button.addEventListener("click", async () => {
      const submitOperationId = this.debugOperation(isBooking ? "booking.confirm_booking" : "contact.submit");
      const submitToken = this.debugStart(isBooking ? "booking.confirm_booking.operation" : "contact.submit", {
        area,
        phase: "frontend",
        operationId: submitOperationId,
        details: {
          required_fields_count: isBooking ? 2 : 3,
          optional_fields_count: isBooking ? 2 : 1,
          has_upload_prompt: Boolean(this.lastComponents.some((component) => cleanText(component.component_type) === "upload_dropzone")),
        },
      });
      [summary?.field, name.field, email.field].filter(Boolean).forEach((field) => field.classList.remove("is-invalid"));
      const missing = [];
      const summaryValue = summary ? cleanText(summary.input.value) : "";
      const nameValue = cleanText(name.input.value);
      const emailValue = cleanText(email.input.value);
      if (!isBooking && !summaryValue) {
        missing.push(label.missingMessage);
        summary.field.classList.add("is-invalid");
      }
      if (!nameValue) {
        missing.push(label.missingName);
        name.field.classList.add("is-invalid");
      }
      if (!emailValue || !looksLikeEmail(emailValue)) {
        missing.push(label.missingEmail);
        email.field.classList.add("is-invalid");
      }
      if (missing.length) {
        this.showChip(`${label.missingFields}: ${missing.join(", ")}`, "error");
        this.debugEnd(submitToken, { status: "skipped", details: { missing_count: missing.length } });
        return;
      }
      const contact = {
        name: nameValue,
        email: emailValue,
        phone: phone.value(),
        website_url: cleanText(website.input.value),
        privacy_consent: true,
      };
      if (isBooking) {
        this.bookingContactDraft = {
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          website_url: contact.website_url,
        };
        if (!this.bookingReservation?.reservation_id) {
          this.showChip(label.reserveSlotFirst, "error");
          this.debugEnd(submitToken, { status: "skipped", details: { reason: "missing_reservation" } });
          return;
        }
        await this.runAction("scheduling.confirm_booking", "booking.initial_consultation", {
          reservation_id: this.bookingReservation.reservation_id,
          contact,
          confirmed: true,
          consent_granted: true,
        }, { idempotent: true, button, debugOperationId: submitOperationId }).then((output) => {
          const ok = output.status === "booked" || output.status === "confirmed";
          if (ok) {
            this.activeBooking = output.booking || null;
            this.bookingReservation = null;
            this.selectedSlot = null;
            this.selectedSlotTimezone = "";
          }
          this.showChip(ok ? label.bookingConfirmed : this.statusCopy(output.status), ok ? "info" : "error");
          if (this.applyActionComponents(output)) {
            this.debugEnd(submitToken, { details: { status: cleanText(output.status), ok, component_updates: true } });
            return;
          }
          this.debugEnd(submitToken, { details: { status: cleanText(output.status), ok } });
        }).catch((error) => {
          this.debugEnd(submitToken, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
          throw error;
        });
        return;
      }
      const pendingAttachments = this.pendingContactAttachments();
      if (pendingAttachments.length) {
        this.showChip(label.waitForUploads, "info");
        this.debugEnd(submitToken, { status: "skipped", details: { reason: "uploads_pending", upload_count: pendingAttachments.length } });
        return;
      }
      this.contactDraft = collectDraft();
      await this.saveContactDraft(this.contactDraft, { button, debugOperationId: this.debugOperation("contact.draft.submit_save") });
      await this.runAction("inquiry.create", "prepare_inquiry.create_contact", {
        contact,
        summary: summaryValue,
        next_steps: this.lastModuleState.next_steps || [],
        service_ids: this.lastModuleState.service_ids || [],
        lead_score: this.lastModuleState.lead_score || {},
        attachment_upload_ids: this.readyContactAttachmentIds(),
        confirmed: true,
        consent_granted: true,
      }, { idempotent: true, button, debugOperationId: submitOperationId }).then((output) => {
        const ok = output.status === "created" || output.status === "submitted";
        if (ok) {
          this.markInquirySubmitted(output);
          this.contactAttachments = [];
          const components = this.contactConfirmationComponents(output);
          this.lastComponents = components;
          this.lastModuleState = {
            ...(this.lastModuleState || {}),
            inquiry_status: "submitted",
          };
          this.renderComponents(components);
        }
        this.showChip(ok ? label.inquirySent : this.statusCopy(output.status), ok ? "info" : "error");
        this.debugEnd(submitToken, { details: { status: cleanText(output.status), ok } });
      }).catch((error) => {
        this.debugEnd(submitToken, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
        throw error;
      });
    });
    this.debugEnd(token, {
      details: {
        required_fields_count: isBooking ? 3 : 4,
        optional_fields_count: isBooking ? 2 : 1,
        is_booking: isBooking,
      },
    });
    return shell;
  }

  formField(label, type) {
    const field = makeEl("label", "website-navigator-field");
    appendText(field, "span", "", label);
    const input = makeEl("input");
    input.type = type;
    field.appendChild(input);
    return { field, input };
  }

  textAreaField(label) {
    const field = makeEl("label", "website-navigator-field");
    appendText(field, "span", "", label);
    const wrap = makeEl("div", "website-navigator-textarea-wrap");
    const input = makeEl("textarea");
    input.rows = 4;
    const clear = makeEl("button", "website-navigator-textarea-clear");
    clear.type = "button";
    const clearLabel = currentLanguage() === "en" ? "Clear message" : "Nachricht löschen";
    clear.setAttribute("aria-label", clearLabel);
    clear.title = clearLabel;
    clear.appendChild(makeEl("span", "website-navigator-textarea-clear-icon"));
    const syncClear = () => {
      clear.hidden = !cleanText(input.value);
    };
    input.addEventListener("input", syncClear);
    clear.addEventListener("click", () => {
      input.value = "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.focus();
    });
    wrap.append(input, clear);
    field.appendChild(wrap);
    syncClear();
    return { field, input, clear };
  }

  defaultWebsiteCheckSessionRuns() {
    const used = this.websiteCheckJobs.length;
    const max = Number(this.websiteCheckSessionRuns?.max || 0);
    return this.websiteCheckSessionRuns || {
      used,
      max,
      remaining: max ? Math.max(0, max - used) : null,
      can_start: !max || used < max,
      is_last_next: Boolean(max && used === max - 1),
      limit_reached: Boolean(max && used >= max),
    };
  }

  websiteCheckPropsFromState() {
    const jobs = this.websiteCheckJobs || [];
    return {
      jobs,
      latest: jobs[0] || null,
      session_runs: this.defaultWebsiteCheckSessionRuns(),
      requires_consent: true,
      direct_open: true,
      tool_id: "website_check.start",
    };
  }

  renderWebsiteCheckFlow(options = {}) {
    const token = this.debugStart("website_check.flow.render", { area: "website_check", phase: "render" });
    const forceStart = options.forceStart === true || this.websiteCheckForceStart === true;
    const state = this.websiteCheckPropsFromState();
    const latest = state.latest || {};
    const status = websiteCheckStatus(latest.status || latest.progress?.status);
    const component = forceStart || !latest || !status
      ? {
        component_key: "website_check_form",
        component_type: "website_check_form",
        props: state,
      }
      : {
        component_key: "website_check_progress",
        component_type: "website_check_progress",
        props: state,
      };
    this.lastComponents = [component];
    this.renderComponents([component]);
    this.debugEnd(token, { details: { component_type: component.component_type, jobs_count: this.websiteCheckJobs.length, latest_status: status } });
  }

  isWebsiteCheckForegroundActive() {
    return this.activeShortcutMode === "website_check" && this.activePanelKey === "flow" && !this.flowSection?.hidden;
  }

  applyWebsiteCheckState(props = {}) {
    const oldStatuses = new Map();
    this.websiteCheckJobs.forEach((job) => {
      const key = websiteCheckJobKey(job);
      if (key) oldStatuses.set(key, websiteCheckStatus(job.status || job.progress?.status));
    });
    const incomingJobs = Array.isArray(props.jobs) ? props.jobs : [];
    if (incomingJobs.length) {
      this.websiteCheckJobs = incomingJobs.filter(Boolean).map((job) => {
        const key = websiteCheckJobKey(job);
        const previous = key ? this.websiteCheckJobs.find((item) => websiteCheckJobKey(item) === key) : null;
        if (!previous) return job;
        return {
          ...previous,
          ...job,
          progress: {
            ...(previous.progress || {}),
            ...(job.progress || {}),
          },
          report_summary: cleanText(job.report_summary) || previous.report_summary,
        };
      });
    } else if (props.latest) {
      const latestKey = websiteCheckJobKey(props.latest);
      const previous = latestKey ? this.websiteCheckJobs.find((job) => websiteCheckJobKey(job) === latestKey) : null;
      const latest = previous
        ? {
          ...previous,
          ...props.latest,
          progress: {
            ...(previous.progress || {}),
            ...(props.latest.progress || {}),
          },
          report_summary: cleanText(props.latest.report_summary) || previous.report_summary,
        }
        : props.latest;
      const rest = this.websiteCheckJobs.filter((job) => websiteCheckJobKey(job) !== latestKey);
      const maxItems = Number((props.session_runs || this.websiteCheckSessionRuns || {}).max || 0);
      this.websiteCheckJobs = [latest, ...rest].filter(Boolean).slice(0, maxItems || undefined);
    }
    if (props.session_runs && typeof props.session_runs === "object") this.websiteCheckSessionRuns = props.session_runs;
    this.websiteCheckJobs.forEach((job) => {
      const key = websiteCheckJobKey(job);
      const status = websiteCheckStatus(job.status || job.progress?.status);
      if (key && cleanText(job.report_summary)) this.websiteCheckSummaryRefreshState.delete(key);
      if (key && this.websiteCheckNeedsSummaryRefresh(job) && oldStatuses.get(key) && !isWebsiteCheckTerminalStatus(oldStatuses.get(key))) {
        this.scheduleWebsiteCheckSummaryRefresh(job);
      }
      const oldStatus = oldStatuses.get(key);
      if (!key || !oldStatus || this.websiteCheckTerminalNotified.has(key)) return;
      if (!isWebsiteCheckTerminalStatus(oldStatus) && isWebsiteCheckTerminalStatus(status) && !this.isWebsiteCheckForegroundActive()) {
        this.websiteCheckTerminalNotified.add(key);
        this.showChip(labels().websiteCheckFinishedChip, isWebsiteCheckSuccessfulStatus(status) ? "info" : "error");
        this.debugEvent("website_check.finished_chip", { area: "website_check", phase: "frontend", details: { latest_status: status } });
      }
    });
  }

  websiteCheckNeedsSummaryRefresh(job) {
    return isWebsiteCheckSuccessfulStatus(job?.status || job?.progress?.status) && !cleanText(job?.report_summary) && Boolean(websiteCheckJobKey(job));
  }

  websiteCheckSummaryIsPreparing(job) {
    const key = websiteCheckJobKey(job);
    const state = key ? this.websiteCheckSummaryRefreshState.get(key) : null;
    return Boolean(state && state.expired !== true);
  }

  scheduleWebsiteCheckSummaryRefresh(job, options = {}) {
    if (!this.websiteCheckNeedsSummaryRefresh(job)) return;
    const key = websiteCheckJobKey(job);
    const initialDelayMs = Number(WEBSITE_CHECK_SUMMARY_REFRESH.initialDelayMs);
    const intervalMs = Number(WEBSITE_CHECK_SUMMARY_REFRESH.intervalMs);
    const maxWaitMs = Number(WEBSITE_CHECK_SUMMARY_REFRESH.maxWaitMs);
    if (!Number.isFinite(initialDelayMs) || !Number.isFinite(intervalMs) || !Number.isFinite(maxWaitMs) || intervalMs <= 0 || maxWaitMs <= 0) return;
    this.debugEvent("website_check.background_update", {
      area: "website_check",
      phase: "frontend",
      details: {
        status: websiteCheckStatus(job?.status || job?.progress?.status),
        immediate: options.immediate === true,
      },
    });
    const now = Date.now();
    const state = this.websiteCheckSummaryRefreshState.get(key) || { startedAt: now, attempts: 0, expired: false };
    if (now - state.startedAt >= maxWaitMs) {
      state.expired = true;
      this.websiteCheckSummaryRefreshState.set(key, state);
      if (this.isWebsiteCheckForegroundActive()) this.renderWebsiteCheckFlow();
      return;
    }
    state.expired = false;
    this.websiteCheckSummaryRefreshState.set(key, state);
    if (this.websiteCheckSummaryRefreshTimer) return;
    const delay = options.immediate === true ? 0 : state.attempts ? intervalMs : initialDelayMs;
    this.websiteCheckSummaryRefreshTimer = window.setTimeout(async () => {
      this.websiteCheckSummaryRefreshTimer = 0;
      const currentState = this.websiteCheckSummaryRefreshState.get(key);
      if (!currentState || currentState.expired === true) return;
      currentState.attempts += 1;
      this.websiteCheckSummaryRefreshState.set(key, currentState);
      let refreshedJob = null;
      try {
        const refreshed = await this.refreshWebsiteCheckState({ render: false });
        refreshedJob = (refreshed?.jobs || []).find((item) => websiteCheckJobKey(item) === key) || refreshed?.latest || null;
      } catch {
      }
      if (!this.websiteCheckNeedsSummaryRefresh(refreshedJob || job)) {
        this.websiteCheckSummaryRefreshState.delete(key);
        if (this.isWebsiteCheckForegroundActive()) this.renderWebsiteCheckFlow();
        return;
      }
      this.scheduleWebsiteCheckSummaryRefresh(refreshedJob || job);
      if (this.isWebsiteCheckForegroundActive()) this.renderWebsiteCheckFlow();
    }, Math.max(0, delay));
  }

  async refreshWebsiteCheckState(options = {}) {
    if (!this.ids?.journey_id || !this.ids?.session_id) return null;
    const operationId = this.debugOperation("website_check.state");
    const token = this.debugStart("website_check.state.request", { area: "website_check", phase: "frontend", operationId });
    const params = new URLSearchParams({
      journey_id: this.ids.journey_id,
      session_id: this.ids.session_id,
    });
    try {
      const data = await this.fetchJson(`/website-checks/state?${params.toString()}`, {
        method: "GET",
        debugArea: "website_check",
        debugOperationId: operationId,
        debugEvent: "website_check.state.request",
      });
      this.applyWebsiteCheckState(data);
      if (options.render !== false && this.isWebsiteCheckForegroundActive()) this.renderWebsiteCheckFlow();
      this.debugEnd(token, {
        details: {
          jobs_count: Array.isArray(data?.jobs) ? data.jobs.length : 0,
          latest_status: websiteCheckStatus(data?.latest?.status || data?.latest?.progress?.status),
          session_runs_used: data?.session_runs?.used ?? null,
          session_runs_max: data?.session_runs?.max ?? null,
        },
      });
      return data;
    } catch (error) {
      this.debugEnd(token, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
      throw error;
    }
  }

  stopWebsiteCheckStatusPolling() {
    if (this.websiteCheckPollTimer) window.clearTimeout(this.websiteCheckPollTimer);
    this.websiteCheckPollTimer = 0;
    this.websiteCheckPollUrl = "";
    this.websiteCheckPollKey = "";
  }

  startWebsiteCheckStatusPolling(latest, liveUrl) {
    const statusUrl = websiteCheckStatusUrl(liveUrl, this.apiOrigin());
    const latestKey = websiteCheckJobKey(latest);
    if (!statusUrl || isWebsiteCheckTerminalStatus(latest?.status || latest?.progress?.status)) {
      this.stopWebsiteCheckStatusPolling();
      return;
    }
    if (this.websiteCheckPollTimer && this.websiteCheckPollUrl === statusUrl && this.websiteCheckPollKey === latestKey) return;
    this.stopWebsiteCheckStatusPolling();
    this.websiteCheckPollUrl = statusUrl;
    this.websiteCheckPollKey = latestKey;
    const poll = async () => {
      if (!this.websiteCheckPollUrl) return;
      try {
        const response = await fetch(this.websiteCheckPollUrl, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (response.ok) {
          const payload = await response.json();
          const current = this.websiteCheckJobs.find((job) => websiteCheckJobKey(job) === latestKey) || latest || {};
          const status = websiteCheckStatus(payload.status || current.status || current.progress?.status);
          const updated = {
            ...current,
            status,
            stage: payload.stage ?? current.stage,
            percent: payload.percent ?? current.percent,
            run_id: cleanText(payload.run_id) || current.run_id,
            request_id: cleanText(payload.request_id) || current.request_id,
            final_url: cleanText(payload.final_url) || current.final_url,
            live_url: current.live_url || liveUrl,
            progress: {
              ...(current.progress || {}),
              status,
              stage: payload.stage ?? current.progress?.stage,
              percent: payload.percent ?? current.progress?.percent,
            },
          };
          this.applyWebsiteCheckState({ latest: updated });
          if (isWebsiteCheckTerminalStatus(status)) {
            this.stopWebsiteCheckStatusPolling();
            if (this.websiteCheckNeedsSummaryRefresh(updated)) this.scheduleWebsiteCheckSummaryRefresh(updated, { immediate: true });
            else if (this.isWebsiteCheckForegroundActive()) this.renderWebsiteCheckFlow();
            return;
          }
        }
      } catch {
      }
      this.websiteCheckPollTimer = window.setTimeout(poll, 3000);
    };
    this.websiteCheckPollTimer = window.setTimeout(poll, 1200);
  }

  handleWebsiteCheckLiveMessage(event) {
    const allowedOrigins = new Set([window.location.origin, this.apiOrigin()]);
    if (!allowedOrigins.has(event.origin)) return;
    const data = event.data || {};
    if (!data || data.type !== "website_navigator_live_status") return;
    const status = websiteCheckStatus(data.status || data.event);
    if (!status) return;
    const slug = cleanText(data.slug);
    const runId = cleanText(data.run_id);
    const requestId = cleanText(data.request_id);
    const current = this.websiteCheckJobs.find((job) => {
      if (runId && cleanText(job.run_id || job.job_id) === runId) return true;
      if (requestId && cleanText(job.request_id) === requestId) return true;
      return slug && cleanText(job.normalized_domain) === slug;
    }) || this.websiteCheckJobs[0] || {};
    const updated = {
      ...current,
      status,
      percent: data.percent ?? current.percent,
      run_id: runId || current.run_id,
      request_id: requestId || current.request_id,
      final_url: cleanText(data.final_url) || current.final_url,
      progress: {
        ...(current.progress || {}),
        status,
        percent: data.percent ?? current.progress?.percent,
      },
    };
    this.applyWebsiteCheckState({ latest: updated });
    this.debugEvent("website_check.stream.update", {
      area: "website_check",
      phase: "frontend",
      details: {
        latest_status: status,
        has_final_url: Boolean(cleanText(updated.final_url)),
        has_live_url: Boolean(cleanText(updated.live_url)),
      },
    });
    if (isWebsiteCheckTerminalStatus(status)) this.stopWebsiteCheckStatusPolling();
    if (this.websiteCheckNeedsSummaryRefresh(updated)) this.scheduleWebsiteCheckSummaryRefresh(updated, { immediate: true });
    if (this.isWebsiteCheckForegroundActive()) this.renderWebsiteCheckFlow();
  }

  renderWebsiteCheckCounter(parent, sessionRuns) {
    const runs = sessionRuns || this.defaultWebsiteCheckSessionRuns();
    const used = Number(runs.used || 0);
    const max = Number(runs.max || 0);
    if (!used || !max) return;
    const row = makeEl("div", "website-navigator-check-run-counter");
    appendText(row, "span", "", `${labels().websiteCheckRunsUsed}: ${used}/${max}`);
    parent.appendChild(row);
  }

  renderWebsiteCheckHistory(parent, jobs) {
    const maxItems = Number(this.defaultWebsiteCheckSessionRuns().max || 0);
    const items = (Array.isArray(jobs) ? jobs : this.websiteCheckJobs).filter(Boolean).slice(0, maxItems || undefined);
    if (!items.length) return;
    const label = labels();
    const wrapper = makeEl("section", "website-navigator-check-history");
    appendText(wrapper, "h4", "website-navigator-check-history-title", label.websiteCheckHistory);
    items.forEach((job) => {
      const row = makeEl("article", "website-navigator-check-history-item");
      appendText(row, "span", "website-navigator-check-history-url", cleanText(job.url || job.normalized_domain));
      const finalUrl = cleanText(job.final_url);
      const status = websiteCheckStatus(job.status || job.progress?.status);
      if (finalUrl && isWebsiteCheckSuccessfulStatus(status)) {
        const link = makeEl("a", "website-navigator-action is-report-action", label.openReport);
        link.href = absoluteUrl(finalUrl, this.apiOrigin());
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        row.appendChild(link);
      } else {
        appendText(row, "span", "website-navigator-check-history-status", this.statusCopy(status) || label.reportPreparing);
      }
      wrapper.appendChild(row);
    });
    parent.appendChild(wrapper);
  }

  renderWebsiteCheckForm(type, props) {
    const token = this.debugStart("website_check.form.render", { area: "website_check", phase: "render" });
    const label = labels();
    const jobs = Array.isArray(props.jobs) ? props.jobs : this.websiteCheckJobs;
    const sessionRuns = props.session_runs || this.defaultWebsiteCheckSessionRuns();
    const canStart = sessionRuns.can_start !== false && !sessionRuns.limit_reached;
    const shell = this.componentShell(label.liveWebsiteCheck, type, { description: this.modeDescriptionForTarget("website_check", "website_check") });
    this.renderWebsiteCheckCounter(shell, sessionRuns);
    if (sessionRuns.limit_reached) {
      appendText(shell, "p", "website-navigator-limit-note", label.websiteCheckLimitReached);
    } else if (sessionRuns.is_last_next) {
      appendText(shell, "p", "website-navigator-limit-note is-last", label.websiteCheckLastRun);
    }
    if (canStart) {
      const field = this.formField(label.websiteUrl, "url");
      field.input.placeholder = "https://";
      shell.appendChild(field.field);
      const actions = makeEl("div", "website-navigator-form-actions");
      const button = makeEl("button", "website-navigator-action is-primary is-start-site-action", label.startCheck);
      button.type = "button";
      actions.appendChild(button);
      shell.appendChild(actions);
      button.addEventListener("click", async () => {
        const operationId = this.debugOperation("website_check.start");
        const startToken = this.debugStart("website_check.start.operation", { area: "website_check", phase: "frontend", operationId });
        const url = cleanText(field.input.value);
        if (!url) {
          this.showChip(label.required, "error");
          this.debugEnd(startToken, { status: "skipped", details: { reason: "missing_url" } });
          return;
        }
        await this.runAction("website_check.start", "website_check.run_check", {
          url,
          confirmed: true,
          consent_granted: true,
        }, { idempotent: true, button, debugOperationId: operationId }).then((output) => {
          this.applyWebsiteCheckState({
            latest: output,
            jobs: output.status === "limit_reached" ? this.websiteCheckJobs : [output, ...this.websiteCheckJobs].slice(0, Number((output.session_runs || {}).max || 0) || undefined),
            session_runs: output.session_runs,
          });
          if (output.status === "limit_reached") {
            this.showChip(label.websiteCheckLimitReached, "error");
            this.websiteCheckForceStart = true;
            this.renderWebsiteCheckFlow({ forceStart: true });
            this.debugEnd(startToken, { details: { latest_status: cleanText(output.status), session_runs_used: output.session_runs?.used ?? null, session_runs_max: output.session_runs?.max ?? null } });
            return;
          }
          this.showChip(label.websiteCheckStarted);
          this.websiteCheckForceStart = false;
          this.renderWebsiteCheckFlow();
          this.openStream();
          this.debugEnd(startToken, { details: { latest_status: cleanText(output.status), session_runs_used: output.session_runs?.used ?? null, session_runs_max: output.session_runs?.max ?? null } });
        }).catch((error) => {
          this.debugEnd(startToken, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
          throw error;
        });
      });
    }
    this.renderWebsiteCheckHistory(shell, jobs);
    this.debugEnd(token, {
      details: {
        jobs_count: jobs.length,
        session_runs_used: sessionRuns.used ?? null,
        session_runs_max: sessionRuns.max ?? null,
        can_start: canStart,
      },
    });
    return shell;
  }

  renderWebsiteCheckProgress(type, props) {
    const token = this.debugStart("website_check.progress.render", { area: "website_check", phase: "render" });
    const latest = props.latest || props.progress || {};
    const nestedProgress = latest.progress || {};
    const percent = Math.max(0, Math.min(100, Number(latest.percent || nestedProgress.percent || 0)));
    const status = cleanText(latest.status || nestedProgress.status || props.status || "idle");
    const liveUrl = cleanText(latest.live_url || props.live_url);
    const finalUrl = cleanText(latest.final_url || props.final_url);
    const isTerminal = isWebsiteCheckTerminalStatus(status);
    const isSuccessful = isWebsiteCheckSuccessfulStatus(status);
    const label = labels();
    const shell = this.componentShell(label.liveWebsiteCheckProgress, type, { description: this.modeDescriptionForTarget("website_check", "website_check_progress") });
    const progress = makeEl("div", "website-navigator-progress");
    if (isTerminal) {
      if (isSuccessful) this.markShortcutModeFinished("website_check", "modeAnalyzed");
      this.stopWebsiteCheckStatusPolling();
      appendText(progress, "p", "website-navigator-check-result-text", isSuccessful ? label.websiteCheckFinished : this.statusCopy(status));
      const links = makeEl("div", "website-navigator-action-row website-navigator-progress-links");
      if (finalUrl && isSuccessful) {
        const reportLink = makeEl("a", "website-navigator-action is-primary is-report-action", label.openReport);
        reportLink.href = absoluteUrl(finalUrl, this.apiOrigin());
        reportLink.target = "_blank";
        reportLink.rel = "noopener noreferrer";
        links.appendChild(reportLink);
      }
      const sessionRuns = props.session_runs || this.defaultWebsiteCheckSessionRuns();
      if (sessionRuns.can_start !== false && !sessionRuns.limit_reached) {
        const restartButton = makeEl("button", "website-navigator-action is-start-site-action", label.startForAnotherSite);
        restartButton.type = "button";
        restartButton.addEventListener("click", () => {
          this.websiteCheckForceStart = true;
          this.renderWebsiteCheckFlow({ forceStart: true });
        });
        links.appendChild(restartButton);
      }
      if (links.children.length) progress.appendChild(links);
      const summary = cleanText(latest.report_summary || props.report_summary);
      if (isSuccessful) {
        const details = makeEl("details", "website-navigator-check-summary");
        details.open = true;
        appendText(details, "summary", "website-navigator-check-summary-title", label.websiteCheckSummary);
        appendText(details, "p", "mb-0", summary || (this.websiteCheckSummaryIsPreparing(latest) ? label.websiteCheckSummaryPreparing : label.websiteCheckSummaryEmpty));
        progress.appendChild(details);
      }
      shell.appendChild(progress);
      this.debugEnd(token, {
        details: {
          latest_status: status,
          has_live_url: Boolean(liveUrl),
          has_final_url: Boolean(finalUrl),
          has_report_summary: Boolean(cleanText(latest.report_summary || props.report_summary)),
        },
      });
      return shell;
    }
    this.startWebsiteCheckStatusPolling(latest, liveUrl);
    appendText(progress, "p", "mb-0", this.statusCopy(status));
    const track = makeEl("div", "website-navigator-progress-track");
    const fill = makeEl("div", "website-navigator-progress-fill");
    fill.style.setProperty("--website-navigator-progress", `${percent}%`);
    track.appendChild(fill);
    progress.appendChild(track);
    if (liveUrl) {
      const preview = makeEl("div", "website-navigator-live-preview");
      appendText(preview, "strong", "website-navigator-live-preview-title", label.livePreview);
      const frame = makeEl("iframe", "website-navigator-live-frame");
      frame.src = websiteCheckEmbeddedLiveUrl(liveUrl, this.apiOrigin());
      frame.title = label.livePreview;
      frame.loading = "lazy";
      frame.referrerPolicy = "strict-origin-when-cross-origin";
      frame.setAttribute("sandbox", "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox");
      preview.appendChild(frame);
      progress.appendChild(preview);
    }
    const currentKey = websiteCheckJobKey(latest);
    const previousJobs = (Array.isArray(props.jobs) ? props.jobs : this.websiteCheckJobs)
      .filter((job) => websiteCheckJobKey(job) && websiteCheckJobKey(job) !== currentKey);
    this.renderWebsiteCheckHistory(progress, previousJobs);
    const links = makeEl("div", "website-navigator-action-row website-navigator-progress-links");
    if (liveUrl) {
      const link = makeEl("a", "website-navigator-action", label.openLiveView);
      link.href = absoluteUrl(liveUrl, this.apiOrigin());
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      links.appendChild(link);
    }
    if (links.children.length) progress.appendChild(links);
    shell.appendChild(progress);
    this.debugEnd(token, {
      details: {
        latest_status: status,
        has_live_url: Boolean(liveUrl),
        has_final_url: Boolean(finalUrl),
        percent,
      },
    });
    return shell;
  }

  renderInquirySummary() {
    return null;
  }

  renderContactConfirmation(type, props) {
    const label = labels();
    this.markShortcutModeFinished("contact", "modeSent");
    const shell = this.componentShell(label.inquirySentTitle, type || "contact_confirmation", { description: this.modeDescriptionForTarget("contact", "contact_confirmation") });
    const card = makeEl("article", "website-navigator-booking-confirmation website-navigator-contact-confirmation");
    const icon = makeEl("span", "website-navigator-contact-confirmation-icon");
    icon.setAttribute("aria-hidden", "true");
    const copy = makeEl("div", "website-navigator-contact-confirmation-copy");
    appendText(copy, "p", "website-navigator-contact-confirmation-text", label.inquirySentBody);
    card.append(icon, copy);
    shell.appendChild(card);
    return shell;
  }

  renderUpload(type, props) {
    const token = this.debugStart("upload.form.render", { area: "upload", phase: "render" });
    const label = labels();
    const shell = this.componentShell(label.fileUpload, type, { description: this.modeDescriptionForTarget("contact", "contact_upload") });
    appendText(shell, "p", "mb-0", `${label.allowed}: ${(props.allowed_extensions || []).join(", ").toUpperCase()}`);
    const input = makeEl("input");
    input.type = "file";
    input.accept = (props.allowed_extensions || []).map((item) => `.${String(item).toLowerCase()}`).join(",");
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      this.debugEvent("upload.file.selected", {
        area: "upload",
        phase: "frontend",
        details: {
          file_size: file?.size || 0,
          file_type: cleanText(file?.type),
          has_file: Boolean(file),
        },
      });
    });
    shell.appendChild(input);
    const button = makeEl("button", "website-navigator-action is-primary", label.upload);
    button.type = "button";
    shell.appendChild(button);
    button.addEventListener("click", async () => {
      const file = input.files?.[0];
      if (!file) {
        this.showChip(label.required, "error");
        return;
      }
      const purpose = cleanText(props.purpose);
      if (!purpose) {
        this.showChip(label.connectionFailed, "error");
        return;
      }
      const operationId = this.debugOperation("upload.prepare");
      await this.runAction("upload.prepare", "upload_handoff.prepare_upload", {
        file_name: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        purpose,
        confirmed: true,
        consent_granted: true,
      }, { idempotent: true, button, debugOperationId: operationId }).then(async (output) => {
        await this.uploadFile(output, file, button, operationId);
      });
    });
    this.debugEnd(token, {
      details: {
        allowed_extensions_count: Array.isArray(props.allowed_extensions) ? props.allowed_extensions.length : 0,
        max_file_size_bytes: props.max_file_size_bytes || null,
      },
    });
    return shell;
  }

  async uploadFile(prepared, file, button, operationId = "") {
    if (!prepared.upload_url) return;
    button.disabled = true;
    const form = new FormData();
    form.append("file", file);
    const uploadUrl = prepared.upload_url.startsWith("http") ? prepared.upload_url : `${this.apiOrigin()}${prepared.upload_url}`;
    const requestToken = this.latencyDebug?.requestStart?.(uploadUrl, {
      method: "POST",
      body: form,
      debugArea: "upload",
      operationId: operationId || this.debugOperation("upload.file"),
      actionId: "upload.file",
    });
    const token = this.debugStart("upload.file.request", {
      area: "upload",
      phase: "frontend",
      operationId: requestToken?.operation_id || operationId,
      details: {
        file_size: file.size,
        file_type: cleanText(file.type),
      },
    });
    let requestFinished = false;
    const pendingToken = this.showPendingIndicator(this.actionLoadingText("upload.file"));
    try {
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: form,
        headers: {
          ...(this.latencyDebug?.requestHeaders?.(requestToken) || {}),
        },
      });
      const responseText = await response.text();
      const parseStartedAt = performance.now();
      const data = responseText ? JSON.parse(responseText) : null;
      const jsonParseMs = performance.now() - parseStartedAt;
      this.latencyDebug?.requestEnd?.(requestToken, {
        status: response.status,
        ok: response.ok,
        responseText,
        data,
        serverTiming: response.headers.get("server-timing"),
        requestId: response.headers.get("x-wnav-request-id"),
        actionId: "upload.file",
        jsonParseMs,
      });
      requestFinished = true;
      if (!response.ok) throw new Error(cleanText(data.detail) || "upload_failed");
      this.showChip(labels().uploadReceived);
      this.pollUpload(data.upload_id);
      this.debugEnd(token, { details: { status: cleanText(data.status), upload_id_present: Boolean(cleanText(data.upload_id)) } });
    } catch (error) {
      if (!requestFinished) this.latencyDebug?.requestError?.(requestToken, error);
      this.debugEnd(token, { status: "error", details: { detail: cleanText(error?.detail || error?.message) } });
      this.showChip(labels().connectionFailed, "error");
    } finally {
      this.hidePendingIndicator(pendingToken);
      button.disabled = false;
    }
  }

  pollUpload(uploadId) {
    let count = 0;
    const operationId = this.debugOperation("upload.poll");
    const token = this.debugStart("upload.poll", { area: "upload", phase: "frontend", operationId });
    const pendingToken = this.showPendingIndicator(this.actionLoadingText("upload.status"));
    const tick = async () => {
      count += 1;
      this.debugEvent("upload.poll.tick", { area: "upload", phase: "frontend", operationId, details: { poll_count: count } });
      try {
        const data = await this.fetchJson(`/uploads/${encodeURIComponent(uploadId)}/status`, {
          debugArea: "upload",
          debugOperationId: operationId,
          debugEvent: "upload.status",
        });
        if (data.status === "ready") {
          this.showChip(labels().fileReady);
          this.debugEnd(token, { details: { status: cleanText(data.status), poll_count: count } });
          this.hidePendingIndicator(pendingToken);
          return;
        }
        if (data.status === "scan_failed" || data.status === "rejected") {
          this.showChip(this.statusCopy(data.status), "error");
          this.debugEnd(token, { status: "error", details: { status: cleanText(data.status), poll_count: count } });
          this.hidePendingIndicator(pendingToken);
          return;
        }
      } catch {
        this.showChip(labels().connectionFailed, "error");
        this.debugEnd(token, { status: "error", details: { poll_count: count } });
        this.hidePendingIndicator(pendingToken);
        return;
      }
      if (count < 12) window.setTimeout(tick, 2500);
      else {
        this.hidePendingIndicator(pendingToken);
        this.debugEnd(token, { status: "skipped", details: { status: "timeout", poll_count: count } });
      }
    };
    tick();
  }

  renderPageHint(type, props) {
    const shell = this.componentShell(labels().nextStep, type);
    if (props.guidance) appendText(shell, "p", "mb-0", props.guidance);
    this.appendActionButtons(shell, props.actions || []);
    return shell;
  }

  renderQuestionChips(type, props) {
    const shell = this.componentShell(labels().commonQuestions, type);
    const row = makeEl("div", "website-navigator-action-row");
    (props.questions || []).forEach((question) => {
      const prompt = textFor(question.prompt || question.question || question.label || question);
      const button = makeEl("button", "website-navigator-action", prompt);
      button.type = "button";
      button.addEventListener("click", () => {
        const targetMode = this.prepareAssistanceQuestion();
        this.sendMessage(prompt, { targetMode });
      });
      row.appendChild(button);
    });
    shell.appendChild(row);
    return shell;
  }

  renderAmbientHint(type, props) {
    if (!props.hint) return null;
    const shell = this.componentShell(labels().hint, type);
    appendText(shell, "p", "mb-0", textFor(props.hint.text || props.hint.label || props.hint));
    return shell;
  }

  renderNextActions(type, props) {
    const shell = this.componentShell(labels().possibleNextSteps, type);
    this.appendActionButtons(shell, props.actions || []);
    return shell;
  }

  appendActionButtons(shell, actions) {
    const row = makeEl("div", "website-navigator-action-row");
    actions.forEach((action) => {
      const button = makeEl("button", "website-navigator-action", textFor(action.label || action.title || action.action_key));
      button.type = "button";
      button.addEventListener("click", () => {
        const key = cleanText(action.action_key);
        if (key === "create_inquiry" || key === "prepare_inquiry") {
          this.openContactMode();
          return;
        }
        if (key === "start_website_check") {
          this.openWebsiteCheckMode();
          return;
        }
        const promptKey = cleanText(action.prompt_key) || ACTION_PROMPT_KEYS[key];
        const shortcutMode = this.normalizeShortcutMode(promptKey);
        if (shortcutMode) this.setActiveShortcutMode(shortcutMode);
        const prompt = textFor(action.prompt || PROMPTS[promptKey]);
        if (prompt) this.sendMessage(prompt, { showUserMessage: false });
      });
      row.appendChild(button);
    });
    if (row.children.length) shell.appendChild(row);
  }

  renderGeneric(type, props) {
    const shell = this.componentShell(type || labels().navigator, type || "generic");
    const text = cleanText(props.answer || props.status || "");
    if (text) appendText(shell, "p", "mb-0", text);
    return shell;
  }

  async runAction(actionId, workflowId, payload, options = {}) {
    await this.ensureBootstrap();
    this.markNavigatorSessionActivity();
    const button = options.button;
    const debugArea = options.debugArea || this.debugAreaForAction(actionId);
    const operationId = options.debugOperationId || this.debugOperation(actionId);
    const token = this.debugStart(`${debugArea}.action`, {
      area: debugArea,
      phase: "frontend",
      operationId,
      details: {
        action_id: cleanText(actionId),
        workflow_id: cleanText(workflowId),
        idempotent: options.idempotent === true,
      },
    });
    if (button) button.disabled = true;
    let pendingToken = options.showLoading === false ? "" : this.showPendingIndicator(this.actionLoadingText(actionId));
    try {
      const data = await this.fetchJson("/component-actions", {
        method: "POST",
        debugArea,
        debugOperationId: operationId,
        debugEvent: `${debugArea}.${cleanText(actionId).replaceAll(".", "_")}`,
        actionId,
        body: JSON.stringify({
          journey_id: this.ids.journey_id,
          session_id: this.ids.session_id,
          action_id: actionId,
          component_key: payload.component_key || null,
          idempotency_key: options.idempotent ? makeUuid() : null,
          payload: {
            ...payload,
            workflow_id: workflowId,
            locale: currentLanguage(),
            timezone: payload.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      });
      const output = data.state_patch || {};
      if (output.status === "requires_consent") {
        this.showChip(labels().consent, "error");
      }
      if (output.status === "adapter_unavailable") {
        this.showChip(labels().unavailable, "error");
      }
      this.debugEnd(token, {
        details: {
          action_id: cleanText(actionId),
          status: cleanText(output.status || data.status),
          component_updates_count: Array.isArray(data.component_updates) ? data.component_updates.length : 0,
          output_keys: Object.keys(output || {}).slice(0, 20),
        },
      });
      return output;
    } catch (error) {
      this.debugEnd(token, { status: "error", details: { action_id: cleanText(actionId), detail: cleanText(error?.detail || error?.message) } });
      this.showConnectionError(error);
      throw error;
    } finally {
      this.hidePendingIndicator(pendingToken);
      if (button) button.disabled = false;
    }
  }

  statusCopy(status) {
    return navigatorText("status_messages", status, status);
  }

  openStream() {
    if (!this.ids || !window.EventSource) return;
    const operationId = this.debugOperation("stream");
    const token = this.debugStart("stream.open", { area: "stream", phase: "frontend", operationId });
    if (this.stream) {
      this.debugEvent("stream.closed", { area: "stream", phase: "frontend", operationId, details: { reason: "replace_existing" } });
      this.stream.close();
    }
    const params = new URLSearchParams({
      journey_id: this.ids.journey_id,
      session_id: this.ids.session_id,
    });
    if (this.latencyDebug?.enabled) {
      params.set("wnav_debug", "1");
      params.set("trace_id", this.latencyDebug.traceId?.() || "");
      params.set("operation_id", operationId);
    }
    const url = `${this.apiRoot()}/messages/stream?${params.toString()}`;
    const stream = new EventSource(url);
    this.stream = stream;
    let closed = false;
    const closeStream = (reason, status = "ok") => {
      if (closed) return;
      closed = true;
      this.debugEvent("stream.closed", { area: "stream", phase: "frontend", operationId, details: { reason } });
      this.debugEnd(token, { status, details: { reason } });
      stream.close();
    };
    stream.addEventListener("open", () => {
      this.debugEvent("stream.opened", { area: "stream", phase: "frontend", operationId });
    });
    stream.addEventListener("component_update", (event) => {
      try {
        const data = JSON.parse(event.data);
        const patches = data.patches || [];
        this.debugEvent("stream.event.component_update", { area: "stream", phase: "frontend", operationId, details: { patches_count: patches.length } });
        if (patches.length) this.handleComponentPatches(patches);
      } catch {
      }
    });
    stream.addEventListener("async_job_update", (event) => {
      try {
        const data = JSON.parse(event.data);
        this.debugEvent("stream.event.async_job_update", {
          area: "stream",
          phase: "frontend",
          operationId,
          details: {
            jobs_count: Array.isArray(data.jobs) ? data.jobs.length : 0,
            latest_status: websiteCheckStatus(data.latest?.status || data.latest?.progress?.status),
          },
        });
        if (Array.isArray(data.jobs) || data.latest || data.session_runs) this.applyWebsiteCheckState(data);
      } catch {
      }
    });
    stream.addEventListener("message", (event) => {
      this.debugEvent("stream.event.message", { area: "stream", phase: "frontend", operationId, details: { payload_bytes: new Blob([event.data || ""]).size } });
    });
    stream.addEventListener("done", () => {
      this.debugEvent("stream.event.done", { area: "stream", phase: "frontend", operationId });
      closeStream("done");
    });
    stream.addEventListener("error", () => closeStream("error", "error"));
  }

  handleComponentPatches(patches) {
    const foreground = [];
    let hasWebsiteCheckPatch = false;
    patches.forEach((patch) => {
      const type = this.normalizeShortcutMode(patch.component_type || patch.component_key);
      if (type === "website_check") {
        hasWebsiteCheckPatch = true;
        this.applyWebsiteCheckState(patch.props || {});
        return;
      }
      foreground.push(patch);
    });
    if (foreground.length) this.renderComponents(foreground);
    if (hasWebsiteCheckPatch && this.isWebsiteCheckForegroundActive()) {
      this.websiteCheckForceStart = false;
      this.renderWebsiteCheckFlow();
    }
  }
}

export function initWebsiteNavigator() {
  const root = document.querySelector("[data-website-navigator]");
  if (!root) return;
  new WebsiteNavigator(root).init();
}
